var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/websocket/schema.ts
import { z as z3 } from "zod";

// src/ai/schema.ts
import { z } from "zod";
var ECGPredictSinusRhythmRequestSchema = z.object({
  isSinusRhythm: z.boolean().optional().nullable(),
  signalQuality: z.number().optional().nullable()
});

// src/domain/models.ts
import { first } from "@quromedical/utils";
import { z as z2 } from "zod";

// src/resources.ts
var supportedResources = [
  "AllergyIntolerance",
  "AuditEvent",
  "CareTeam",
  "ChargeItemDefinition",
  "Claim",
  "ClinicalImpression",
  "Communication",
  "Condition",
  "Coverage",
  "Device",
  "DeviceUseStatement",
  "DiagnosticReport",
  "Encounter",
  "Group",
  "Location",
  "Media",
  "Observation",
  "Organization",
  "Patient",
  "Practitioner",
  "PractitionerRole",
  "RelatedPerson",
  "ServiceRequest",
  "Task",
  "MedicationRequest",
  "ActivityDefinition",
  "PlanDefinition",
  "CarePlan",
  "Questionnaire",
  "Appointment",
  "DocumentReference",
  "Flag"
];
var supportedResourceSet = new Set(supportedResources);

// src/domain/models.ts
var UUIDSchema = z2.string().uuid();
var BaseUUIDSearchSchema = z2.string().superRefine((val, ctx) => {
  const ids = val.split(",");
  const parsed = UUIDSchema.array().safeParse(ids);
  if (parsed.success) {
    return val;
  }
  ctx.addIssue({
    code: z2.ZodIssueCode.custom,
    message: "Invalid UUID detected, must be a string of valid UUIDs comma separated",
    path: []
  });
});
var UUIDSearchSchema = z2.object({
  ids: BaseUUIDSearchSchema
});
var ReferenceViewSchema = z2.object({
  id: UUIDSchema,
  // TODO: this should be a fhir resource type
  type: z2.string(),
  name: z2.string()
});
var UUIDInputSchema = z2.object({
  id: UUIDSchema
});
var ReferenceSchema = z2.string().refine((value) => {
  const parts = value.split("/");
  if (parts.length !== 2) {
    return false;
  }
  const [resourceType, id] = parts;
  if (!supportedResourceSet.has(resourceType)) {
    return false;
  }
  const parsedId = UUIDSchema.safeParse(id);
  return parsedId.success;
});
var VersionedReferenceSchema = z2.string().refine((value) => {
  const parts = value.split("/");
  if (parts.length !== 4) {
    return false;
  }
  const [resourceType, id, history, versionId] = parts;
  if (!supportedResourceSet.has(resourceType)) {
    return false;
  }
  if (history !== "_history") {
    return false;
  }
  if (!versionId) {
    return false;
  }
  const parsedId = UUIDSchema.safeParse(id);
  return parsedId.success;
});
var DisplayReferenceSchema = z2.object({
  id: UUIDSchema.optional(),
  reference: ReferenceSchema,
  display: z2.string().optional()
});
var valueQuantityModel = (input) => {
  return {
    toString: () => `${input.valueQuantity.value} ${input.valueQuantity.unit}`,
    toValue: () => input
  };
};
var valueCodeableConceptModel = (input) => {
  return {
    toString: () => input.valueCodeableConcept.text || first(input.valueCodeableConcept.coding)?.display || "",
    toValue: () => input
  };
};
var valueStringModel = (input) => {
  return {
    toString: () => input.valueString,
    toValue: () => input
  };
};
var valueRangeModel = (input) => {
  return {
    toString: () => `${input.valueRange.low?.value || ""} - ${input.valueRange.high?.value || ""} ${input.valueRange.low?.unit || ""}`,
    toValue: () => input
  };
};
var valueRatioModel = (input) => {
  return {
    toString: () => `${input.valueRatio.numerator?.value || ""} / ${input.valueRatio.denominator?.value || ""}`,
    toValue: () => input
  };
};
var valueSampledDataModel = (input) => {
  return {
    toString: () => input.valueSampledData.data || "",
    toValue: () => input
  };
};
var valueAttachmentModel = (input) => {
  return {
    toString: () => input.valueAttachment.title || "",
    toValue: () => input
  };
};
var valueTimeModel = (input) => {
  return {
    toString: () => input.valueTime,
    toValue: () => input
  };
};
var valueDateTimeModel = (input) => {
  return {
    toString: () => input.valueDateTime,
    toValue: () => input
  };
};
var valuePeriodModel = (input) => {
  return {
    toString: () => `${input.valuePeriod.start} - ${input.valuePeriod.end}`,
    toValue: () => input
  };
};
var valueTypeMapping = {
  valueQuantity: valueQuantityModel,
  valueCodeableConcept: valueCodeableConceptModel,
  valueString: valueStringModel,
  valueRange: valueRangeModel,
  valueRatio: valueRatioModel,
  valueSampledData: valueSampledDataModel,
  valueAttachment: valueAttachmentModel,
  valueTime: valueTimeModel,
  valueDateTime: valueDateTimeModel,
  valuePeriod: valuePeriodModel
};
var getValueTypeModel = (input) => {
  const keys = Object.keys(input);
  const key = keys[0];
  const handler = valueTypeMapping[key];
  return handler(input);
};

// src/websocket/schema.ts
var SubscriptionActionSchema = z3.literal("subscribe");
var SubscriptionParamsSchema = z3.object({
  action: SubscriptionActionSchema,
  authToken: z3.string(),
  locationId: UUIDSchema.optional()
});
var BaseCommentSchema = z3.object({
  ts: z3.number(),
  practitionerId: z3.string()
});
var BaseAlertSchema = z3.object({
  patientId: z3.string(),
  ts: z3.number(),
  type: z3.string(),
  value: z3.any(),
  comments: z3.array(z3.any()).optional()
});
var createBaseAlertSchema = (type, value, comment) => z3.object({
  patientId: z3.string(),
  ts: z3.number(),
  type,
  value,
  comments: comment.array().optional()
});
var ECGAlertCommentSchema = BaseCommentSchema.extend({
  quality: z3.string(),
  classification: z3.string(),
  details: z3.string().optional()
});
var ECGSinusRhythmAlertDataSchema = ECGPredictSinusRhythmRequestSchema.extend({
  sensorId: z3.string()
});
var ECGSinusRhythmSchema = z3.literal("ecg-sinus-rhythm");
var ECGSinusRhythmAlertSchema = createBaseAlertSchema(
  ECGSinusRhythmSchema,
  ECGSinusRhythmAlertDataSchema,
  ECGAlertCommentSchema
);

// src/vitals/schema.ts
import { z as z4 } from "zod";
var MeasurementName = /* @__PURE__ */ ((MeasurementName3) => {
  MeasurementName3["Activity"] = "ACT";
  MeasurementName3["HeartRate"] = "HTR";
  MeasurementName3["RespiratoryRate"] = "RSR";
  MeasurementName3["SkinTemperature"] = "STP";
  MeasurementName3["CoreTemperature"] = "CTP";
  MeasurementName3["OxygenSaturation"] = "MS-SpO2";
  MeasurementName3["PerfusionIndex"] = "MS-Pi";
  MeasurementName3["Steps"] = "STS";
  MeasurementName3["Posture"] = "POS";
  MeasurementName3["Battery"] = "BAT";
  MeasurementName3["RRI"] = "RRI";
  MeasurementName3["ECG"] = "ECG";
  MeasurementName3["Glucose"] = "GLUCOSE";
  MeasurementName3["GlucoseFasting"] = "GLUCOSE-FASTING";
  MeasurementName3["GlucoseBeforeMeal"] = "GLUCOSE-BEFORE-MEAL";
  MeasurementName3["GlucoseAfterMeal"] = "GLUCOSE-AFTER-MEAL";
  MeasurementName3["SystolicArterialBloodPressure"] = "BP-SYSTOLIC";
  MeasurementName3["DiastolicArterialBloodPressure"] = "BP-DIASTOLIC";
  MeasurementName3["MeanArterialBloodPressure"] = "BP-MAP";
  MeasurementName3["CombinedArterialBloodPressure"] = "BP-COMBINED";
  MeasurementName3["Weight"] = "WEIGHT";
  MeasurementName3["PostureCalibration"] = "POS-CALIBRATION";
  MeasurementName3["EarlyWarningScore"] = "EWS";
  MeasurementName3["ButtonPress"] = "BTN";
  MeasurementName3["SensorLowQuality"] = "SENSOR-LOW-QUALITY";
  MeasurementName3["Fall"] = "FALL";
  MeasurementName3["WearStatus"] = "WEAR";
  MeasurementName3["DeviceStatus"] = "DEVICE";
  return MeasurementName3;
})(MeasurementName || {});
var MeasurementNameSchema = z4.nativeEnum(MeasurementName);
var TimeToLiveOffsetSeconds = /* @__PURE__ */ ((TimeToLiveOffsetSeconds2) => {
  TimeToLiveOffsetSeconds2[TimeToLiveOffsetSeconds2["Minute"] = 60] = "Minute";
  TimeToLiveOffsetSeconds2[TimeToLiveOffsetSeconds2["Hour"] = 3600] = "Hour";
  TimeToLiveOffsetSeconds2[TimeToLiveOffsetSeconds2["Day"] = 86400] = "Day";
  TimeToLiveOffsetSeconds2[TimeToLiveOffsetSeconds2["Week"] = 604800] = "Week";
  TimeToLiveOffsetSeconds2[TimeToLiveOffsetSeconds2["Month"] = 2629743] = "Month";
  TimeToLiveOffsetSeconds2[TimeToLiveOffsetSeconds2["Year"] = 31556926] = "Year";
  return TimeToLiveOffsetSeconds2;
})(TimeToLiveOffsetSeconds || {});
var longTermMetrics = [
  "BP-COMBINED" /* CombinedArterialBloodPressure */,
  "BP-SYSTOLIC" /* SystolicArterialBloodPressure */,
  "BP-DIASTOLIC" /* DiastolicArterialBloodPressure */,
  "BP-MAP" /* MeanArterialBloodPressure */,
  "GLUCOSE" /* Glucose */,
  "GLUCOSE-BEFORE-MEAL" /* GlucoseBeforeMeal */,
  "GLUCOSE-AFTER-MEAL" /* GlucoseAfterMeal */,
  "GLUCOSE-FASTING" /* GlucoseFasting */,
  "WEIGHT" /* Weight */
];
var longTermMetricTTL = 6 * 2629743 /* Month */;
var MeasurementTTLMap = {
  ["BP-COMBINED" /* CombinedArterialBloodPressure */]: longTermMetricTTL,
  ["BP-SYSTOLIC" /* SystolicArterialBloodPressure */]: longTermMetricTTL,
  ["BP-DIASTOLIC" /* DiastolicArterialBloodPressure */]: longTermMetricTTL,
  ["BP-MAP" /* MeanArterialBloodPressure */]: longTermMetricTTL,
  ["GLUCOSE" /* Glucose */]: longTermMetricTTL,
  ["GLUCOSE-BEFORE-MEAL" /* GlucoseBeforeMeal */]: longTermMetricTTL,
  ["GLUCOSE-AFTER-MEAL" /* GlucoseAfterMeal */]: longTermMetricTTL,
  ["GLUCOSE-FASTING" /* GlucoseFasting */]: longTermMetricTTL,
  ["WEIGHT" /* Weight */]: longTermMetricTTL
};
var PostureReading = /* @__PURE__ */ ((PostureReading2) => {
  PostureReading2[PostureReading2["LayingDown"] = 1] = "LayingDown";
  PostureReading2[PostureReading2["Sitting"] = 2] = "Sitting";
  PostureReading2[PostureReading2["LeaningBack"] = 3] = "LeaningBack";
  PostureReading2[PostureReading2["Standing"] = 4] = "Standing";
  PostureReading2[PostureReading2["Walking"] = 5] = "Walking";
  PostureReading2[PostureReading2["Running"] = 6] = "Running";
  PostureReading2[PostureReading2["Unknown"] = -1] = "Unknown";
  PostureReading2[PostureReading2["NotFound"] = -2] = "NotFound";
  return PostureReading2;
})(PostureReading || {});
var PostureCalibration = /* @__PURE__ */ ((PostureCalibration2) => {
  PostureCalibration2[PostureCalibration2["Upright"] = 1] = "Upright";
  PostureCalibration2[PostureCalibration2["LayingSupine"] = 2] = "LayingSupine";
  return PostureCalibration2;
})(PostureCalibration || {});
var timestampRefinement = (obj, ctx) => {
  const { start, end } = obj;
  if (start > end) {
    ctx.addIssue({
      code: "custom",
      message: "Start timestamp cannot be greater than end timestamp",
      path: ["start", "end"]
    });
  }
  const startDate = new Date(start);
  if (isNaN(startDate.getTime())) {
    ctx.addIssue({
      code: "custom",
      message: "Invalid start timestamp",
      path: ["start"]
    });
  }
  const endDate = new Date(end);
  if (isNaN(endDate.getTime())) {
    ctx.addIssue({
      code: "custom",
      message: "Invalid end timestamp",
      path: ["end"]
    });
  }
};
var optionalTimestampRefinement = (obj, ctx) => {
  const { start, end } = obj;
  if (start && end && start > end) {
    ctx.addIssue({
      code: "custom",
      message: "Start timestamp cannot be greater than end timestamp",
      path: ["start", "end"]
    });
  }
  if (start) {
    const startDate = new Date(start);
    if (isNaN(startDate.getTime())) {
      ctx.addIssue({
        code: "custom",
        message: "Invalid start timestamp",
        path: ["start"]
      });
    }
  }
  if (end) {
    const endDate = new Date(end);
    if (isNaN(endDate.getTime())) {
      ctx.addIssue({
        code: "custom",
        message: "Invalid end timestamp",
        path: ["end"]
      });
    }
  }
};
var VitalTimeRangeOptionalSchema = z4.object({
  patientId: UUIDSchema.describe("The ID of the patient"),
  start: z4.number().int().min(0).optional().describe("The start timestamp in epoch format"),
  end: z4.number().int().min(0).optional().describe("The end timestamp in epoch format")
});
var VitalTimeRangeSchema = z4.object({
  start: z4.number().int().min(0).describe("The start timestamp in epoch format"),
  end: z4.number().int().min(0).describe("The end timestamp in epoch format")
});
var MeasurementSchema = z4.object({
  ts: z4.number().describe("The timestamp when the measurement was taken"),
  value: z4.string().describe("The value of the measurement"),
  type: MeasurementNameSchema.describe("The type of the measurement")
});
var VitalMeasurementSchema = MeasurementSchema.extend({
  sensorId: z4.string().describe("The ID of the sensor that created the measurement"),
  relayTs: z4.string().describe("The timestamp when the measurement was received on the relay device"),
  cloudTs: z4.string().describe("The timestamp when the measurement was received by the cloud"),
  metadata: z4.record(z4.string()).describe("Additional metadata for the measurement")
});
var BaseTimeSeriesRecordSchema = z4.object({
  ts: z4.number().describe("The timestamp when the recording happened"),
  value: z4.number().describe("The measurement value of the record"),
  measurementName: z4.nativeEnum(MeasurementName).describe("The name of the measurement that is being recorded")
});
var SensorTimeSeriesRecordSchema = BaseTimeSeriesRecordSchema.extend({
  sensorId: z4.string().describe("The ID of the sensor that created the measurement")
});
var VitalsResultSchema = z4.array(SensorTimeSeriesRecordSchema).optional();
var LatestResultSchema = SensorTimeSeriesRecordSchema.optional();
var measurementNameSchema = z4.nativeEnum(MeasurementName);
var VitalsResultMeasuresSchema = z4.record(measurementNameSchema, VitalsResultSchema);
var LatestResultMeasuresSchema = z4.record(measurementNameSchema, LatestResultSchema);
var LatestResultNotesMeasuresSchema = z4.record(
  measurementNameSchema,
  BaseTimeSeriesRecordSchema.extend({
    sensorId: z4.string().optional()
  })
);
var LiveDataMeasuresSchema = z4.object({
  latest: LatestResultMeasuresSchema,
  ecg: SensorTimeSeriesRecordSchema.array()
});
var VitalsMeasurementRequestSchema = z4.object({
  patientId: UUIDSchema,
  measurementName: measurementNameSchema,
  start: z4.number(),
  end: z4.number()
});
var VitalsQueryParamsSchema = VitalTimeRangeOptionalSchema.superRefine(
  optionalTimestampRefinement
);
var VitalsPaginationTimingsSchema = VitalTimeRangeSchema.superRefine(timestampRefinement);
var createAggregateDataSchema = (dataSchema, errorSchema) => {
  const successSchema = z4.object({
    type: z4.literal("success"),
    data: dataSchema
  });
  const defaultErrorSchema = z4.object({
    type: z4.literal("error"),
    error: errorSchema
  });
  const unionSchema = z4.discriminatedUnion("type", [successSchema, defaultErrorSchema]);
  return { unionSchema, successSchema, defaultErrorSchema };
};
var {
  unionSchema: VitalsAggregateDataSchema,
  successSchema: VitalsAggregateDataSuccessSchema,
  defaultErrorSchema: VitalsAggregateDataErrorSchema
} = createAggregateDataSchema(VitalsResultMeasuresSchema, z4.any());
var {
  unionSchema: LatestAggregateDataSchema,
  successSchema: LatestAggregateDataSuccessSchema,
  defaultErrorSchema: LatestAggregateDataErrorSchema
} = createAggregateDataSchema(LatestResultMeasuresSchema, z4.any());
var VitalsAggregateResultSchema = z4.object({
  vitals: VitalsAggregateDataSchema,
  latest: LatestAggregateDataSchema,
  next: VitalsPaginationTimingsSchema.optional()
});
var VitalsAggregateMeasureResultSchema = z4.object({
  start: z4.number().int().min(0).describe("The start timestamp in epoch format"),
  end: z4.number().int().min(0).describe("The end timestamp in epoch format"),
  records: SensorTimeSeriesRecordSchema.array()
});

// src/patient/mapping.ts
import {
  ExtensionUrl as ExtensionUrl2,
  PatientStatusCode,
  valsOrNone as valsOrNone5,
  updateExtensions,
  createExtensionValue as createExtensionValue2,
  findExtensionValue as findExtensionValue2,
  filterReferencesByType
} from "@quromedical/fhir-common";

// src/mappings/id.ts
import { createIdentifier, valsOrNone } from "@quromedical/fhir-common";
import { compact } from "@quromedical/utils";
var createOptionalIdentifier = (code, value) => {
  if (!value) {
    return void 0;
  }
  return createIdentifier(code, value);
};
var attachIdentifiers = (resource, ...identifiers) => {
  const identifier = [...resource.identifier || [], ...compact(identifiers)];
  return {
    ...resource,
    identifier: valsOrNone(identifier)
  };
};

// src/mappings/reference.ts
import {
  getReferenceData,
  getVersionReferenceData,
  isVersionReference
} from "@quromedical/fhir-common";
import { flatten } from "@quromedical/utils";
var filterResourcesByType = (resourceType, resources = []) => resources.filter((resource) => resource.resourceType === resourceType);
var doesResourceMatchReference = (referenceResourceType, referenceId, resource) => resource.resourceType === referenceResourceType && resource.id === referenceId;
var findReferencedResources = (references, resources) => {
  const foundResources = references.map((ref) => {
    const referenceData = getReferenceData(ref);
    if (!referenceData) {
      return [];
    }
    const { resourceType, id } = referenceData;
    return resources.filter((resource) => doesResourceMatchReference(resourceType, id, resource));
  });
  return flatten(foundResources);
};
var findReferencedResource = (reference, resources = []) => {
  const referenceData = getReferenceData(reference);
  if (!referenceData) {
    return void 0;
  }
  return resources.find(
    (resource) => doesResourceMatchReference(referenceData.resourceType, referenceData.id, resource)
  );
};
var isValidReference = (reference) => !!reference?.reference;
var getValidReference = (reference) => {
  if (isValidReference(reference)) {
    return reference;
  }
  return void 0;
};
var getValidVersionReference = (resourceTypes, reference) => {
  const isReference = isVersionReference(reference);
  if (!isReference) {
    return void 0;
  }
  const { resourceType } = getVersionReferenceData(reference);
  const isValidResourceType = resourceTypes.includes(resourceType);
  if (!isValidResourceType) {
    return void 0;
  }
  return reference;
};

// src/mappings/bundle.ts
import { URL } from "url";
var getPaginationToken = (relation, bundle) => {
  const { link } = bundle;
  const found = link?.find((l) => l.relation === relation);
  if (!found) {
    return void 0;
  }
  const url = new URL(found.url);
  const pageToken = url.searchParams.get("_page_token");
  if (!pageToken) {
    return void 0;
  }
  return pageToken;
};
var getPaginationTokenEncoded = (relation, bundle) => {
  const pageToken = getPaginationToken(relation, bundle);
  return pageToken ? Buffer.from(pageToken).toString("base64") : void 0;
};
var getBundlePaginationTokens = (bundle) => {
  const previousToken = getPaginationToken("previous", bundle);
  const nextToken = getPaginationToken("next", bundle);
  return {
    previousToken,
    nextToken
  };
};
var getBundlePaginationTokensEncoded = (bundle) => {
  const previousToken = getPaginationTokenEncoded("previous", bundle);
  const nextToken = getPaginationTokenEncoded("next", bundle);
  return {
    previousToken,
    nextToken
  };
};

// src/person/mapping.ts
import {
  getPersonNames,
  getIdentifier,
  getEmail,
  getCity,
  getCountry,
  getPostalCode,
  getAddressLines,
  getContactValues,
  createFullHumanName,
  createFhirDate,
  createCodeableConcept,
  valsOrNone as valsOrNone4,
  createExtensionValue,
  ExtensionUrl,
  findExtensionValue,
  LanguagePreferenceDisplay,
  system,
  getFirstCodeFromCodableConcepts
} from "@quromedical/fhir-common";
import { compact as compact3, first as first2 } from "@quromedical/utils";

// src/organization/address.ts
import { valsOrNone as valsOrNone2 } from "@quromedical/fhir-common";
var doseAddressHaveData = (address) => {
  const hasValue = !!address;
  if (!hasValue) {
    return false;
  }
  const hasLines = address.line?.length;
  const hasData = address.city || address.country || address.state || address.postalCode || address.district || address.extension || hasLines;
  return !!hasData;
};
var createAddressList = (address) => {
  const hasAddress = doseAddressHaveData(address);
  if (!hasAddress) {
    return void 0;
  }
  return [{ ...address, line: valsOrNone2(address.line) }];
};

// src/organization/contact-point.ts
import { valsOrNone as valsOrNone3 } from "@quromedical/fhir-common";
import { compact as compact2, partial } from "@quromedical/utils";
var createContactPoint = (system9, rank, value) => {
  if (!value) {
    return void 0;
  }
  return {
    system: system9,
    rank,
    value
  };
};
var createEmailContactPoint = partial(createContactPoint, "email", 1);
var createPrimaryPhoneContactPoint = partial(createContactPoint, "phone", 1);
var createSecondaryPhoneContactPoint = partial(createContactPoint, "phone", 2);
var mergeContactPoints = (contactPoint) => {
  const compacted = compact2(contactPoint);
  return valsOrNone3(compacted);
};

// src/person/schema.ts
import {
  HumanNamePrefix,
  IdentifierCode as ResourceIdentifierCode,
  CountryCode,
  LanguagePreference
} from "@quromedical/fhir-common";
import { z as z5 } from "zod";
var IdentifierCode2 = ((IdentifierCode16) => {
  IdentifierCode16[IdentifierCode16["saIdNumber"] = ResourceIdentifierCode.southAfricanIdNumber] = "saIdNumber";
  IdentifierCode16[IdentifierCode16["namibiaIdNumber"] = ResourceIdentifierCode.namibianIdNumber] = "namibiaIdNumber";
  IdentifierCode16[IdentifierCode16["passportNumber"] = ResourceIdentifierCode.passportNumber] = "passportNumber";
  return IdentifierCode16;
})(IdentifierCode2 || {});
var GenderSchema = z5.union([
  z5.literal("unknown"),
  z5.literal("male"),
  z5.literal("female"),
  z5.literal("other")
]);
var GeneralSchema = z5.object({
  prefixName: z5.nativeEnum(HumanNamePrefix),
  givenName: z5.string(),
  familyName: z5.string(),
  gender: GenderSchema,
  birthDate: z5.string(),
  identifierCode: z5.nativeEnum(IdentifierCode2),
  identifierValue: z5.string()
});
var GeneralUpdateSchema = z5.object({
  id: z5.string().uuid(),
  general: GeneralSchema
});
var PersonGeneralGetResponseSchema = GeneralSchema.partial();
var AddressEmergencyAccessSchema = z5.object({
  addressEmergencyAccess: z5.string()
});
var CoordinatesSchema = z5.object({
  latitude: z5.number(),
  longitude: z5.number()
});
var primaryPhoneRegex = /^\+[0-9]?[0-9]{0,16}$/;
var alternatePhoneRegex = /^\+[0-9]?[0-9]{0,16}$/;
var PrimaryPhoneNumberSchema = z5.string().regex(primaryPhoneRegex, {
  message: "Primary phone number must start with '+27' and be 12 characters long."
});
var AlternatePhoneNumberSchema = z5.string().regex(alternatePhoneRegex, {
  message: "Alternate phone number must be in international phone number format +27XXXXXXXXX"
}).optional();
var telecomPhoneNumbersSchema = z5.array(AlternatePhoneNumberSchema).transform((numbers) => numbers.filter((num) => num !== void 0)).refine((lines) => PrimaryPhoneNumberSchema.safeParse(lines[0]).success, {
  message: "The primary phone number must be in international phone number format +27XXXXXXXXX"
});
var ContactSchema = z5.object({
  languagePreference: z5.nativeEnum(LanguagePreference).optional(),
  /**
   * In formik you can reference this as `phoneNumbers.0` and `phoneNumbers.1` to populate array
   * values if there isn't an appropriate multi-text input
   */
  telecomPhoneNumbers: telecomPhoneNumbersSchema,
  telecomEmail: z5.string(),
  /**
   * Google address place_Id is from google place autocomplete
   */
  addressPlaceId: z5.string().optional(),
  addressCoordinates: CoordinatesSchema.optional(),
  addressLines: z5.array(z5.string()),
  addressCity: z5.string(),
  addressCountry: z5.nativeEnum(CountryCode),
  addressPostalCode: z5.string()
});
var ContactGetSchema = ContactSchema.omit({ telecomPhoneNumbers: true }).extend({
  telecomPhoneNumbers: z5.array(z5.string())
});
var ContactUpdateSchema = z5.object({
  id: z5.string().uuid(),
  contact: ContactSchema
});
var PartialPersonSchema = z5.object({
  general: GeneralSchema.partial(),
  contact: ContactGetSchema.partial()
});
var PersonSchema = z5.object({
  general: GeneralSchema,
  contact: ContactSchema
});
var PersonContactWithAddressEmergencyAccessSchema = ContactSchema.and(
  AddressEmergencyAccessSchema.partial()
);
var PersonContactGetResponseSchema = ContactGetSchema.partial();

// src/person/mapping.ts
var identifierCodes = Object.values(IdentifierCode2);
var isValidID = (id) => !!(id.code && id.value);
var getPersonIdentifier = (identifier) => identifierCodes.map((code) => ({
  code,
  value: getIdentifier(code, identifier)
})).find(isValidID);
var mapFhirPersonToGeneralModel = (person) => {
  const { gender = "unknown", birthDate, identifier } = person;
  const { familyName, givenName, prefix } = getPersonNames(person);
  const id = getPersonIdentifier(identifier);
  return {
    gender,
    birthDate,
    givenName,
    familyName,
    prefixName: prefix,
    identifierValue: id?.value,
    identifierCode: id?.code
  };
};
var getEmergencyAccess = findExtensionValue(ExtensionUrl.addressEmergencyAccess);
var getAddressPlaceId = findExtensionValue(ExtensionUrl.addressGooglePlaceId);
var getAddressLongitude = findExtensionValue(ExtensionUrl.addressLongitude);
var getAddressLatitude = findExtensionValue(ExtensionUrl.addressLatitude);
var mapFhirPersonToContactModel = (person) => {
  const { telecom, address, communication } = person;
  const telecomEmail = getEmail(telecom);
  const telecomPhoneNumbers = getContactValues("phone", telecom);
  const addressCity = getCity(address);
  const addressPostalCode = getPostalCode(address);
  const addressLines = getAddressLines(address);
  const firstAddress = first2(address);
  const addressCountry = getCountry(firstAddress);
  const addressEmergencyAccess = getEmergencyAccess(firstAddress?.extension);
  const addressPlaceId = getAddressPlaceId(firstAddress?.extension);
  const addressCoordinates = getCoordinates(firstAddress);
  if (person.resourceType === "Patient") {
    const patientCommunication = communication;
    const languageCodableConcept = first2(patientCommunication)?.language;
    const languagePreference = languageCodableConcept ? getFirstCodeFromCodableConcepts([languageCodableConcept]) : void 0;
    return {
      addressLines,
      addressCity,
      addressCountry,
      telecomEmail,
      telecomPhoneNumbers,
      addressPostalCode,
      addressEmergencyAccess,
      addressPlaceId,
      addressCoordinates,
      languagePreference
    };
  }
  if (person.resourceType === "RelatedPerson") {
    const patientCommunication = communication;
    const languageCodableConcept = first2(patientCommunication)?.language;
    const languagePreference = languageCodableConcept ? getFirstCodeFromCodableConcepts([languageCodableConcept]) : void 0;
    return {
      addressLines,
      addressCity,
      addressCountry,
      telecomEmail,
      telecomPhoneNumbers,
      addressPostalCode,
      addressEmergencyAccess,
      addressPlaceId,
      addressCoordinates,
      languagePreference
    };
  }
  return {
    addressLines,
    addressCity,
    addressCountry,
    telecomEmail,
    telecomPhoneNumbers,
    addressPostalCode,
    addressEmergencyAccess,
    addressPlaceId,
    addressCoordinates
  };
};
var createIdentifier2 = (identifierCode, identifierValue) => {
  if (identifierCode && identifierValue) {
    return {
      type: createCodeableConcept(identifierCode),
      value: identifierValue
    };
  }
  return void 0;
};
var mapPersonGeneralToFhirPerson = (person) => {
  const { gender, birthDate, familyName, givenName, prefixName, identifierCode, identifierValue } = person;
  const name = createFullHumanName({
    given: givenName,
    family: familyName,
    prefix: prefixName,
    use: "official"
  });
  const dateOfBirth = birthDate ? createFhirDate(new Date(birthDate)) : void 0;
  const requiredIdentifier = createIdentifier2(identifierCode, identifierValue);
  const identifier = compact3(valsOrNone4([requiredIdentifier]));
  return {
    birthDate: dateOfBirth,
    gender,
    name,
    identifier
  };
};
var mergePersonGeneralWithFhir = (fhir, general) => {
  const fhirPartial = mapPersonGeneralToFhirPerson(general);
  return { ...fhir, ...fhirPartial };
};
var createEmergencyAccessExtension = createExtensionValue(ExtensionUrl.addressEmergencyAccess);
var createAddressPlaceId = createExtensionValue(ExtensionUrl.addressGooglePlaceId);
var createAddressLatitude = createExtensionValue(ExtensionUrl.addressLatitude);
var createAddressLongitude = createExtensionValue(ExtensionUrl.addressLongitude);
var isPatientContact = (contact) => !!contact?.addressEmergencyAccess;
var createAddressExtensionList = (contact = {}) => {
  const { addressCoordinates, addressPlaceId } = contact;
  const addressEmergencyAccess = isPatientContact(contact) && contact.addressEmergencyAccess;
  const emergencyAccess = addressEmergencyAccess && createEmergencyAccessExtension(addressEmergencyAccess);
  const placeId = addressPlaceId && createAddressPlaceId(addressPlaceId);
  const coordinatesLatitude = addressCoordinates && createAddressLatitude(addressCoordinates.latitude);
  const coordinatesLongitude = addressCoordinates && createAddressLongitude(addressCoordinates.longitude);
  const extensions = [emergencyAccess, placeId, coordinatesLatitude, coordinatesLongitude];
  return valsOrNone4(compact3(extensions));
};
var mapPersonContactToFhirPartial = (contact) => {
  const [primary, secondary] = contact.telecomPhoneNumbers || [];
  const telecom = mergeContactPoints([
    createEmailContactPoint(contact.telecomEmail),
    createPrimaryPhoneContactPoint(primary),
    createSecondaryPhoneContactPoint(secondary)
  ]);
  const extension = createAddressExtensionList(contact);
  const address = {
    line: contact.addressLines,
    postalCode: contact.addressPostalCode,
    city: contact.addressCity,
    country: contact.addressCountry,
    extension
  };
  const addressList = createAddressList(address);
  return { address: addressList, telecom };
};
var mapPatientContactToFhirPartial = (contact) => {
  const personContact = mapPersonContactToFhirPartial(contact);
  const languagePreference = contact.languagePreference;
  const communication = languagePreference ? [
    {
      language: createCodeableConcept(
        languagePreference,
        LanguagePreferenceDisplay[languagePreference],
        LanguagePreferenceDisplay[languagePreference],
        system
      ),
      preferred: true
    }
  ] : void 0;
  return { ...personContact, communication };
};
var mapRelatedPersonContactToFhirPartial = (contact) => {
  const personContact = mapPersonContactToFhirPartial(contact);
  const languagePreference = contact.languagePreference;
  const communication = languagePreference ? [
    {
      language: createCodeableConcept(
        languagePreference,
        LanguagePreferenceDisplay[languagePreference],
        LanguagePreferenceDisplay[languagePreference],
        system
      ),
      preferred: true
    }
  ] : void 0;
  return { ...personContact, communication };
};
var mergePersonContactWithFhir = (fhir, contact) => {
  const fhirPartial = mapPersonContactToFhirPartial(contact);
  if (fhir.resourceType === "Patient" || fhir.resourceType === "RelatedPerson") {
    const languagePreference = contact.languagePreference;
    const communication = languagePreference ? [
      {
        language: createCodeableConcept(
          languagePreference,
          LanguagePreferenceDisplay[languagePreference],
          LanguagePreferenceDisplay[languagePreference],
          system
        ),
        preferred: true
      }
    ] : void 0;
    fhirPartial.communication = communication;
    return { ...fhir, ...fhirPartial };
  }
  return { ...fhir, ...fhirPartial };
};
var getCoordinates = (firstAddress) => {
  const addressLongitude = getAddressLongitude(firstAddress?.extension);
  const addressLatitude = getAddressLatitude(firstAddress?.extension);
  const hasCoordinates = addressLatitude && addressLongitude;
  const addressCoordinates = hasCoordinates ? {
    latitude: addressLatitude,
    longitude: addressLongitude
  } : void 0;
  return addressCoordinates;
};

// src/person/validation.ts
import { last, sum } from "@quromedical/utils";
import { parse, isValid } from "date-fns";
import * as yup2 from "yup";

// src/person/address.ts
import { CountryCode as CountryCode2 } from "@quromedical/fhir-common";
import * as yup from "yup";
var countryCodes = Object.values(CountryCode2);
var countrySchema = yup.string().required().oneOf(countryCodes);
var linesSchema = yup.mixed().test({
  // the first line of the address is always required
  test: (lines = []) => lines[0]?.length > 0
});
var citySchema = yup.string().required();
var postalCodeSchema = yup.string().required();

// src/person/validation.ts
var toInt = (v) => +v;
var isValidDateOfBirth = (idNumber) => {
  const year = idNumber.slice(0, 2);
  const month = toInt(idNumber.slice(2, 4));
  const day = toInt(idNumber.slice(4, 6));
  const date5 = `${year}-${month}-${day}`;
  const parsedDate = parse(date5, "yy-MM-dd", /* @__PURE__ */ new Date());
  const isValidDate = isValid(parsedDate);
  return isValidDate;
};
var isValidSouthAfricanIdNumber = (input = "") => {
  const trimmed = input.trim();
  if (!isValidDateOfBirth(trimmed)) {
    return false;
  }
  const chars = trimmed.split("");
  const numericRegex = /^\d+$/;
  const isNumeric = numericRegex.test(trimmed);
  if (!isNumeric) {
    return false;
  }
  if (chars.length !== 13) {
    return false;
  }
  const checksum = +chars[chars.length - 1];
  const odds = chars.slice(0, -1).filter((_, i) => i % 2 === 0).map(toInt);
  const evens = chars.filter((_, i) => i % 2 === 1).map(toInt);
  const A = sum(odds);
  const B = evens.join("");
  const C = sum((2 * +B).toString().split("").map(toInt));
  const D = A + C;
  const Z = 10 - D % 10;
  const checkString = last(Z.toString().split(""));
  if (!checkString) {
    return false;
  }
  const checkDigit = toInt(checkString);
  return checkDigit === checksum;
};
var getGenderFromSouthAfricanIdNumber = (input = "") => {
  const isIdValid = isValidSouthAfricanIdNumber(input);
  if (!isIdValid) {
    return void 0;
  }
  const genderIndex = input.trim().split("")[6];
  const gender = toInt(genderIndex) >= 5 ? "male" : "female";
  return gender;
};
var getBirthDateFromSouthAfricanIdNumber = (input = "") => {
  const isIdValid = isValidSouthAfricanIdNumber(input);
  if (!isIdValid) {
    return void 0;
  }
  const trimmed = input.trim().slice(0, 6);
  const relative = /* @__PURE__ */ new Date();
  const birthDate = parse(trimmed, "yyMMdd", relative);
  return birthDate;
};
var phoneRegex = /^\+[0-9]?[0-9]{0,16}$/;
var phoneSchema = yup2.string().required().matches(phoneRegex, {
  message: "Use an international phone number format. e.g. +276451230429"
});
var telecomPhoneNumbersSchema2 = yup2.array(phoneSchema.required()).required().min(1);
var emailSchema = yup2.string().lowercase().email().required();
var saIdNumber = yup2.string().required().test({
  name: "idNumberValidation",
  test: (i) => isValidSouthAfricanIdNumber(i || ""),
  message: "SA ID number is not valid"
}).length(13);
var namibianIdNumber = yup2.string().required().length(11);
var usernameSchema = yup2.string().test({
  name: "AuthUsername",
  test: (username) => {
    const isEmail = emailSchema.isValidSync(username);
    if (isEmail) {
      return true;
    }
    return phoneSchema.isValidSync(username);
  }
});
var telecomPhoneNumbersSchemas = yup2.mixed().test({
  test: (lines = []) => lines[0]?.length > 0
}).test({
  test: (lines = []) => {
    const line = lines.filter((v) => v !== void 0);
    return telecomPhoneNumbersSchema2.isValidSync(line);
  }
});
var personContactFieldSchemas = {
  addressLines: linesSchema,
  addressCity: citySchema,
  addressPostalCode: postalCodeSchema,
  addressCountry: countrySchema,
  telecomEmail: emailSchema,
  // using an object like below due to the way the fields are configured in formik
  telecomPhoneNumbers: telecomPhoneNumbersSchemas,
  addressEmergencyAccess: yup2.string().optional(),
  addressPlaceId: yup2.string().optional()
};
var personContactSchema = yup2.object(personContactFieldSchemas).required();
var genders = ["unknown", "female", "male", "other"];
var identifierCodes2 = Object.values(IdentifierCode2);
var identifierCodeSchema = yup2.string().required().oneOf(identifierCodes2);
var identifierPartialRequiredFieldSchemas = {
  identifierCode: identifierCodeSchema,
  identifierValue: yup2.string().required().when("identifierCode", {
    is: IdentifierCode2.saIdNumber,
    then: saIdNumber,
    otherwise: yup2.string().when("identifierCode", {
      is: IdentifierCode2.namibiaIdNumber,
      then: namibianIdNumber,
      otherwise: yup2.string().required()
    })
  })
};
var personGeneralFieldSchemas = {
  prefixName: yup2.string().required(),
  givenName: yup2.string().required(),
  familyName: yup2.string().required(),
  gender: yup2.string().required().oneOf(genders),
  birthDate: yup2.date().required(),
  ...identifierPartialRequiredFieldSchemas
};
var personMinimalGeneralFieldSchema = yup2.object({
  prefixName: personGeneralFieldSchemas.prefixName,
  givenName: personGeneralFieldSchemas.givenName,
  familyName: personGeneralFieldSchemas.familyName,
  gender: personGeneralFieldSchemas.gender
}).defined();
var personGeneralSchema = yup2.object(personGeneralFieldSchemas).required();

// src/patient/mapping.ts
var statusActiveMap = {
  [PatientStatusCode.Onboarded]: true,
  [PatientStatusCode.Admitted]: true,
  [PatientStatusCode.Discharged]: false,
  [PatientStatusCode.Invalidated]: false
};
var setPatientStatus = (patient, status2) => {
  const extension = updateExtensions(ExtensionUrl2.latestStatus, patient.extension, [
    createExtensionValue2(ExtensionUrl2.latestStatus)(status2)
  ]);
  return {
    ...patient,
    active: statusActiveMap[status2],
    extension
  };
};
var updatePatientStatus = (patient, status2) => {
  const update = setPatientStatus(patient, status2);
  return {
    id: patient.id,
    ...update
  };
};
var mapOnboardModelToFhirPatient = (general, contact) => {
  const generalPartial = mapPersonGeneralToFhirPerson(general);
  const contactPartial = mapPatientContactToFhirPartial(contact);
  const partialPatient = {
    ...generalPartial,
    ...contactPartial,
    resourceType: "Patient"
  };
  const withStatus = setPatientStatus(partialPatient, PatientStatusCode.Onboarded);
  const extension = updateExtensions(ExtensionUrl2.latestStatus, withStatus.extension, [
    createExtensionValue2(ExtensionUrl2.latestStatus)(PatientStatusCode.Onboarded)
  ]);
  return {
    ...withStatus,
    extension
  };
};
var updatePatientCareTeam = (patient, assignedPractitioners, assignedOrganizations) => {
  const updatedPatient = {
    ...patient,
    generalPractitioner: valsOrNone5([...assignedPractitioners, ...assignedOrganizations])
  };
  return updatedPatient;
};
var getPatientCareTeam = (patient) => {
  const practitionersReferences = filterReferencesByType(
    "Practitioner",
    patient.generalPractitioner
  );
  const organizationsReferences = filterReferencesByType(
    "Organization",
    patient.generalPractitioner
  );
  const practitioners = practitionersReferences.filter(isValidReference);
  const organizations = organizationsReferences.filter(isValidReference);
  return { practitioners, organizations };
};
var getPatientStatus = (patient) => findExtensionValue2(ExtensionUrl2.latestStatus)(patient.extension);
var mapFhirToSummaryModelPatient = (fhir, call) => {
  const general = mapFhirPersonToGeneralModel(fhir);
  const contact = mapFhirPersonToContactModel(fhir);
  const status2 = getPatientStatus(fhir);
  const practitionersReferences = filterReferencesByType("Practitioner", fhir.generalPractitioner);
  const organizationsReferences = filterReferencesByType("Organization", fhir.generalPractitioner);
  const practitioners = practitionersReferences.filter(isValidReference);
  const organizations = organizationsReferences.filter(isValidReference);
  return {
    id: fhir.id,
    status: status2,
    contact,
    call,
    general,
    generalPractitioner: {
      organizations,
      practitioners
    }
  };
};

// src/patient/validation.ts
import * as yup3 from "yup";
var generalPractitionerUpdateRequestSchema = yup3.object({
  organizations: yup3.array(yup3.string().required()).min(0).required(),
  practitioners: yup3.array(yup3.string().required()).min(1).required()
});

// src/patient/schema.ts
import {
  IdentifierCode as IdentifierCode3,
  PatientStatusCode as PatientStatusCode2
} from "@quromedical/fhir-common";
import { MedicalAidPersonSchema as MedicalAidPersonSchema2 } from "@quromedical/healthbridge";
import { z as z12 } from "zod";

// src/call/schema.ts
import { z as z6 } from "zod";
var callStatus = ["pending", "in-processing", "success", "failed"];
var CallStatusSchema = z6.enum(callStatus);
var PatientCallSchema = z6.object({
  patientId: UUIDSchema,
  name: z6.string(),
  practitionerName: z6.string(),
  practitionerId: UUIDSchema,
  status: CallStatusSchema,
  callBackTime: z6.number().optional()
});
var WorkloadSchema = z6.object({
  low: z6.number(),
  medium: z6.number(),
  high: z6.number()
});
var CallOverviewBarSchema = z6.object({
  callsCompleted: z6.number(),
  callBacks: z6.number(),
  toCall: z6.number(),
  workload: WorkloadSchema
});

// src/domain/pagination.ts
import { z as z7 } from "zod";
var PaginationSchema = z7.object({
  pageToken: z7.string().optional(),
  count: z7.number().optional()
});
var createPaginatedResponse = (schema) => z7.object({ items: schema.array(), nextToken: z7.string().optional() });

// src/domain/schema.ts
import {
  ampathSystem,
  carboneSystem,
  droppaSystem,
  fhirObservationInterpretationSystem,
  iconoirSystem,
  lancetSystem,
  loincSystem,
  medpraxSystem,
  system as system2,
  twilioSystem
} from "@quromedical/fhir-common";
import { z as z9 } from "zod";

// src/media/base.ts
import { ContentType } from "@quromedical/fhir-common";
import { z as z8 } from "zod";
var ContentTypeSchema = z8.nativeEnum(ContentType);
var MediaDataSchema = z8.object({
  title: z8.string(),
  key: z8.string(),
  contentType: ContentTypeSchema
});

// src/domain/schema.ts
var systemSchema = z9.enum([
  system2,
  medpraxSystem,
  ampathSystem,
  lancetSystem,
  droppaSystem,
  loincSystem,
  fhirObservationInterpretationSystem,
  twilioSystem,
  iconoirSystem,
  carboneSystem
]);
var DateTimeSchema = z9.string().datetime();
var RequiredCodingSchema = z9.object({
  system: z9.string(),
  code: z9.string(),
  display: z9.string().optional(),
  version: z9.string().optional()
});
var CodingSchema = z9.object({
  code: z9.string(),
  display: z9.string().optional(),
  system: z9.string().optional(),
  version: z9.string().optional()
});
var DisplayCodeableConceptSchema = z9.object({
  coding: z9.array(CodingSchema),
  text: z9.string().optional()
});
var BundleLinkSchema = z9.object({
  relation: z9.string(),
  url: z9.string()
});
var PaginatedBundleSchema = z9.object({
  link: BundleLinkSchema.array().optional()
});
var PartialPeriodSchema = z9.object({
  start: z9.string().optional(),
  end: z9.string().optional()
});
var PeriodSchema = z9.object({
  start: z9.string(),
  end: z9.string().optional()
});
var PaginatedResponseSchema = z9.object({
  items: z9.any().array(),
  link: BundleLinkSchema.array().optional()
});
var WithIdSchema = z9.object({ id: z9.string() });
var dateSchema = z9.union([z9.string().pipe(z9.coerce.date()), z9.date()]);
var comparatorSchema = z9.enum(["<", "<=", ">=", ">"]);
var QuantitySchema = z9.object({
  value: z9.number().optional(),
  unit: z9.string().optional(),
  system: z9.string().optional(),
  code: z9.string().optional(),
  comparator: comparatorSchema.optional()
});
var RangeSchema = z9.object({
  low: QuantitySchema.optional(),
  high: QuantitySchema.optional()
});
var RatioSchema = z9.object({
  numerator: QuantitySchema.optional(),
  denominator: QuantitySchema.optional()
});
var SampleDataSchema = z9.object({
  origin: QuantitySchema,
  period: z9.number(),
  factor: z9.number().optional(),
  lowerLimit: z9.number().optional(),
  upperLimit: z9.number().optional(),
  dimensions: z9.number(),
  data: z9.string().optional()
});
var ValueQuantitySchema = z9.object({
  valueQuantity: QuantitySchema
});
var ValueCodeableConceptSchema = z9.object({
  valueCodeableConcept: DisplayCodeableConceptSchema
});
var ValueStringSchema = z9.object({
  valueString: z9.string()
});
var ValueRangeSchema = z9.object({
  valueRange: RangeSchema
});
var ValueRatioSchema = z9.object({
  valueRatio: RatioSchema
});
var ValueSampledDataSchema = z9.object({
  valueSampledData: SampleDataSchema
});
var ValueAttachmentSchema = z9.object({
  valueAttachment: MediaDataSchema
});
var ValueTimeSchema = z9.object({
  valueTime: z9.string()
});
var ValueDateTimeSchema = z9.object({
  valueDateTime: z9.string()
});
var ValuePeriodSchema = z9.object({
  valuePeriod: PeriodSchema
});
var ValueTypeSchema = ValueQuantitySchema.or(ValueCodeableConceptSchema).or(ValueStringSchema).or(ValueRangeSchema).or(ValueRatioSchema).or(ValueSampledDataSchema).or(ValueAttachmentSchema).or(ValueTimeSchema).or(ValueDateTimeSchema).or(ValuePeriodSchema);

// src/medical-aid/schema.ts
import { z as z10 } from "zod";
var SchemeSchema = z10.object({
  administrator: z10.string().optional(),
  schemeCode: z10.string(),
  schemeName: z10.string()
});
var MedicalAidSchema = SchemeSchema.extend({
  plan: z10.string(),
  membershipNumber: z10.string()
});
var MedicalAidMembershipSchema = z10.object({
  medicalAid: MedicalAidSchema,
  dependantCode: z10.string(),
  isEligibleAsNextOfKin: z10.boolean(),
  isMainMember: z10.boolean(),
  isActive: z10.boolean(),
  start: z10.string(),
  end: z10.string().optional(),
  status: z10.string()
});
var MedicalAidPersonSchema = PartialPersonSchema.extend({
  medicalAidMembership: MedicalAidMembershipSchema.partial().optional()
});
var MedicalAidValidationResponseSchema = z10.array(MedicalAidPersonSchema.partial());
var MedicalAidValidationMembershipNumberRequestSchema = z10.object({
  schemeCode: z10.string(),
  membershipNumber: z10.string()
});
var MedicalAidValidationIdNumberRequestSchema = z10.object({
  schemeCode: z10.string(),
  idNumber: z10.string()
});
var MedicalAidValidationRequestApiSchema = z10.object({
  schemeCode: z10.string(),
  idNumber: z10.string().optional(),
  membershipNumber: z10.string().optional()
});
var AdministratorSchema = z10.object({
  administratorCode: z10.string(),
  administratorName: z10.string()
});
var GetAdministratorsResponseSchema = z10.object({
  administrators: AdministratorSchema.array()
});
var GetSchemesResponseSchema = z10.object({
  schemes: SchemeSchema.array()
});
var coverageStatus = ["active", "cancelled", "draft", "entered-in-error"];
var PatientMedicalAidMembershipSchema = z10.object({
  mainMember: DisplayReferenceSchema,
  patient: DisplayReferenceSchema,
  scheme: DisplayReferenceSchema,
  status: z10.enum(coverageStatus),
  memberNumber: z10.string(),
  dependantCode: z10.string(),
  plan: z10.string()
});

// src/related-person/mapping.ts
import {
  createCodeableConcept as createCodeableConcept2,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts2
} from "@quromedical/fhir-common";
var mapModelWithIdToFhirRelatedPerson = (relatedPerson) => {
  const { id } = relatedPerson;
  const result = mapModelToFhirRelatedPerson(relatedPerson);
  return {
    ...result,
    id
  };
};
var mapModelToFhirRelatedPerson = (relatedPerson) => {
  const { patient, contact, general, relationship } = relatedPerson;
  const generalResult = mapPersonGeneralToFhirPerson(general);
  const contactResult = mapRelatedPersonContactToFhirPartial(contact);
  return {
    ...generalResult,
    ...contactResult,
    resourceType: "RelatedPerson",
    active: true,
    patient,
    relationship: [createCodeableConcept2(relationship)]
  };
};
var mapFhirToRelatedPersonModel = (fhir) => {
  const contact = mapFhirPersonToContactModel(fhir);
  const general = mapFhirPersonToGeneralModel(fhir);
  const relationship = getFirstCodeFromCodableConcepts2(
    fhir.relationship
  );
  const patient = getValidReference(fhir.patient);
  return {
    contact,
    general,
    relationship,
    patient
  };
};
var mapFhirToRelatedPersonModelWithId = (fhir) => {
  const result = mapFhirToRelatedPersonModel(fhir);
  return {
    id: fhir.id,
    ...result
  };
};

// src/related-person/schema.ts
import { RelatedPersonRelationship as RelatedPersonRelationship2 } from "@quromedical/fhir-common";
import { z as z11 } from "zod";
var RelatedPersonSchema = z11.object({
  general: GeneralSchema.partial(),
  contact: ContactSchema.partial(),
  patient: DisplayReferenceSchema,
  relationship: z11.nativeEnum(RelatedPersonRelationship2)
});
var CreateRelatedPersonSchema = RelatedPersonSchema.pick({
  contact: true,
  general: true
});
var RelatedPersonWithIdSchema = RelatedPersonSchema.extend({
  id: z11.string().uuid()
});
var RelatedPersonUpdateSchema = z11.object({
  id: UUIDSchema,
  general: GeneralSchema.pick({
    prefixName: true,
    givenName: true,
    familyName: true
  }).extend({
    gender: GenderSchema.optional(),
    birthDate: z11.string().optional(),
    identifierCode: z11.nativeEnum(IdentifierCode2).optional(),
    identifierValue: z11.string().optional()
  })
});
var PartialRelatedPersonSchema = z11.object({
  general: GeneralSchema.partial(),
  contact: ContactGetSchema.partial(),
  patient: DisplayReferenceSchema.optional(),
  relationship: z11.nativeEnum(RelatedPersonRelationship2).optional()
});
var RelatedPersonGetResponseSchema = PartialRelatedPersonSchema.extend({
  id: UUIDSchema
});
var RelatedPersonSearchParamsSchema = z11.object({
  patient: z11.string().uuid().optional(),
  name: z11.string().optional(),
  email: z11.string().optional(),
  phone: z11.string().optional(),
  relationship: z11.nativeEnum(RelatedPersonRelationship2).optional()
});
var RelatedPersonGetAllResponseSchema = PaginatedBundleSchema.extend({
  relatedPersons: RelatedPersonGetResponseSchema.array()
});

// src/patient/schema.ts
var IdType = ((IdType2) => {
  IdType2[IdType2["saIdNumber"] = IdentifierCode3.southAfricanIdNumber] = "saIdNumber";
  IdType2[IdType2["namibiaIdNumber"] = IdentifierCode3.namibianIdNumber] = "namibiaIdNumber";
  IdType2[IdType2["passportNumber"] = IdentifierCode3.passportNumber] = "passportNumber";
  return IdType2;
})(IdType || {});
var GeneralPractitionerUpdateRequestSchema = z12.object({
  practitioners: z12.string().array(),
  organizations: z12.string().array(),
  careTeams: z12.string().array().optional()
});
var PatientGeneralPractitionerUpdateSchema = GeneralPractitionerUpdateRequestSchema.extend(
  {
    id: UUIDSchema
  }
);
var GeneralPractitionerSchema = z12.object({
  practitioners: DisplayReferenceSchema.array(),
  organizations: DisplayReferenceSchema.array(),
  careTeams: DisplayReferenceSchema.array().optional()
});
var CoverageIdentifiersSchema = z12.object({
  schemeCode: z12.string(),
  dependantCode: z12.string(),
  subscriberId: z12.string()
});
var PatientRequiredCodingSchema = z12.object({
  code: z12.string(),
  system: z12.string(),
  display: z12.string().optional(),
  userSelected: z12.boolean().optional(),
  version: z12.string().optional()
});
var PatientSummarySchema = PartialPersonSchema.extend({
  status: z12.nativeEnum(PatientStatusCode2).optional(),
  generalPractitioner: GeneralPractitionerSchema,
  call: PatientCallSchema.optional()
});
var PatientGetResponseSchema = PatientSummarySchema.extend({
  id: UUIDSchema
});
var PatientRequestSchema = UUIDInputSchema.extend({
  withCall: z12.boolean().optional()
});
var PatientGetAllResponseSchema = PaginatedBundleSchema.extend({
  patients: PatientGetResponseSchema.array()
});
var PatientSearchSchema = PaginationSchema.extend({
  sort: z12.string().optional().describe("The field to sort by"),
  active: z12.boolean().optional().describe("Whether the patient is active"),
  name: z12.string().optional().describe("The name of the patient"),
  identifier: z12.string().optional().describe("The ID number of the patient")
});
var PatientPrivateOnboardRequestSchema = z12.object({
  patient: PartialPersonSchema,
  nextOfKin: CreateRelatedPersonSchema
});
var PartialPersonWithoutGeneralSchema = PartialPersonSchema.omit({
  general: true
});
var PatientMedicalAidOnboardSchema = PartialPersonWithoutGeneralSchema.extend({
  nextOfKin: PartialRelatedPersonSchema.partial().optional()
});
var PatientPrivateOnboardSchema = PartialPersonSchema.extend({
  id: z12.string().uuid(),
  nextOfKin: RelatedPersonGetResponseSchema.optional()
});
var MedicalAidOnboardPatientSchema = z12.object({
  /**
   * For now it is assumed that this is the SA ID number, but at a later stage we may need to add an
   * identifier code below
   */
  identifierValue: z12.string(),
  contact: PersonContactWithAddressEmergencyAccessSchema
});
var PatientMedicalAidUpdateRequestSchema = z12.object({
  /**
   * To convert a patient to a medical aid patient all we need is the selected coverage identifiers
   */
  coverage: CoverageIdentifiersSchema,
  id: UUIDSchema
});
var Coordinate = z12.object({
  latitude: z12.number(),
  longitude: z12.number()
});
var CreatePatientParamsSchema = z12.object({
  /**
   * Will be override by the medical-aid if it provides us with an identifier
   */
  identifierValue: z12.string(),
  contact: ContactSchema.omit({ addressCoordinates: true }),
  membership: MedicalAidPersonSchema2.partial(),
  /**
   * Will override / unset address coordinates from contact
   */
  addressCoordinates: Coordinate.optional()
});
var PrivateToMedicalAidConversionResponseSchema = z12.object({
  patientId: z12.string()
});
var PatientOnboardResponseSchema = z12.object({
  patientId: z12.string()
});
var PatientMedicalAidOnboardRequestSchema = z12.object({
  patient: MedicalAidOnboardPatientSchema,
  nextOfKin: CreateRelatedPersonSchema,
  coverage: CoverageIdentifiersSchema
});
var StartPatientCallRequestSchema = z12.object({
  patientId: UUIDSchema
});
var EndPatientCallRequestSchema = z12.object({
  patientId: UUIDSchema,
  timeValue: z12.number().int().min(0),
  status: CallStatusSchema
});
var UpdateGeneralPatientSchema = GeneralUpdateSchema;
var PatientCoverageResponseSchema = z12.object({
  coverage: PatientMedicalAidMembershipSchema.partial().optional(),
  mainMember: MedicalAidPersonSchema2.pick({ contact: true, general: true }).partial().optional()
});
var PatientCreateCoverageResponseSchema = z12.object({
  coverage: PatientMedicalAidMembershipSchema.partial(),
  mainMember: MedicalAidPersonSchema2.pick({ contact: true, general: true }).partial().optional()
});
var PatientMedicalAidOnboardUpdateSchema = PersonSchema.extend({
  id: z12.string().uuid(),
  nextOfKin: RelatedPersonGetResponseSchema,
  medicalAidMembership: MedicalAidMembershipSchema
});
var PatientQuerySearchSchema = z12.object({
  id: UUIDSchema,
  search: z12.string().describe("The search query to search for")
});
var PatientQuerySearchResponseSchema = z12.object({
  summary: z12.string().describe("The AI summary of the search results")
});

// src/notes/schema.ts
import {
  ClinicalImpressionAssessmentValueCode,
  ClinicalImpressionCode,
  ClinicalImpressionLocationCode,
  ClinicalImpressionPlanValueCode
} from "@quromedical/fhir-common";
import { z as z19 } from "zod";

// src/admission/schema.ts
import {
  EncounterClass,
  EncounterService,
  DiagnosisTypeCode,
  EncounterDischargeReasonCode,
  EncounterInvalidateReasonCode,
  EncounterPausedReasonCode
} from "@quromedical/fhir-common";
import { z as z17 } from "zod";

// src/allergy/schema.ts
import { z as z13 } from "zod";
var AllergyTypeSchema = z13.enum(["medication", "food", "environment", "biologic"]);
var AllergyStatusSchema = z13.enum(["active", "inactive", "resolved"]);
var AllergySeveritySchema = z13.enum(["mild", "severe", "unknown"]);
var AllergyTypeArraySchema = AllergyTypeSchema;
var AllergyCodeSchema = z13.object({
  name: z13.string(),
  code: z13.string(),
  severity: AllergySeveritySchema
});
var AllergyAdmissionCreateRequestSchema = AllergyCodeSchema.array();
var MedpraxAllergySchema = z13.object({
  name: z13.string(),
  code: z13.string(),
  objectID: z13.string()
});
var AllergySearchSchema = PaginationSchema.extend({
  status: AllergyStatusSchema.optional(),
  patientId: z13.string().uuid()
});
var AllergySchema = z13.object({
  name: z13.string(),
  code: z13.string(),
  admission: DisplayReferenceSchema.optional(),
  patient: DisplayReferenceSchema,
  recorder: DisplayReferenceSchema,
  status: AllergyStatusSchema,
  type: AllergyTypeArraySchema,
  severity: AllergySeveritySchema.optional()
});
var AllergyWithIdSchema = AllergySchema.extend({
  id: UUIDSchema
});
var AllergyWithIdArraySchema = z13.object({
  items: AllergyWithIdSchema.array()
});
var AllergyCreateSchema = z13.object({
  name: z13.string(),
  code: z13.string(),
  patient: UUIDSchema,
  admission: UUIDSchema.optional(),
  recorder: UUIDSchema.optional(),
  severity: AllergySeveritySchema
});
var AllergyCreateRequestSchema = z13.object({
  patient: UUIDSchema,
  admission: UUIDSchema.optional(),
  items: AllergyCodeSchema.array()
});
var UpdateAllergySchema = z13.object({
  id: UUIDSchema,
  status: AllergyStatusSchema,
  severity: AllergySeveritySchema
});
var AllergyGetAllResponseSchema = createPaginatedResponse(AllergyWithIdSchema);

// src/consumable/schema.ts
import { z as z14 } from "zod";
var Status = [
  "planned",
  "billable",
  "not-billable",
  "aborted",
  "billed",
  "entered-in-error",
  "unknown"
];
var ConsumableSchema = z14.object({
  medpraxCode: z14.string(),
  nappiCode: z14.string(),
  name: z14.string(),
  quantity: QuantitySchema,
  patient: DisplayReferenceSchema,
  admission: DisplayReferenceSchema,
  dateOfUse: z14.string(),
  performer: DisplayReferenceSchema,
  status: z14.enum(Status)
});
var ConsumableCreateInputRequestSchema = z14.object({
  medpraxCode: z14.string(),
  dateOfUse: z14.string(),
  quantity: z14.number(),
  name: z14.string().optional(),
  nappiCode: z14.string().optional()
});
var ConsumableCreateRequestSchema = z14.object({
  consumables: ConsumableCreateInputRequestSchema.array(),
  patient: UUIDSchema,
  admission: UUIDSchema
});
var ConsumableGetAllRequestSchema = PaginationSchema.extend({
  patientId: UUIDSchema
});
var ConsumableUsageSchema = ConsumableSchema.pick({
  name: true,
  dateOfUse: true,
  performer: true,
  status: true
}).extend({
  id: UUIDSchema,
  quantity: z14.number()
});
var ConsumableGetAllResponseSchema = PaginatedBundleSchema.extend({
  consumableUsages: ConsumableUsageSchema.partial().array()
});
var ConsumableSearchParamsSchema = z14.object({
  query: z14.string().optional()
});
var ConsumableGetResponseSchema = ConsumableUsageSchema.partial();
var ConsumableUpdateRequestSchema = z14.object({
  id: UUIDSchema,
  status: z14.enum(Status)
});
var ConsumableSearchResponseSchema = z14.object({
  medpraxCode: z14.string(),
  name: z14.string().optional(),
  nappiCode: z14.string().optional()
});
var quroConsumableCodes = [
  "C80Q",
  "S18O",
  "T2JW",
  "R2A3",
  "B9DE",
  "G2IJ",
  "11F2",
  "5URI",
  "E733",
  "MVG9",
  "Q8FK",
  "M83M",
  "I2JI",
  "M9BM",
  "4V52",
  "R2B0",
  "4COM",
  "B5AY",
  "2ADR",
  "2ATR",
  "VB1C",
  "I6A8",
  "I5LV",
  "7UG2",
  "235X",
  "I7S1",
  "48X3",
  "1TG6",
  "8DEX",
  "AK20",
  "O861",
  "SOD6",
  "SOD9",
  "SOE4",
  "800E",
  "A01I",
  "I05Q",
  "GL12",
  "G0PC",
  "9TYU",
  "F3MP",
  "1LL7",
  "1JLO",
  "G667",
  "C2C5",
  "C1C5",
  "1R97",
  "1R98",
  "378I",
  "G3UA",
  "QLT4",
  "466S",
  "G4DM",
  "R9DP",
  "Q8FK",
  "5CH4",
  "G527",
  "2SO0",
  "G528",
  "SA17",
  "5NOR",
  "3SOD",
  "3NO3",
  "1R07",
  "940R",
  "ROI1",
  "0DHJ",
  "G2PX",
  "Q4MZ",
  "YP91",
  "11H4",
  "M83M",
  "JHJ1",
  "W9DZ",
  "H240",
  "3SAB",
  "C1KQ",
  "3W50",
  "A1U7",
  "T17B",
  "0DIR",
  "G2PR",
  "B1YG",
  "12P0",
  "01Z5",
  "HUI7",
  "2TEG",
  "4TEG",
  "1TEG",
  "4COM",
  "B35L",
  "5URI",
  "PPE2"
];

// src/medication/schema.ts
import {
  MedicationRequestDosageInstructionAdministrationCode,
  MedicationType
} from "@quromedical/fhir-common";
import { z as z15 } from "zod";
var MedicationMeasureSchema = z15.object({
  value: z15.string(),
  unit: z15.string()
});
var MedicationScheduleSchema = z15.string().refine(
  (val) => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 1 && num <= 6;
  },
  {
    message: "String must be a valid number between 1 and 6"
  }
);
var MedicationRouteCode = /* @__PURE__ */ ((MedicationRouteCode2) => {
  MedicationRouteCode2["TAB"] = "TAB";
  MedicationRouteCode2["SYR"] = "SYR";
  MedicationRouteCode2["CAP"] = "CAP";
  MedicationRouteCode2["LOZ"] = "LOZ";
  MedicationRouteCode2["MOW"] = "MOW";
  MedicationRouteCode2["SPR"] = "SPR";
  MedicationRouteCode2["NAS"] = "NAS";
  MedicationRouteCode2["NOD"] = "NOD";
  MedicationRouteCode2["EAR"] = "EAR";
  MedicationRouteCode2["OIN"] = "OIN";
  MedicationRouteCode2["DRP"] = "DRP";
  MedicationRouteCode2["SOP"] = "SOP";
  MedicationRouteCode2["EMO"] = "EMO";
  MedicationRouteCode2["DSP"] = "DSP";
  MedicationRouteCode2["EFT"] = "EFT";
  MedicationRouteCode2["CRE"] = "CRE";
  MedicationRouteCode2["OPD"] = "OPD";
  MedicationRouteCode2["CHU"] = "CHU";
  MedicationRouteCode2["SHA"] = "SHA";
  MedicationRouteCode2["VAT"] = "VAT";
  MedicationRouteCode2["PAD"] = "PAD";
  MedicationRouteCode2["LOT"] = "LOT";
  MedicationRouteCode2["POW"] = "POW";
  MedicationRouteCode2["SRT"] = "SRT";
  MedicationRouteCode2["VCR"] = "VCR";
  MedicationRouteCode2["GRA"] = "GRA";
  MedicationRouteCode2["SLN"] = "SLN";
  MedicationRouteCode2["INH"] = "INH";
  MedicationRouteCode2["ENE"] = "ENE";
  MedicationRouteCode2["TBL"] = "TBL";
  MedicationRouteCode2["SUP"] = "SUP";
  MedicationRouteCode2["SOL"] = "SOL";
  MedicationRouteCode2["SAC"] = "SAC";
  MedicationRouteCode2["EYO"] = "EYO";
  MedicationRouteCode2["STR"] = "STR";
  MedicationRouteCode2["UNI"] = "UNI";
  MedicationRouteCode2["CPS"] = "CPS";
  MedicationRouteCode2["INF"] = "INF";
  MedicationRouteCode2["POR"] = "POR";
  MedicationRouteCode2["VAG"] = "VAG";
  MedicationRouteCode2["UNG"] = "UNG";
  MedicationRouteCode2["PAI"] = "PAI";
  MedicationRouteCode2["EED"] = "EED";
  MedicationRouteCode2["EEO"] = "EEO";
  MedicationRouteCode2["SCR"] = "SCR";
  MedicationRouteCode2["NAG"] = "NAG";
  MedicationRouteCode2["SRC"] = "SRC";
  MedicationRouteCode2["PAS"] = "PAS";
  MedicationRouteCode2["SUS"] = "SUS";
  MedicationRouteCode2["WAF"] = "WAF";
  MedicationRouteCode2["SWB"] = "SWB";
  MedicationRouteCode2["PES"] = "PES";
  MedicationRouteCode2["GEL"] = "GEL";
  MedicationRouteCode2["DOU"] = "DOU";
  MedicationRouteCode2["UDV"] = "UDV";
  MedicationRouteCode2["IMP"] = "IMP";
  MedicationRouteCode2["ATH"] = "ATH";
  MedicationRouteCode2["MLS"] = "MLS";
  MedicationRouteCode2["VAC"] = "VAC";
  MedicationRouteCode2["KIT"] = "KIT";
  MedicationRouteCode2["AMP"] = "AMP";
  MedicationRouteCode2["PAT"] = "PAT";
  MedicationRouteCode2["BDL"] = "BDL";
  MedicationRouteCode2["TUL"] = "TUL";
  MedicationRouteCode2["PLA"] = "PLA";
  MedicationRouteCode2["BAN"] = "BAN";
  MedicationRouteCode2["GAU"] = "GAU";
  MedicationRouteCode2["IUD"] = "IUD";
  MedicationRouteCode2["NEB"] = "NEB";
  MedicationRouteCode2["VAF"] = "VAF";
  MedicationRouteCode2["VRI"] = "VRI";
  MedicationRouteCode2["DRO"] = "DRO";
  MedicationRouteCode2["VIA"] = "VIA";
  MedicationRouteCode2["MET"] = "MET";
  MedicationRouteCode2["INJ"] = "INJ";
  MedicationRouteCode2["TES"] = "TES";
  MedicationRouteCode2["CLS"] = "CLS";
  MedicationRouteCode2["LIQ"] = "LIQ";
  MedicationRouteCode2["SNG"] = "SNG";
  MedicationRouteCode2["COT"] = "COT";
  MedicationRouteCode2["BRA"] = "BRA";
  MedicationRouteCode2["AQS"] = "AQS";
  MedicationRouteCode2["EMU"] = "EMU";
  MedicationRouteCode2["FOA"] = "FOA";
  MedicationRouteCode2["ECT"] = "ECT";
  MedicationRouteCode2["DEV"] = "DEV";
  MedicationRouteCode2["NDL"] = "NDL";
  MedicationRouteCode2["ACC"] = "ACC";
  MedicationRouteCode2["PDR"] = "PDR";
  MedicationRouteCode2["EEN"] = "EEN";
  MedicationRouteCode2["CRY"] = "CRY";
  MedicationRouteCode2["SUT"] = "SUT";
  MedicationRouteCode2["SUN"] = "SUN";
  MedicationRouteCode2["TUH"] = "TUH";
  MedicationRouteCode2["FCT"] = "FCT";
  MedicationRouteCode2["SWS"] = "SWS";
  MedicationRouteCode2["CHG"] = "CHG";
  MedicationRouteCode2["OLT"] = "OLT";
  MedicationRouteCode2["PVC"] = "PVC";
  MedicationRouteCode2["NSS"] = "NSS";
  MedicationRouteCode2["ORS"] = "ORS";
  MedicationRouteCode2["BAS"] = "BAS";
  MedicationRouteCode2["LFI"] = "LFI";
  MedicationRouteCode2["SPO"] = "SPO";
  MedicationRouteCode2["LZS"] = "LZS";
  MedicationRouteCode2["ODT"] = "ODT";
  MedicationRouteCode2["CHC"] = "CHC";
  MedicationRouteCode2["OSP"] = "OSP";
  MedicationRouteCode2["SLT"] = "SLT";
  MedicationRouteCode2["PAP"] = "PAP";
  MedicationRouteCode2["GEO"] = "GEO";
  MedicationRouteCode2["PAO"] = "PAO";
  MedicationRouteCode2["UDL"] = "UDL";
  MedicationRouteCode2["SLW"] = "SLW";
  MedicationRouteCode2["OOL"] = "OOL";
  MedicationRouteCode2["PWO"] = "PWO";
  MedicationRouteCode2["SPC"] = "SPC";
  MedicationRouteCode2["LIT"] = "LIT";
  MedicationRouteCode2["SCT"] = "SCT";
  MedicationRouteCode2["CRO"] = "CRO";
  MedicationRouteCode2["NIC"] = "NIC";
  MedicationRouteCode2["PSD"] = "PSD";
  MedicationRouteCode2["SCL"] = "SCL";
  MedicationRouteCode2["TPN"] = "TPN";
  MedicationRouteCode2["MC2"] = "MC2";
  MedicationRouteCode2["OPG"] = "OPG";
  MedicationRouteCode2["DRE"] = "DRE";
  MedicationRouteCode2["MC3"] = "MC3";
  MedicationRouteCode2["OPI"] = "OPI";
  MedicationRouteCode2["GAS"] = "GAS";
  MedicationRouteCode2["MC1"] = "MC1";
  MedicationRouteCode2["CWG"] = "CWG";
  MedicationRouteCode2["CPD"] = "CPD";
  MedicationRouteCode2["LIG"] = "LIG";
  MedicationRouteCode2["PTD"] = "PTD";
  MedicationRouteCode2["CAT"] = "CAT";
  MedicationRouteCode2["CES"] = "CES";
  MedicationRouteCode2["DES"] = "DES";
  MedicationRouteCode2["POI"] = "POI";
  MedicationRouteCode2["MLT"] = "MLT";
  MedicationRouteCode2["DEB"] = "DEB";
  MedicationRouteCode2["BIS"] = "BIS";
  MedicationRouteCode2["CPC"] = "CPC";
  MedicationRouteCode2["GRE"] = "GRE";
  MedicationRouteCode2["GUS"] = "GUS";
  MedicationRouteCode2["JES"] = "JES";
  MedicationRouteCode2["OIR"] = "OIR";
  MedicationRouteCode2["SLL"] = "SLL";
  MedicationRouteCode2["STI"] = "STI";
  MedicationRouteCode2["STO"] = "STO";
  MedicationRouteCode2["OGS"] = "OGS";
  MedicationRouteCode2["JUT"] = "JUT";
  MedicationRouteCode2["REN"] = "REN";
  MedicationRouteCode2["HEA"] = "HEA";
  MedicationRouteCode2["ODF"] = "ODF";
  MedicationRouteCode2["ZZZ"] = "ZZZ";
  MedicationRouteCode2["PLS"] = "PLS";
  MedicationRouteCode2["CSM"] = "CSM";
  MedicationRouteCode2["NIH"] = "NIH";
  MedicationRouteCode2["FLS"] = "FLS";
  MedicationRouteCode2["PFS"] = "PFS";
  return MedicationRouteCode2;
})(MedicationRouteCode || {});
var MedicationRouteCodeDescription = {
  TAB: "TABLET",
  SYR: "SYRUP",
  CAP: "CAPSULE",
  LOZ: "LOZENGE",
  MOW: "MOUTH WASH",
  SPR: "SPRAY",
  NAS: "NASAL SPRAY",
  NOD: "NOSE DROPS",
  EAR: "EAR DROPS",
  OIN: "OINTMENT",
  DRP: "DROPS",
  SOP: "SOAP",
  EMO: "EMOLLIENT",
  DSP: "DISPERSABLE TABLET",
  EFT: "EFFERVESCENT TABLET",
  CRE: "CREAM",
  OPD: "OPHTHALMIC DROPS",
  CHU: "CHEW TABLET",
  SHA: "SHAMPOO",
  VAT: "VAGINAL TABLET",
  PAD: "ADHESIVE PAD",
  LOT: "LOTION",
  POW: "POWDER",
  SRT: "SLOW RELEASE TABLET",
  VCR: "VAGINAL CREAM",
  GRA: "GRANULES",
  SLN: "SOLUTION UNBREAKABLE",
  INH: "INHALER",
  ENE: "ENEMA",
  TBL: "REAGENT TABLET",
  SUP: "SUPPOSITORY",
  SOL: "SOLUTION BREAKABLE",
  SAC: "SACHET",
  EYO: "EYE OINTMENT",
  STR: "STRIPS",
  UNI: "INHALER DEVICE",
  CPS: "SPINCAP",
  INF: "INFUSION",
  POR: "POWDER EXTERNAL USE",
  VAG: "VAGINAL PREPARATION",
  UNG: "UNG",
  PAI: "PAINT",
  EED: "EYE AND EAR DROPS",
  EEO: "EYE/EAR OINTMENT",
  SCR: "SCRUB",
  NAG: "NASAL GEL",
  SRC: "SLOW RELEASE CAP",
  PAS: "PASTE",
  SUS: "SUSPENSION",
  WAF: "WAFER",
  SWB: "SWAB",
  PES: "PESSARY",
  GEL: "GEL",
  DOU: "DOUCHE",
  UDV: "UNI DOSE VIAL",
  IMP: "IMPLANT",
  ATH: "AUTOHALER",
  MLS: "LOCAL APPLICATION",
  VAC: "VACCINE",
  KIT: "KIT",
  AMP: "AMPULE",
  PAT: "PATCH",
  BDL: "DEBRIDEMENT APPL",
  TUL: "TULLE",
  PLA: "PLASTER",
  BAN: "BANDAGE",
  GAU: "GAUZE",
  IUD: "INTRA UTERINE DEVICE",
  NEB: "NEBULISING SOLUTION",
  VAF: "VAGINAL FOAM",
  VRI: "VAGINAL RING",
  DRO: "REAGENT DROP",
  VIA: "VIAL",
  MET: "METER",
  INJ: "INJECTION",
  TES: "TEST",
  CLS: "CLEANSER",
  LIQ: "LIQUID",
  SNG: "SYRINGE",
  COT: "COTTON WOOL",
  BRA: "KNEE BRACE",
  AQS: "AQUA SPRAY",
  EMU: "EMULSION",
  FOA: "FOAM",
  ECT: "ENTERIC COATED TAB",
  DEV: "INJECTION DEVICE",
  NDL: "NEEDLE",
  ACC: "ACCUHALER",
  PDR: "POWDER (GALENICAL)",
  EEN: "EAR/EYE/NOSE DROPS",
  CRY: "CRYSTALS",
  SUT: "SUTURE",
  SUN: "SINGLE UNIT NAS SPR",
  TUH: "TURBUHALER",
  FCT: "FILM COATED TABLET",
  SWS: "WIPES",
  CHG: "CHEW GUM",
  OLT: "OIL",
  PVC: "VAGINAL CAP",
  NSS: "NASULE",
  ORS: "ORAL SOLUTION",
  BAS: "BANDAGE & SUBSTANCE",
  LFI: "LIQ FOR INHALATION",
  SPO: "SPRAYS ORAL TOPICAL",
  LZS: "LOZENGE SYSTEMIC",
  ODT: "ORAL DRUG TEAS",
  CHC: "CHEW CAP",
  OSP: "SPRAYS ORAL SYSTEMIC",
  SLT: "SUBLINGUAL TABLET",
  PAP: "PAPER",
  GEO: "GELS ORAL TOPICAL",
  PAO: "PASTES ORAL TOPICAL",
  UDL: "UNIT DOSE ORAL LIQ",
  SLW: "SOAP LIQUID WASH",
  OOL: "OIL ORAL",
  PWO: "POWDER ORAL TOPICAL",
  SPC: "SPRINKLE CAPS",
  LIT: "LIQUID TOPICAL",
  SCT: "PROFESIONAL SERVICES",
  CRO: "CREAMS ORAL TOPICAL",
  NIC: "NASAL INH CAP TOPICA",
  PSD: "PAD WITH SUBSTANCE",
  SCL: "SCALP LOTION",
  TPN: "PARENTERAL NUTRITION",
  MC2: "MULTI COMP VARIAT 2",
  OPG: "OPHTHALMIC GEL",
  DRE: "DRESSING",
  MC3: "MULTI COMP VARIAT 3",
  OPI: "INTRA OCULAR INJ",
  GAS: "GAS FOR INH",
  MC1: "MULTI COMP VARIAT 1",
  CWG: "COLLAGEN WOUND GEL",
  CPD: "COLLAGEN POWDER",
  LIG: "LIQUID ORAL GEL",
  PTD: "PATCH TRANSDERMAL",
  CAT: "CATHETER",
  CES: "CEMENT W SUBSTANCE",
  DES: "DRUG ELUTING STENT",
  POI: "POWDER FOR INJ",
  MLT: "MELT TABS",
  DEB: "DRUG ELUTING BALLOON",
  BIS: "BISCUITS",
  CPC: "COMBINATION PACK CAP",
  GRE: "GRANULES EXTERNAL",
  GUS: "GAUZE WITH SUBSTANCE",
  JES: "JEL SYSTEMATIC",
  OIR: "OINTMENT RECTAL SYST",
  SLL: "SUBLINGUAL LIQUIDS",
  STI: "TOPICAL STICKS",
  STO: "STRIPS ORAL",
  OGS: "OPHTHALMIC GAS",
  JUT: "JEL TOPICAL U DOSE",
  REN: "RENTAL",
  HEA: "HEARING AID",
  ODF: "ORODISPERSIBLE FILM",
  ZZZ: "UNKNOWN",
  PLS: "PLASTER W/SUBSTANCE",
  CSM: "CONSUMABLE",
  NIH: "NASAL INHALER",
  FLS: "FLOS (DRIED PLANT)",
  PFS: "PRE-FILLED SYRINGE"
};
var MedicationRouteCodeSchema = z15.nativeEnum(MedicationRouteCode);
var MedicationTimeUnitSchema = z15.enum(["s", "min", "h", "d", "wk", "mo", "a"]).optional();
var MedicationUsageStatuses = [
  "active",
  "completed",
  "entered-in-error",
  "intended",
  "stopped",
  "on-hold",
  "unknown",
  "not-taken"
];
var MedicationTypeSchema = z15.nativeEnum(MedicationType);
var MedicationUsageStatusSchema = z15.enum(MedicationUsageStatuses);
var MedicationRepeatSchema = z15.object({
  frequency: z15.number(),
  period: z15.number(),
  periodUnit: MedicationTimeUnitSchema,
  type: MedicationTypeSchema.optional()
});
var MedicationDurationSchema = z15.object({
  value: z15.number(),
  unit: z15.string()
});
var MedicationDosageSchema = z15.object({
  route: MedicationRouteCodeSchema,
  timingRepeat: MedicationRepeatSchema,
  additionalInstruction: z15.nativeEnum(MedicationRequestDosageInstructionAdministrationCode).optional()
});
var MedicationSchema = z15.object({
  code: z15.string(),
  name: z15.string(),
  nappiCode: z15.string(),
  strength: MedicationMeasureSchema.optional(),
  volume: MedicationMeasureSchema.optional(),
  individualPackSize: MedicationMeasureSchema.optional(),
  dispensingVolumePacksize: z15.string().optional(),
  schedule: z15.string().optional(),
  route: z15.nativeEnum(MedicationRouteCode),
  type: MedicationTypeSchema.optional()
});
var BaseMedicationReferenceSchema = z15.object({
  /** `fhir4.MedicationStatement.subject` or `fhir4.MedicationRequest.subject` */
  patient: DisplayReferenceSchema,
  /** `fhir.MedicationStatement.note.text` or `fhir4.MedicationRequest.note.text` */
  admission: DisplayReferenceSchema,
  /** Practitioner that prescribed the medication.
   * `fhir4.MedicationStatement.informationSource`  or `fhir4.MedicationRequest.recorder` */
  recorder: DisplayReferenceSchema,
  /** `fhir.MedicationStatement.medicationCodeableConcept` */
  medication: MedicationSchema,
  /** `fhir4.MedicationStatement.dosage` */
  dosage: MedicationDosageSchema,
  /** `fhir4.MedicationStatement.dateAsserted` or `fhir4.MedicationRequest.authoredOn`  */
  date: z15.string(),
  noteText: z15.string().optional(),
  type: MedicationTypeSchema.optional()
});
var PartialBaseMedicationReferenceSchema = z15.object({
  patient: DisplayReferenceSchema.optional(),
  admission: DisplayReferenceSchema.optional(),
  noteText: z15.string().optional(),
  recorder: DisplayReferenceSchema.optional(),
  medication: MedicationSchema.partial(),
  dosage: MedicationDosageSchema.partial(),
  date: z15.string().optional(),
  groupIdentifier: z15.string().optional(),
  type: MedicationTypeSchema.optional()
});
var MedicationUsageSchema = BaseMedicationReferenceSchema.extend({
  /** `fhir4.MedicationStatement.status` */
  status: MedicationUsageStatusSchema,
  /** `fhir4.MedicationStatement.effectivePeriod` */
  period: PeriodSchema,
  /** `fhir4.MedicationStatement.derivedFrom` */
  derivedFrom: DisplayReferenceSchema.optional(),
  /** `fhir4.MedicationStatement.identifier`  */
  groupIdentifier: z15.string().optional()
});
var PartialMedicationUsageSchema = PartialBaseMedicationReferenceSchema.extend({
  status: MedicationUsageStatusSchema.optional(),
  period: PeriodSchema.optional(),
  derivedFrom: DisplayReferenceSchema.optional()
});
var PartialMedicationRequestWithIdSchema = PartialMedicationUsageSchema.extend({
  id: UUIDSearchSchema
});
var MedicationUseSchema = z15.enum(["create-patient-prescription", "create-usage"]);
var MedicationGetAllParamsSchema = z15.object({
  use: MedicationUseSchema,
  query: z15.string().optional()
});
var CreateMedicationUsageSchema = MedicationRepeatSchema.extend({
  medicationCode: z15.string(),
  noteText: z15.string().optional()
});
var MedicationCreateRequestSchema = z15.object({
  medicationUsage: z15.array(CreateMedicationUsageSchema).min(1),
  patientId: UUIDSchema,
  admissionId: UUIDSchema
});
var MedicationGetResponseSchema = PartialMedicationUsageSchema.extend({
  id: UUIDSchema
});
var MedicationGetAllResponseSchema = PaginatedBundleSchema.extend({
  medicationUsages: z15.array(MedicationGetResponseSchema)
});
var MedicationCreateResponseSchema = z15.array(MedicationGetResponseSchema);
var MedicationQueryParamsSchema = PaginationSchema.extend({
  subject: UUIDSchema,
  status: MedicationUsageStatusSchema.optional()
});

// src/observation/schema.ts
import { ObservationCode } from "@quromedical/fhir-common";
import { z as z16 } from "zod";
var ObservationStatus = [
  "registered",
  "preliminary",
  "final",
  "amended",
  "corrected",
  "cancelled",
  "entered-in-error",
  "unknown"
];
var ObservationStatusSchema = z16.enum(ObservationStatus);
var ObservationVitalMeasurementSchema = BaseTimeSeriesRecordSchema.extend({
  sensorId: z16.string().optional().describe("The ID of the sensor that created the measurement")
});
var ObservationSchema = z16.object({
  code: z16.nativeEnum(ObservationCode),
  measures: ObservationVitalMeasurementSchema.array(),
  status: ObservationStatusSchema,
  device: DisplayReferenceSchema,
  performers: DisplayReferenceSchema.array(),
  patient: DisplayReferenceSchema,
  dateTime: z16.string(),
  admission: DisplayReferenceSchema.optional()
});
var ObservationGetResponseSchema = ObservationSchema.partial().extend({
  id: UUIDSchema
});
var MeasuresSchema = BaseTimeSeriesRecordSchema.extend({
  sensorId: z16.string().optional().describe("The ID of the sensor that created the measurement")
});
var CreateObservationSchema = z16.object({
  code: z16.nativeEnum(ObservationCode).describe("The type of observation to be created"),
  subject: z16.string().describe("The subject of the observation"),
  measures: z16.array(MeasuresSchema).describe("Measures to be created").min(1)
});
var ObservationReferenceRangeSchema = z16.object({
  low: z16.object({
    value: z16.number(),
    unit: z16.string()
  }),
  high: z16.object({
    value: z16.number(),
    unit: z16.string()
  })
});
var ObservationComponentSchema = z16.object({
  code: DisplayCodeableConceptSchema,
  referenceRange: ObservationReferenceRangeSchema.optional(),
  interpretation: DisplayCodeableConceptSchema.optional(),
  value: ValueTypeSchema
});
var ObservationPregnancySchema = z16.object({
  lactation: z16.boolean().describe("Is patient lactating"),
  isPregnant: z16.boolean().describe("Is patient pregnant"),
  edd: z16.string().optional().describe("Estimated date of delivery")
});
var ObservationPathologyTestResult = [ObservationCode.pathologyTestResult];
var ObservationPathologyTestResultSchema = z16.enum(ObservationPathologyTestResult);
var ObservationPathologySchema = z16.object({
  code: ObservationPathologyTestResultSchema,
  status: ObservationStatusSchema,
  patient: DisplayReferenceSchema,
  admission: DisplayReferenceSchema,
  basedOn: DisplayReferenceSchema,
  effectiveDateTime: z16.string(),
  components: ObservationComponentSchema.array()
});
var ObservationResultSearchSchema = PaginationSchema.extend({
  code: z16.nativeEnum(ObservationCode).optional(),
  status: ObservationStatusSchema.optional(),
  basedOn: z16.string().optional(),
  subject: UUIDSchema.optional()
});
var ObservationReportComponentSchema = z16.object({
  code: z16.string().optional(),
  interpretation: z16.string().optional(),
  referenceRange: z16.string().optional(),
  valueQuantity: z16.object({
    value: z16.string().optional(),
    unit: z16.string().optional()
  }),
  valueString: z16.string().optional()
});
var ObservationWithoutAdmissionSchema = ObservationPathologySchema.omit({
  admission: true
});
var ObservationResultSchema = ObservationWithoutAdmissionSchema.extend({
  components: ObservationReportComponentSchema.array(),
  id: UUIDSchema
});
var ObservationResultsResponseSchema = PaginatedBundleSchema.extend({
  results: ObservationResultSchema.array().optional()
});

// src/admission/schema.ts
var encounterStatus = [
  "planned",
  "arrived",
  "triaged",
  "in-progress",
  "onleave",
  "finished",
  "cancelled",
  "entered-in-error",
  "unknown"
];
var EncounterDischargeStatusReason = z17.nativeEnum(EncounterDischargeReasonCode);
var encounterPausedStatusReason = z17.nativeEnum(EncounterPausedReasonCode);
var EncounterInvalidateStatusReason = z17.nativeEnum(EncounterInvalidateReasonCode);
var encounterStatusReasonSchema = z17.union([
  EncounterDischargeStatusReason,
  encounterPausedStatusReason,
  EncounterInvalidateStatusReason
]);
var EncounterPausedStatusReason = z17.nativeEnum(EncounterPausedReasonCode);
var EncounterStatusReasonSchema = z17.union([
  EncounterDischargeStatusReason,
  EncounterInvalidateStatusReason,
  EncounterPausedStatusReason
]);
var AdmissionDiagnosisSchema = z17.object({
  condition: DisplayReferenceSchema,
  type: z17.nativeEnum(DiagnosisTypeCode).optional()
});
var CreateAdmissionDiagnosisSchema = z17.object({
  condition: z17.string().optional(),
  type: z17.nativeEnum(DiagnosisTypeCode).optional()
});
var AdmissionSubjectSchema = PatientSummarySchema.extend({
  id: UUIDSchema
});
var CoverageIdentifiersSchema2 = z17.object({
  schemeCode: z17.string(),
  dependantCode: z17.string(),
  subscriberId: z17.string()
});
var BaseAdmissionMedicalAidAuthorizationSchema = z17.object({
  date: z17.string().optional(),
  code: z17.string().optional()
});
var AdmissionMedicalAidAuthorizationSchema = BaseAdmissionMedicalAidAuthorizationSchema.extend({
  coverage: CoverageIdentifiersSchema2
});
var AdmissionStatusHistorySchema = z17.object({
  period: PartialPeriodSchema,
  status: z17.enum(encounterStatus)
});
var BaseAdmissionSchema = z17.object({
  status: z17.enum(encounterStatus),
  statusReason: EncounterStatusReasonSchema.optional(),
  class: z17.nativeEnum(EncounterClass),
  service: z17.nativeEnum(EncounterService),
  acute: RequiredCodingSchema.array().optional(),
  chronic: RequiredCodingSchema.array().optional(),
  reasonOfAdmission: RequiredCodingSchema.array().optional(),
  period: PeriodSchema,
  /**
   * @deprecated use subject field instead
   */
  patient: DisplayReferenceSchema.describe("This field has been deprecated"),
  subject: AdmissionSubjectSchema,
  oxygen: QuantitySchema.optional(),
  statusHistory: AdmissionStatusHistorySchema.array().optional(),
  diagnosis: AdmissionDiagnosisSchema.array().optional(),
  pregnancy: ObservationPregnancySchema.optional(),
  location: DisplayReferenceSchema.optional()
});
var MedicalAidAdmissionSchema = BaseAdmissionSchema.extend({
  primaryIcd10Code: RequiredCodingSchema.optional(),
  medicalAidAuthorization: AdmissionMedicalAidAuthorizationSchema
});
var AdmissionSchema = BaseAdmissionSchema.extend({
  icd10Codes: RequiredCodingSchema.array().optional(),
  medicalAidAuthorization: AdmissionMedicalAidAuthorizationSchema.optional(),
  primaryIcd10Code: RequiredCodingSchema
});
var AdmissionWithUsagesSchema = AdmissionSchema.extend({
  medicationUsages: PartialMedicationRequestWithIdSchema.array(),
  consumableUsages: ConsumableUsageSchema.partial().array()
});
var AdmissionVitalSummarySchema = z17.object({
  vitals: LiveDataMeasuresSchema,
  alerts: ECGSinusRhythmAlertSchema
});
var AdmissionMediaSchema = z17.object({
  media: DisplayReferenceSchema.array().optional()
});
var AdmissionGetResponseSchema = AdmissionSchema.partial().extend({
  id: UUIDSchema,
  vitals: LiveDataMeasuresSchema.optional(),
  alerts: ECGSinusRhythmAlertSchema.array().optional()
});
var AdmissionGetResponseWithMediaSchema = AdmissionGetResponseSchema.and(AdmissionMediaSchema);
var AdmissionGetResponseWithVitalsSchema = AdmissionGetResponseSchema.extend({
  vitals: LiveDataMeasuresSchema.optional(),
  alerts: ECGSinusRhythmAlertSchema.array().optional(),
  media: DisplayReferenceSchema.array().optional()
});
var AdmissionGetAllResponseSchema = PaginatedBundleSchema.extend({
  admissions: AdmissionGetResponseSchema.array()
});
var AdmissionStatusSchema = z17.enum(encounterStatus).describe("The status of the admission");
var AdmissionClassSchema = z17.nativeEnum(EncounterClass).describe("The class of the service");
var AdmissionServiceTypeSchema = z17.nativeEnum(EncounterService).describe("The service type for the patient");
var AdmissionSearchSchema = PaginationSchema.extend({
  status: AdmissionStatusSchema.optional(),
  class: AdmissionClassSchema.optional(),
  serviceType: AdmissionServiceTypeSchema.optional(),
  patient: BaseUUIDSearchSchema.optional().describe("The patient ids"),
  patientName: z17.string().optional().describe("The name of the patient"),
  sort: z17.string().optional().describe("The field to sort by"),
  includeAdminAssignments: z17.boolean().optional().describe("Should include admin assignments"),
  includeVitals: z17.boolean().optional().describe("Should include vitals"),
  location: BaseUUIDSearchSchema.optional().describe("The location of the admission")
});
var AdmissionWithoutIcd10PrimaryCode = AdmissionSchema.omit({
  primaryIcd10Code: true
});
var AdmissionWithPartialPrimaryCodeSchema = AdmissionWithoutIcd10PrimaryCode.extend({
  primaryIcd10Code: RequiredCodingSchema.optional()
});
var AdmissionGetOneByIdResponseSchema = AdmissionWithPartialPrimaryCodeSchema.partial().extend({
  id: UUIDSchema
});
var ICD10CodeSchema = z17.string();
var CreateAdmissionSchema = z17.object({
  class: AdmissionClassSchema,
  service: AdmissionServiceTypeSchema,
  periodStart: z17.string(),
  practitioners: GeneralPractitionerUpdateRequestSchema,
  primaryIcd10Code: ICD10CodeSchema.optional(),
  icd10Codes: ICD10CodeSchema.array().optional(),
  acute: ICD10CodeSchema.array().optional(),
  chronic: ICD10CodeSchema.array().optional(),
  reasonOfAdmission: ICD10CodeSchema.array().min(1),
  medicalAidAuthorization: BaseAdmissionMedicalAidAuthorizationSchema.optional(),
  oxygen: z17.number().optional(),
  attachments: MediaDataSchema.array().optional(),
  diagnosis: CreateAdmissionDiagnosisSchema.array().optional(),
  pregnancy: ObservationPregnancySchema.optional(),
  location: UUIDSchema,
  allergy: AllergyAdmissionCreateRequestSchema.optional()
});
var CreateAdmissionSchemaWithPatientSchema = CreateAdmissionSchema.extend({
  patientId: UUIDSchema
});
var AdmissionUpdateSchema = z17.object({
  id: UUIDSchema,
  primaryIcd10Code: ICD10CodeSchema.optional(),
  icd10Codes: ICD10CodeSchema.array().optional(),
  oxygen: z17.number().optional(),
  medicalAidAuthorization: BaseAdmissionMedicalAidAuthorizationSchema.optional(),
  periodStart: z17.string(),
  attachments: MediaDataSchema.array().optional(),
  service: AdmissionServiceTypeSchema,
  diagnosis: CreateAdmissionDiagnosisSchema.array().optional(),
  pregnancy: ObservationPregnancySchema.optional(),
  location: UUIDSchema.optional(),
  reasonOfAdmission: ICD10CodeSchema.array().optional()
});
var AdmissionLocationUpdateSchema = z17.object({
  location: z17.string()
});
var AdmissionDischargeRequestSchema = z17.object({
  id: UUIDSchema,
  periodEnd: z17.string().date(),
  statusReason: z17.nativeEnum(EncounterDischargeReasonCode)
});
var AdmissionPauseRequestSchema = z17.object({
  id: UUIDSchema,
  statusReason: z17.nativeEnum(EncounterPausedReasonCode)
});
var AdmissionInvalidateRequestSchema = z17.object({
  id: UUIDSchema,
  statusReason: z17.nativeEnum(EncounterInvalidateReasonCode)
});

// src/media/schema.ts
import { z as z18 } from "zod";
var CreateUploadURLSchema = z18.object({
  title: z18.string().min(1),
  contentType: ContentTypeSchema,
  patientId: UUIDSchema
});
var MediaSchema = MediaDataSchema.extend({
  patient: DisplayReferenceSchema,
  createdBy: DisplayReferenceSchema,
  createdDateTime: z18.string(),
  admission: DisplayReferenceSchema.optional(),
  partOf: z18.array(DisplayReferenceSchema).optional()
});
var MediaItemCreateSchema = z18.array(
  MediaSchema.extend({
    data: z18.string()
  })
);
var PatientMediaCreateSchema = z18.object({
  patient: UUIDSchema,
  media: z18.array(MediaDataSchema),
  admission: DisplayReferenceSchema.optional()
});
var MediaCreateSchema = z18.object({
  media: z18.array(MediaDataSchema)
});
var PatientMediaSearchSchema = PaginationSchema.extend({
  subject: UUIDSchema
});
var MediaGetResponseSchema = MediaSchema.partial().extend({
  id: UUIDSchema
});
var CreateUploadUrlResponseSchema = z18.object({
  url: z18.string(),
  key: z18.string(),
  /**
   * The data here should be included in the S3 request in order to allow the request to be
   * authenticated
   */
  formData: z18.record(z18.string(), z18.string())
});
var MediaGetAllResponseSchema = PaginatedBundleSchema.extend({
  media: MediaGetResponseSchema.array()
});

// src/notes/schema.ts
var noteRefinement = (obj, ctx) => {
  const { planCode, plan, assessmentCode, assessment } = obj;
  if (planCode !== ClinicalImpressionPlanValueCode.Continue && plan === void 0) {
    ctx.addIssue({
      code: "custom",
      message: "Plan comments are required for the selected plan.",
      path: ["plan"]
    });
  }
  if (assessmentCode !== ClinicalImpressionAssessmentValueCode.Stable && assessment === void 0) {
    ctx.addIssue({
      code: "custom",
      message: "Assessment comments are required.",
      path: ["assessment"]
    });
    return;
  }
};
var ClinicalImpressionAssessmentValueCodeSchema = z19.nativeEnum(
  ClinicalImpressionAssessmentValueCode
);
var ClinicalImpressionPlanValueCodeSchema = z19.nativeEnum(ClinicalImpressionPlanValueCode);
var CreateSOAPNoteSchema = z19.object({
  assessmentCode: ClinicalImpressionAssessmentValueCodeSchema,
  planCode: ClinicalImpressionPlanValueCodeSchema,
  subjective: z19.string().describe("Subjective note-text"),
  objective: z19.string().optional().describe("Objective note-text"),
  assessment: z19.string().optional(),
  plan: z19.string().optional()
}).superRefine(noteRefinement);
var NoteRefinementSchema = z19.object({
  assessmentCode: ClinicalImpressionAssessmentValueCodeSchema,
  planCode: ClinicalImpressionPlanValueCodeSchema,
  subjective: z19.string(),
  objective: z19.string().optional(),
  assessment: z19.string().optional(),
  plan: z19.string().optional()
});
var SOAPNoteSchema = NoteRefinementSchema.partial().extend({
  subjective: z19.string().array().optional()
});
var StructuredContentTypeSchema = z19.literal("nested-comment-nodes");
var StructuredNoteBaseSchema = z19.object({
  id: z19.string(),
  display: z19.string(),
  selected: z19.boolean().optional(),
  color: z19.string().optional().describe("This represents a color name and not necessarily a hex or some other value"),
  type: z19.enum(["comment", "group"]).optional()
});
var StructuredNoteItemSchema = StructuredNoteBaseSchema.extend({
  type: z19.literal("comment"),
  text: z19.string().optional()
});
var StructuredNoteGroupSchema = StructuredNoteBaseSchema.extend({
  type: z19.literal("group"),
  children: z19.lazy(() => z19.array(StructuredNoteNodeSchema))
});
var StructuredNoteNodeSchema = StructuredNoteGroupSchema.or(StructuredNoteItemSchema);
var StructuredContentSchema = z19.object({
  /**
   * In the event we choose to modify the data stored in the structuredContent then we can tweak
   * this accordingly, it should make parsing the data easier down the line
   */
  type: StructuredContentTypeSchema,
  data: z19.array(StructuredNoteNodeSchema)
});
var DisplayReferenceSchema2 = z19.object({
  reference: ReferenceSchema.optional(),
  display: z19.string().optional()
});
var clinicalImpressionCodeSchema = z19.nativeEnum(ClinicalImpressionCode).describe("Code for the type of a clinical impression");
var clinicalImpressionLocationCodeSchema = z19.nativeEnum(ClinicalImpressionLocationCode).describe("Where clinical impression was taken");
var CreateNoteSchema = z19.object({
  subject: UUIDSchema.describe("Patient ID"),
  code: clinicalImpressionCodeSchema,
  locationCode: clinicalImpressionLocationCodeSchema,
  note: CreateSOAPNoteSchema.describe("The SOAP note"),
  vitals: LatestResultMeasuresSchema,
  media: z19.array(MediaDataSchema).describe("Media associated with the SOAP note"),
  practitionersToNotify: z19.array(DisplayReferenceSchema2).describe("Practitioners to be notified of the SOAP note"),
  structuredContent: StructuredContentSchema.optional()
});
var NoteUpdateSchema = z19.object({
  id: UUIDSchema.describe("NoteID to be updated"),
  subjective: z19.string().optional().describe("Additional subjective note"),
  media: z19.array(MediaDataSchema).describe("Additional media"),
  pinned: z19.boolean().optional().describe("Whether the note is pinned")
});
var NoteAdmissionSchema = AdmissionSchema.partial().extend({
  id: UUIDSchema
});
var NoteSchema = z19.object({
  id: UUIDSchema,
  code: clinicalImpressionCodeSchema.optional(),
  locationCode: clinicalImpressionLocationCodeSchema.optional(),
  assessor: DisplayReferenceSchema2.optional(),
  media: MediaGetResponseSchema.array(),
  note: SOAPNoteSchema,
  vitals: LatestResultNotesMeasuresSchema,
  practitionersToNotify: DisplayReferenceSchema2.array(),
  admission: NoteAdmissionSchema.optional(),
  dateCreated: z19.string().optional(),
  pinned: z19.boolean(),
  structuredContent: StructuredContentSchema.optional()
});
var NoteBundleSchema = PaginatedBundleSchema.extend({
  pins: NoteSchema.array(),
  notes: NoteSchema.array()
});
var NoteSearchSchema = PaginationSchema.extend({
  search: z19.string().optional().describe("Search string"),
  dateFrom: z19.string().optional().describe("FHIR Datetime to search from"),
  dateTo: z19.string().optional().describe("FHIR Datetime to search until"),
  subject: BaseUUIDSearchSchema.optional().describe("The subject of the note")
});

// src/notes/mapping.ts
import {
  ClinicalImpressionInvestigationCode,
  ClinicalImpressionNoteCode,
  ExtensionUrl as ExtensionUrl5,
  createCodeableConcept as createCodeableConcept6,
  createExtensionValue as createExtensionValue4,
  filterExtensions as filterExtensions2,
  findExtension,
  findExtensionValue as findExtensionValue4,
  findResourceByType,
  getInvestigationReferencesByCode,
  isNotePinned
} from "@quromedical/fhir-common";
import { compact as compact7 } from "@quromedical/utils";

// src/admission/mapping.ts
import {
  createCodeableConcept as createCodeableConcept5,
  createCoding as createCoding2,
  createExtensionValue as createExtensionValue3,
  createFhirDate as createFhirDate2,
  EncounterClass as EncounterClass2,
  EncounterService as EncounterService2,
  ExtensionUrl as ExtensionUrl4,
  findExtensionValue as findExtensionValue3,
  getCodes,
  getResourcesFromBundle,
  IdentifierCode as IdentifierCode4,
  valsOrNone as valsOrNone7,
  getReferenceId as getReferenceId3,
  createAuditResource,
  addExtensionToResource,
  DiagnosisTypeCode as DiagnosisTypeCode2,
  createIdentifier as createIdentifier3,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts5
} from "@quromedical/fhir-common";
import { compact as compact5, first as first4, flatten as flatten3, last as last2, partial as partial2 } from "@quromedical/utils";

// src/observation/mapping.ts
import {
  createCodeableConcept as createCodeableConcept4,
  createFhirDateTime,
  createReference,
  getDisplayFromCodableConcepts,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts4,
  getQuantityWithValue,
  getReferenceId as getReferenceId2,
  isValidReference as isValidReference2,
  ObservationCode as ObservationCode2,
  ObservationMeasurementCode,
  ObservationMeasurementUnits,
  valsOrNone as valsOrNone6
} from "@quromedical/fhir-common";
import { first as first3, flatten as flatten2, uniq } from "@quromedical/utils";
import { compact as compact4 } from "@quromedical/utils";

// src/device-command/schema.ts
import { DeviceMetricTypeCode } from "@quromedical/fhir-common";
import { z as z20 } from "zod";
var Command = /* @__PURE__ */ ((Command2) => {
  Command2["PatchCalibrate"] = "patch-calibrate";
  return Command2;
})(Command || {});
var DeviceMetricCalibrationType = ["unspecified", "offset", "gain", "two-point"];
var DeviceMetricCategory = ["unspecified", "measurement", "setting", "calculation"];
var DeviceMetricCalibrationState = [
  "not-calibrated",
  "calibration-required",
  "calibrated",
  "unspecified"
];
var CommandSchema = z20.nativeEnum(Command);
var DeviceMetricCategorySchema = z20.enum(DeviceMetricCategory);
var DeviceMetricCalibrationStateSchema = z20.enum(DeviceMetricCalibrationState);
var DeviceMetricCalibrationTypeSchema = z20.enum(DeviceMetricCalibrationType);
var DeviceMetricTypeSchema = z20.nativeEnum(DeviceMetricTypeCode);
var DeviceCommandSchema = z20.object({
  command: CommandSchema,
  state: DeviceMetricCalibrationStateSchema,
  type: DeviceMetricCalibrationTypeSchema
});
var DeviceMetricSchema = z20.object({
  time: z20.string(),
  type: DeviceMetricTypeSchema,
  category: DeviceMetricCategorySchema,
  command: DeviceCommandSchema,
  measurement: ObservationSchema,
  device: DisplayReferenceSchema
});
var PartialDeviceMetricSchema = DeviceMetricSchema.extend({
  measurement: ObservationSchema.partial(),
  command: DeviceCommandSchema.partial()
}).partial();
var DeviceMetricGetResponseSchema = PartialDeviceMetricSchema.extend({
  id: UUIDSchema
});
var DeviceMetricGetAllResponseSchema = PaginatedBundleSchema.extend({
  deviceMetrics: z20.array(DeviceMetricGetResponseSchema)
});
var DeviceMetricGetAllParamsSchema = PaginationSchema.extend({
  id: UUIDSchema,
  category: DeviceMetricCategorySchema.optional(),
  type: DeviceMetricTypeSchema.optional()
});
var PatchPostureCalibrateValue = /* @__PURE__ */ ((PatchPostureCalibrateValue2) => {
  PatchPostureCalibrateValue2["Upright"] = "Upright";
  PatchPostureCalibrateValue2["LayingSupine"] = "LayingSupine";
  return PatchPostureCalibrateValue2;
})(PatchPostureCalibrateValue || {});
var PatchPostureCalibrateValueSchema = z20.nativeEnum(PatchPostureCalibrateValue);
var PatchCalibrateDataSchema = z20.object({
  patchId: z20.string(),
  posture: PatchPostureCalibrateValueSchema.optional(),
  temperature: z20.string().optional(),
  easyFall: z20.boolean().optional()
});

// src/device-command/mapping.ts
import {
  createCodeableConcept as createCodeableConcept3,
  DeviceMetricTypeCode as DeviceMetricTypeCode2,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts3,
  getReferenceId
} from "@quromedical/fhir-common";
var mapDeviceMetricModelToFhir = (model) => ({
  resourceType: "DeviceMetric",
  category: model.category,
  type: createCodeableConcept3(model.type),
  source: model.device,
  calibration: [
    {
      time: model.time,
      state: model.command.state,
      type: model.command.type
    }
  ]
});
var deviceMetricTypeToCommandMap = {
  [DeviceMetricTypeCode2.patchCalibration]: "patch-calibrate" /* PatchCalibrate */
};
var commandToDeviceMetricTypeMap = {
  ["patch-calibrate" /* PatchCalibrate */]: DeviceMetricTypeCode2.patchCalibration
};
var mapDeviceMetricFhirToModel = (fhir) => {
  const { id, category, type, source, calibration } = fhir;
  const command = calibration?.[0];
  const device = getValidReference(source);
  const typeCode = getFirstCodeFromCodableConcepts3([type]);
  const commandType = typeCode ? deviceMetricTypeToCommandMap[typeCode] : void 0;
  return {
    id,
    category,
    time: command?.time,
    type: typeCode,
    command: {
      command: commandType,
      state: command?.state,
      type: command?.type
    },
    device
  };
};
var attachMeasurementToDeviceMetric = (observations) => {
  return (deviceMetric) => {
    return {
      ...deviceMetric,
      // the deviceMetric is stored on the device field of the observation resource
      measurement: observations.find((o) => getReferenceId(o.device) === deviceMetric.id)
    };
  };
};

// src/observation/mapping.ts
var measurementNameToCodeMapping = {
  ["ACT" /* Activity */]: ObservationMeasurementCode.Activity,
  ["HTR" /* HeartRate */]: ObservationMeasurementCode.HeartRate,
  ["RSR" /* RespiratoryRate */]: ObservationMeasurementCode.RespiratoryRate,
  ["STP" /* SkinTemperature */]: ObservationMeasurementCode.SkinTemperature,
  ["CTP" /* CoreTemperature */]: ObservationMeasurementCode.CoreTemperature,
  ["MS-SpO2" /* OxygenSaturation */]: ObservationMeasurementCode.OxygenSaturation,
  ["STS" /* Steps */]: ObservationMeasurementCode.Steps,
  ["POS" /* Posture */]: ObservationMeasurementCode.Posture,
  ["BAT" /* Battery */]: ObservationMeasurementCode.Battery,
  ["RRI" /* RRI */]: ObservationMeasurementCode.RRI,
  ["ECG" /* ECG */]: ObservationMeasurementCode.ECG,
  ["MS-Pi" /* PerfusionIndex */]: ObservationMeasurementCode.PerfusionIndex,
  ["GLUCOSE" /* Glucose */]: ObservationMeasurementCode.Glucose,
  ["GLUCOSE-BEFORE-MEAL" /* GlucoseBeforeMeal */]: ObservationMeasurementCode.GlucoseBeforeMeal,
  ["GLUCOSE-AFTER-MEAL" /* GlucoseAfterMeal */]: ObservationMeasurementCode.GlucoseAfterMeal,
  ["GLUCOSE-FASTING" /* GlucoseFasting */]: ObservationMeasurementCode.GlucoseFasting,
  ["BP-SYSTOLIC" /* SystolicArterialBloodPressure */]: ObservationMeasurementCode.SystolicArterialBloodPressure,
  ["BP-DIASTOLIC" /* DiastolicArterialBloodPressure */]: ObservationMeasurementCode.DiastolicArterialBloodPressure,
  ["BP-MAP" /* MeanArterialBloodPressure */]: ObservationMeasurementCode.MeanArterialBloodPressure,
  ["BP-COMBINED" /* CombinedArterialBloodPressure */]: ObservationMeasurementCode.CombinedArterialBloodPressure,
  ["WEIGHT" /* Weight */]: ObservationMeasurementCode.Weight,
  ["POS-CALIBRATION" /* PostureCalibration */]: ObservationMeasurementCode.PostureCalibration,
  ["EWS" /* EarlyWarningScore */]: ObservationMeasurementCode.EarlyWarningScore,
  ["BTN" /* ButtonPress */]: ObservationMeasurementCode.ButtonPress,
  ["SENSOR-LOW-QUALITY" /* SensorLowQuality */]: ObservationMeasurementCode.SensorLowQuality,
  ["FALL" /* Fall */]: ObservationMeasurementCode.Fall,
  ["WEAR" /* WearStatus */]: ObservationMeasurementCode.WearStatus,
  ["DEVICE" /* DeviceStatus */]: ObservationMeasurementCode.DeviceStatus
};
var codeToMeasurementNameMapping = {
  [ObservationMeasurementCode.Activity]: "ACT" /* Activity */,
  [ObservationMeasurementCode.HeartRate]: "HTR" /* HeartRate */,
  [ObservationMeasurementCode.RespiratoryRate]: "RSR" /* RespiratoryRate */,
  [ObservationMeasurementCode.SkinTemperature]: "STP" /* SkinTemperature */,
  [ObservationMeasurementCode.CoreTemperature]: "CTP" /* CoreTemperature */,
  [ObservationMeasurementCode.OxygenSaturation]: "MS-SpO2" /* OxygenSaturation */,
  [ObservationMeasurementCode.PerfusionIndex]: "MS-Pi" /* PerfusionIndex */,
  [ObservationMeasurementCode.Steps]: "STS" /* Steps */,
  [ObservationMeasurementCode.Posture]: "POS" /* Posture */,
  [ObservationMeasurementCode.Battery]: "BAT" /* Battery */,
  [ObservationMeasurementCode.RRI]: "RRI" /* RRI */,
  [ObservationMeasurementCode.ECG]: "ECG" /* ECG */,
  [ObservationMeasurementCode.Glucose]: "GLUCOSE" /* Glucose */,
  [ObservationMeasurementCode.GlucoseAfterMeal]: "GLUCOSE-AFTER-MEAL" /* GlucoseAfterMeal */,
  [ObservationMeasurementCode.GlucoseBeforeMeal]: "GLUCOSE-BEFORE-MEAL" /* GlucoseBeforeMeal */,
  [ObservationMeasurementCode.GlucoseFasting]: "GLUCOSE-FASTING" /* GlucoseFasting */,
  [ObservationMeasurementCode.SystolicArterialBloodPressure]: "BP-SYSTOLIC" /* SystolicArterialBloodPressure */,
  [ObservationMeasurementCode.DiastolicArterialBloodPressure]: "BP-DIASTOLIC" /* DiastolicArterialBloodPressure */,
  [ObservationMeasurementCode.MeanArterialBloodPressure]: "BP-MAP" /* MeanArterialBloodPressure */,
  [ObservationMeasurementCode.CombinedArterialBloodPressure]: "BP-COMBINED" /* CombinedArterialBloodPressure */,
  [ObservationMeasurementCode.Weight]: "WEIGHT" /* Weight */,
  [ObservationMeasurementCode.PostureCalibration]: "POS-CALIBRATION" /* PostureCalibration */,
  [ObservationMeasurementCode.EarlyWarningScore]: "EWS" /* EarlyWarningScore */,
  [ObservationMeasurementCode.ButtonPress]: "BTN" /* ButtonPress */,
  [ObservationMeasurementCode.SensorLowQuality]: "SENSOR-LOW-QUALITY" /* SensorLowQuality */,
  [ObservationMeasurementCode.Fall]: "FALL" /* Fall */,
  [ObservationMeasurementCode.WearStatus]: "WEAR" /* WearStatus */,
  [ObservationMeasurementCode.DeviceStatus]: "DEVICE" /* DeviceStatus */
};
var mapCodeToMeasurementName = (code) => codeToMeasurementNameMapping[code];
var mapMeasurementNameToCode = (name) => measurementNameToCodeMapping[name];
var getObservationVitals = (observation) => {
  const { component = [], effectiveDateTime, device } = observation;
  const vitals = component.map((com) => {
    const { code, valueQuantity } = com;
    const observationMeasurementCode = getFirstCodeFromCodableConcepts4([
      code
    ]);
    if (!observationMeasurementCode) {
      return void 0;
    }
    const measurementName = codeToMeasurementNameMapping[observationMeasurementCode];
    const value = getQuantityWithValue(valueQuantity);
    const hasData = value && measurementName && effectiveDateTime;
    if (!hasData) {
      return void 0;
    }
    const date5 = new Date(effectiveDateTime);
    return {
      measurementName,
      sensorId: getReferenceId2(device),
      ts: date5.getTime(),
      value: value.value
    };
  });
  return compact4(vitals);
};
var getObservationVitalsByMeasurementCode = (observations, measurementCode) => {
  const vitalData = observations.map(({ component = [], device, effectiveDateTime }) => {
    const res = component.find(({ code }) => code.text === measurementCode);
    if (!res) {
      return void 0;
    }
    return {
      sensorId: getReferenceId2(device),
      ts: effectiveDateTime ? new Date(effectiveDateTime).getTime() : void 0,
      value: res.valueQuantity?.value
    };
  });
  const result = first3(compact4(vitalData));
  return result;
};
var mapObservationsToLatestVitals = (observations) => {
  const measurementNames = Object.keys(measurementNameToCodeMapping);
  const vitals = measurementNames.reduce((prev, key) => {
    const measurementCode = measurementNameToCodeMapping[key];
    if (!measurementCode) {
      return prev;
    }
    const result = getObservationVitalsByMeasurementCode(observations, measurementCode);
    if (!result) {
      return prev;
    }
    return {
      ...prev,
      [key]: {
        measurementName: key,
        sensorId: result.sensorId,
        ts: result.ts,
        value: result.value
      }
    };
  }, {});
  return vitals;
};
var mapMeasurementToObservationComponent = ({ measurementName, value }) => {
  const measurementCode = measurementNameToCodeMapping[measurementName];
  const unit = ObservationMeasurementUnits[measurementCode];
  const code = createCodeableConcept4(measurementCode);
  return {
    code,
    valueQuantity: {
      value,
      unit
    }
  };
};
var getDeviceId = (vitals) => {
  const deviceIds = Object.values(vitals).map((obj) => obj.sensorId);
  return first3(compact4(deviceIds));
};
var mapLatestVitalsToObservation = ({ vitals, subject, practitioner, effectiveDateTime, type }) => {
  const component = Object.values(vitals).map(
    mapMeasurementToObservationComponent
  );
  const deviceId = getDeviceId(vitals);
  const validUUID = UUIDSchema.safeParse(deviceId);
  const device = validUUID.success ? createReference("Device", validUUID.data) : void 0;
  const observation = {
    subject,
    component,
    effectiveDateTime,
    status: "final",
    resourceType: "Observation",
    performer: [practitioner],
    code: createCodeableConcept4(type),
    device
  };
  return observation;
};
var mapFhirToModelObservation = (fhir) => {
  const { status: status2, code, subject, issued, performer = [], device, encounter } = fhir;
  const measures = getObservationVitals(fhir);
  const performers = performer.filter(isValidReference2);
  const model = {
    status: status2,
    performers,
    measures,
    code: getFirstCodeFromCodableConcepts4([code]),
    dateTime: issued,
    patient: getValidReference(subject),
    device: getValidReference(device),
    admission: getValidReference(encounter)
  };
  return model;
};
var mapFhirToModelObservationWithId = (fhir) => ({
  ...mapFhirToModelObservation(fhir),
  id: fhir.id
});
var invertedComparatorMapping = {
  "<": ">=",
  "<=": ">",
  ">": "<=",
  ">=": "<"
};
var mapReferenceRange = (ranges) => {
  if (!ranges) {
    return "";
  }
  const test = compact4(
    ranges?.map((range) => {
      if (range.low?.extension) {
        const comparator = invertedComparatorMapping[`${first3(range.low?.extension)?.valueString}`];
        return `${comparator} ${range.low.value} ${range.low.unit}`;
      }
      if (range.high?.extension) {
        const comparator = invertedComparatorMapping[`${first3(range.high?.extension)?.valueString}`];
        return `${comparator} ${range.high.value} ${range.high.unit}`;
      }
      if (range.low && range.high) {
        return `${range.low.value} - ${range.high.value} ${range.low.unit}`;
      }
      if (range.low && !range.high) {
        return `${range.low.value} ${range.low.unit}`;
      }
      if (!range.low && range.high) {
        return `${range.high.value} ${range.high.unit}`;
      }
    })
  );
  return `${test}`;
};
var mapToPathologyReportTable = (res) => {
  const table = res?.map((component) => {
    const interpretation = (component?.interpretation ? getDisplayFromCodableConcepts(component?.interpretation) : "") || "";
    const result = {
      code: getDisplayFromCodableConcepts([component.code]) || "",
      interpretation,
      valueQuantity: {
        value: component.valueQuantity?.value?.toString(),
        unit: component.valueQuantity?.unit
      },
      referenceRange: mapReferenceRange(component.referenceRange),
      valueString: component?.valueString
    };
    return result;
  });
  return flatten2(compact4(table));
};
var mapModelToFhirObservationPathologyReport = (fhir) => {
  const { status: status2, subject, effectiveDateTime, basedOn, component, code } = fhir;
  const components = mapToPathologyReportTable(component);
  const model = {
    status: status2,
    code: getFirstCodeFromCodableConcepts4([
      code
    ]),
    basedOn: getValidReference(first3(basedOn)),
    components,
    effectiveDateTime,
    patient: getValidReference(subject)
  };
  return { ...model, id: fhir.id };
};
var mapModelToFhirObservation = ({ code, dateTime, measures, patient, performers, status: status2, device, admission }) => {
  const component = valsOrNone6(
    measures.map(mapMeasurementToObservationComponent)
  );
  const effectiveDateTime = getEffectiveDateTimeFromMeasures(measures);
  return {
    resourceType: "Observation",
    status: status2,
    effectiveDateTime,
    subject: patient,
    performer: performers,
    code: createCodeableConcept4(code),
    device,
    component,
    issued: dateTime,
    encounter: admission
  };
};
var mapPregnancyToObservationComponent = (pregnancy) => {
  const { edd, lactation } = pregnancy;
  const eddComponent = edd ? {
    code: createCodeableConcept4(ObservationCode2.edd),
    valueString: edd
  } : void 0;
  const lactationComponent = lactation ? {
    code: createCodeableConcept4(ObservationCode2.lactation),
    valueBoolean: lactation
  } : void 0;
  return compact4([eddComponent, lactationComponent]);
};
var mapPregnancyToFhirObservation = ({
  pregnancy,
  patient,
  performers,
  encounter
}) => {
  const pregnancyObservation = {
    resourceType: "Observation",
    status: "final",
    subject: patient,
    performer: performers,
    code: createCodeableConcept4(ObservationCode2.pregnancy),
    issued: createFhirDateTime(/* @__PURE__ */ new Date()),
    component: mapPregnancyToObservationComponent(pregnancy),
    valueBoolean: pregnancy.isPregnant,
    encounter: {
      reference: encounter
    }
  };
  return pregnancyObservation;
};
var mapFhirObservationToModelPregnancy = (fhir) => {
  const eddComponent = fhir.component?.find(({ code }) => code.text === ObservationCode2.edd);
  const lactationComponent = fhir.component?.find(
    ({ code }) => code.text === ObservationCode2.lactation
  );
  return {
    edd: eddComponent?.valueString,
    isPregnant: fhir.valueBoolean || false,
    lactation: lactationComponent?.valueBoolean || false
  };
};
var getEffectiveDateTimeFromMeasures = (measures) => {
  const effectiveDateTimeArray = measures.map((measure) => measure.ts);
  const uniqEffectiveDateTimes = uniq(effectiveDateTimeArray);
  const uniqEffectiveDateTime = uniqEffectiveDateTimes.length !== 1 ? void 0 : first3(uniqEffectiveDateTimes);
  const effectiveDateTime = uniqEffectiveDateTime ? createFhirDateTime(new Date(uniqEffectiveDateTime)) : void 0;
  return effectiveDateTime;
};
var postureCommandToMeasurementMap = {
  ["LayingSupine" /* LayingSupine */]: 2 /* LayingSupine */,
  ["Upright" /* Upright */]: 1 /* Upright */
};
var postureCalibrationToMeasurement = (time, sensorId, value) => {
  if (typeof value === "undefined") {
    return void 0;
  }
  return {
    sensorId,
    measurementName: "POS-CALIBRATION" /* PostureCalibration */,
    ts: time.getTime(),
    value: postureCommandToMeasurementMap[value]
  };
};
var temperatureCalibrationToMeasurement = (time, sensorId, value) => {
  if (typeof value === "undefined") {
    return void 0;
  }
  return {
    sensorId,
    measurementName: "CTP" /* CoreTemperature */,
    ts: time.getTime(),
    value
  };
};

// src/admission/extension.ts
import {
  createCoding,
  ExtensionUrl as ExtensionUrl3,
  filterExtensions,
  medpraxSystem as medpraxSystem2
} from "@quromedical/fhir-common";
var mapICD10CodeToExtension = (code) => ({
  url: ExtensionUrl3.admissionICD10Code,
  valueCoding: createCoding(code.Code, code.Description, medpraxSystem2)
});
var getExtensionCodingValue = (extension) => extension.valueCoding;
var isRequiredCoding = (coding) => !!coding?.code;
var getAdmissionICD10CodesFromExtension = (extension) => {
  const result = filterExtensions(ExtensionUrl3.admissionICD10Code, extension).map(getExtensionCodingValue).filter(isRequiredCoding);
  return result;
};

// src/admission/mapping.ts
var createPrimaryIcd10FlagExtension = createExtensionValue3(
  ExtensionUrl4.encounterPrimaryIcd10Code
);
var createOxygenRateExtension = createExtensionValue3(ExtensionUrl4.encounterOxygenRate);
var createHospitalizationAuthorizationDateExtension = createExtensionValue3(
  ExtensionUrl4.encounterHospitalizationAuthorizationDate
);
var createHospitalizationDependentCodeExtension = createExtensionValue3(
  ExtensionUrl4.encounterHospitalizationDependentCode
);
var createHospitalizationSchemeCodeExtension = createExtensionValue3(
  ExtensionUrl4.encounterHospitalizationSchemeCode
);
var createHospitalizationSubscriberIdExtension = createExtensionValue3(
  ExtensionUrl4.encounterHospitalizationSubscriberId
);
var isPrimaryIcd10Code = findExtensionValue3(ExtensionUrl4.encounterPrimaryIcd10Code);
var getOxygenRate = findExtensionValue3(ExtensionUrl4.encounterOxygenRate);
var getHospitalizationAuthorizationDate = findExtensionValue3(
  ExtensionUrl4.encounterHospitalizationAuthorizationDate
);
var getHospitalizationDependentCode = findExtensionValue3(
  ExtensionUrl4.encounterHospitalizationDependentCode
);
var getHospitalizationSchemeCode = findExtensionValue3(
  ExtensionUrl4.encounterHospitalizationSchemeCode
);
var getHospitalizationSubscriberId = findExtensionValue3(
  ExtensionUrl4.encounterHospitalizationSubscriberId
);
var mapAuthorizationToEncounterHospitalization = (medicalAidAuthorization) => {
  if (!medicalAidAuthorization) {
    return void 0;
  }
  const { code, coverage, date: date5 } = medicalAidAuthorization;
  const { dependantCode, schemeCode, subscriberId } = coverage;
  const extensionList = [
    date5 ? createHospitalizationAuthorizationDateExtension(date5) : void 0,
    createHospitalizationDependentCodeExtension(dependantCode),
    createHospitalizationSchemeCodeExtension(schemeCode),
    createHospitalizationSubscriberIdExtension(subscriberId)
  ];
  return {
    preAdmissionIdentifier: code ? createIdentifier3(IdentifierCode4.encounterMedicalAidAuthorizationCode, code) : void 0,
    extension: compact5(extensionList)
  };
};
var mapHospitalizationToMedicalAidAuthorization = (hospitalization = {}) => {
  const { extension, preAdmissionIdentifier } = hospitalization;
  const code = preAdmissionIdentifier?.value;
  const date5 = getHospitalizationAuthorizationDate(extension);
  const dependantCode = getHospitalizationDependentCode(extension);
  const subscriberId = getHospitalizationSubscriberId(extension);
  const schemeCode = getHospitalizationSchemeCode(extension);
  const hasAll = code && date5 && dependantCode && subscriberId && schemeCode;
  if (!hasAll) {
    return void 0;
  }
  return {
    coverage: {
      dependantCode,
      schemeCode,
      subscriberId
    },
    code,
    date: date5
  };
};
var mapOxygenToExtension = (oxygen) => createOxygenRateExtension(oxygen);
var mapICD10CodeToCodeableConcept = (type, code) => {
  const isPrimary = type === "primary";
  const extension = createPrimaryIcd10FlagExtension(isPrimary);
  const codableConcept = createCodeableConcept5(code.code, code.display, code.display);
  return addExtensionToResource(codableConcept, extension);
};
var getReasonCode = (primaryIcd10Code, icd10Codes = []) => {
  const primaryReasonCode = mapICD10CodeToCodeableConcept("primary", primaryIcd10Code);
  const additionalReasonCodes = icd10Codes.map(partial2(mapICD10CodeToCodeableConcept, "secondary"));
  const reasonCode = [primaryReasonCode, ...additionalReasonCodes];
  return reasonCode;
};
var mapCodeableConceptToRequiredCoding = (codeableConcept) => codeableConcept?.coding?.filter(isRequiredCoding) || [];
var getICD10CodesFromCodeableConcepts = (codeableConcepts = []) => {
  const codings = codeableConcepts.map(mapCodeableConceptToRequiredCoding);
  return flatten3(codings);
};
var getICD10CodesFromAdmissionDiagnosis = (diagnoses = [], diagnosisType) => {
  return flatten3(
    diagnoses.map((d) => {
      const codings = getICD10CodesFromCodeableConcepts([d.use]);
      const chronicCodes = codings.filter((c) => c.code === diagnosisType);
      if (chronicCodes.length === 0) {
        return [];
      }
      return d.condition.display ? [d.condition.display] : [];
    })
  );
};
var createExtensions = (oxygen) => {
  const oxygenExtension = oxygen ? mapOxygenToExtension(oxygen) : void 0;
  return valsOrNone7(compact5([oxygenExtension]));
};
var mapLocationToFhirLocation = (location) => {
  if (!location) {
    return void 0;
  }
  return [{ location }];
};
var mapFhirLocationToLocationRef = (fhirLocation) => {
  if (!fhirLocation) {
    return void 0;
  }
  const firstLocation = first4(fhirLocation);
  return getValidReference(firstLocation?.location);
};
var updateEncounterFromAdmission = (encounter, update) => {
  const {
    period: period2,
    medicalAidAuthorization,
    oxygen,
    status: status2,
    service,
    diagnosis = [],
    location
  } = update;
  return {
    ...encounter,
    period: period2,
    status: status2 || encounter.status,
    extension: createExtensions(oxygen),
    hospitalization: mapAuthorizationToEncounterHospitalization(medicalAidAuthorization),
    type: [createCodeableConcept5(service)],
    diagnosis: mapAdmissionDiagnosisToFhir(diagnosis),
    location: mapLocationToFhirLocation(location)
  };
};
var mapAdmissionToEncounter = (admission) => {
  const {
    class: cls,
    status: status2,
    patient: subject,
    period: period2,
    service,
    medicalAidAuthorization,
    oxygen,
    statusHistory = [],
    diagnosis = [],
    location
  } = admission;
  const extension = createExtensions(oxygen);
  return {
    resourceType: "Encounter",
    class: createCoding2(cls),
    status: status2,
    subject,
    period: period2,
    type: [createCodeableConcept5(service)],
    hospitalization: mapAuthorizationToEncounterHospitalization(medicalAidAuthorization),
    extension,
    statusHistory,
    diagnosis: mapAdmissionDiagnosisToFhir(diagnosis),
    location: mapLocationToFhirLocation(location)
  };
};
var updateEncounterFromAdmissionWithId = (encounter, update) => {
  const result = updateEncounterFromAdmission(encounter, update);
  return {
    ...result,
    id: encounter.id
  };
};
var mapAdmissionToEncounterWithId = (admission) => {
  const encounter = mapAdmissionToEncounter(admission);
  return {
    ...encounter,
    id: admission.id
  };
};
var classCodes = Object.values(EncounterClass2);
var serviceCodes = Object.values(EncounterService2);
var isCodeOfType = (codes) => (code) => {
  if (!code) {
    return false;
  }
  const isCode = codes.includes(code);
  return isCode;
};
var isClassCode = isCodeOfType(classCodes);
var isServiceCode = isCodeOfType(serviceCodes);
var getClassCode = (coding) => {
  const code = coding?.code;
  const isCode = isClassCode(code);
  return isCode ? code : void 0;
};
var getServiceCode = (codeableConcept) => {
  const codes = getCodes(codeableConcept);
  const foundCoding = codes.find(isServiceCode);
  return foundCoding;
};
var mapPeriodToAdmissionPeriod = (period2 = {}) => {
  const { start, end } = period2;
  if (!start) {
    return void 0;
  }
  return {
    start,
    end
  };
};
var mapEncounterToAdmission = (encounter, observation) => {
  const {
    class: cls,
    status: status2,
    type,
    subject,
    period: period2,
    hospitalization,
    extension,
    statusHistory = [],
    diagnosis = [],
    location = [],
    reasonCode
  } = encounter;
  const { icd10Codes, primaryIcd10Code, chronic, acute, reasonOfAdmission } = mapDiagnosisToCodes(diagnosis);
  const pregnancyObservation = observation ? mapFhirObservationToModelPregnancy(observation) : void 0;
  return {
    status: status2,
    class: getClassCode(cls),
    service: getServiceCode(first4(type)),
    primaryIcd10Code,
    icd10Codes,
    patient: getValidReference(subject),
    period: mapPeriodToAdmissionPeriod(period2),
    medicalAidAuthorization: mapHospitalizationToMedicalAidAuthorization(hospitalization),
    oxygen: getOxygenRate(extension),
    statusHistory,
    diagnosis: mapFhirToAdmissionDiagnosis(diagnosis),
    location: mapFhirLocationToLocationRef(location),
    chronic,
    acute,
    reasonOfAdmission,
    statusReason: reasonCode ? getFirstCodeFromCodableConcepts5(reasonCode) : void 0,
    pregnancy: pregnancyObservation
  };
};
var mapEncounterToAdmissionWithId = (encounter, observation) => {
  const admission = mapEncounterToAdmission(encounter, observation);
  return {
    ...admission,
    id: encounter.id
  };
};
var getFirstAdmissionFromEncounterBundle = (bundle) => {
  const resources = getResourcesFromBundle(bundle);
  const encounter = first4(resources);
  if (!encounter) {
    return void 0;
  }
  return mapEncounterToAdmissionWithId(encounter);
};
var finishEncounter = (encounter, periodEnd) => {
  const period2 = encounter.period ? {
    start: encounter.period.start,
    // end: createFhirDate(periodEnd),
    end: periodEnd
  } : void 0;
  if (!encounter.statusHistory) {
    return {
      ...encounter,
      status: "finished",
      period: period2
    };
  }
  const statusHistory = encounter.statusHistory.map((status2) => {
    const ispaused = status2.status === "in-progress" && status2.period.end === void 0;
    const updatePausedStatus = {
      period: {
        start: status2.period.start,
        end: !ispaused ? status2.period.end : periodEnd
      },
      status: !ispaused ? status2.status : "finished"
    };
    return updatePausedStatus;
  });
  return {
    ...encounter,
    status: "finished",
    period: period2,
    statusHistory
  };
};
var invalidateEncounter = (encounter) => {
  const checkStatus = encounter.status;
  if (checkStatus === "finished") {
    return encounter;
  }
  const period2 = encounter.period ? {
    start: encounter.period.start,
    end: createFhirDate2(/* @__PURE__ */ new Date())
  } : void 0;
  if (!encounter.statusHistory) {
    return {
      ...encounter,
      status: "entered-in-error",
      period: period2
    };
  }
  const statusHistory = encounter.statusHistory.map((status2) => {
    const ispaused = status2.status === "in-progress" && status2.period.end === void 0;
    const updatePausedStatus = {
      period: {
        start: status2.period.start,
        end: !ispaused ? status2.period.end : createFhirDate2(/* @__PURE__ */ new Date())
      },
      status: !ispaused ? status2.status : "entered-in-error"
    };
    return updatePausedStatus;
  });
  return {
    ...encounter,
    status: "entered-in-error",
    period: period2,
    statusHistory
  };
};
var pauseEncounter = (encounter) => {
  const checkStatus = encounter.status;
  if (checkStatus === "finished") {
    return encounter;
  }
  const hasEncounterHistory = encounter.statusHistory ? encounter.statusHistory : [];
  const pausedStatus = {
    period: {
      start: createFhirDate2(/* @__PURE__ */ new Date())
    },
    status: "onleave"
  };
  if (hasEncounterHistory.length === 0) {
    return {
      ...encounter,
      status: "onleave",
      statusHistory: [
        {
          period: {
            start: encounter.period?.start,
            end: createFhirDate2(/* @__PURE__ */ new Date())
          },
          status: "finished"
        },
        pausedStatus
      ]
    };
  }
  const statusHistory = hasEncounterHistory.map((status2) => {
    const isInProgress = status2.status === "in-progress" && status2.period.end === void 0;
    const updatePausedStatus = {
      period: {
        start: status2.period.start,
        end: createFhirDate2(/* @__PURE__ */ new Date())
      },
      status: "finished"
    };
    const updatedHistory = !isInProgress ? status2 : updatePausedStatus;
    return updatedHistory;
  });
  return {
    ...encounter,
    status: "onleave",
    statusHistory: [...statusHistory, pausedStatus]
  };
};
var resumeEncounter = (encounter) => {
  if (!encounter.statusHistory) {
    return encounter;
  }
  const needsHistoryChange = encounter.statusHistory.length === 0 || encounter.status === "finished";
  if (needsHistoryChange) {
    return encounter;
  }
  const resumeStatus = {
    period: {
      start: createFhirDate2(/* @__PURE__ */ new Date())
    },
    status: "in-progress"
  };
  const statusHistory = encounter.statusHistory.map((status2) => {
    const ispaused = status2.status === "onleave" && status2.period.end === void 0;
    const updatePausedStatus = {
      period: {
        start: status2.period.start,
        end: createFhirDate2(/* @__PURE__ */ new Date())
      },
      status: status2.status
    };
    const updatedHistory = !ispaused ? status2 : updatePausedStatus;
    return updatedHistory;
  });
  return {
    ...encounter,
    status: "in-progress",
    statusHistory: [...statusHistory, resumeStatus]
  };
};
var createEncounterAuditResource = (resource, method, fullUrlKey = "encounter") => createAuditResource(resource, method, fullUrlKey);
var createAdmissionSubjectAdder = (patients) => (admission) => {
  const patientId = getReferenceId3(admission.patient);
  if (!patientId) {
    return admission;
  }
  const patient = patients.find((e) => e.id === patientId);
  if (!patient) {
    return admission;
  }
  const subject = mapFhirToSummaryModelPatient(patient);
  return {
    ...admission,
    subject
  };
};
var addSubjectsToAdmission = (admissions, patients) => {
  const addSubjectToAdmission = createAdmissionSubjectAdder(patients);
  return admissions.map(addSubjectToAdmission);
};
var mapCodeableConceptToRequiredCode = (codeableConcept) => {
  const codes = first4(codeableConcept?.coding?.filter(isRequiredCoding));
  return codes;
};
var mapAdmissionDiagnosisToFhir = (diagnosis) => {
  const diagnoses = diagnosis.map((d) => {
    return {
      condition: d.condition,
      use: createCodeableConcept5(d.type || "")
    };
  });
  return diagnoses;
};
var mapFhirToAdmissionDiagnosis = (diagnosis) => {
  const diagnoses = diagnosis.map((d) => {
    return {
      condition: getValidReference(d.condition),
      type: first4(getCodes(d.use))
    };
  });
  return diagnoses;
};
var splitAndRemoveColon = (str) => {
  const codeDisplay = str.split(":").map((word) => word.trim());
  const code = first4(codeDisplay) || "";
  const display = last2(codeDisplay) || "";
  return createCoding2(code, display);
};
var mapDiagnosisToCodes = (diagnoses) => {
  const primary = diagnoses.find(
    (code) => first4(getCodes(code.use)) === DiagnosisTypeCode2.primary
  );
  const primaryIcd10Code = primary?.condition.display ? splitAndRemoveColon(primary?.condition.display) : void 0;
  const icd10Codes = diagnoses.filter((code) => first4(getCodes(code.use)) === DiagnosisTypeCode2.secondary).map((c) => splitAndRemoveColon(c.condition.display || ""));
  const acute = diagnoses.filter((code) => first4(getCodes(code.use)) === DiagnosisTypeCode2.acute).map((c) => splitAndRemoveColon(c.condition.display || ""));
  const chronic = diagnoses.filter((code) => first4(getCodes(code.use)) === DiagnosisTypeCode2.chronic).map((c) => splitAndRemoveColon(c.condition.display || ""));
  const reasonOfAdmission = diagnoses.filter((code) => first4(getCodes(code.use)) === DiagnosisTypeCode2.rfa).map((c) => splitAndRemoveColon(c.condition.display || ""));
  return {
    primaryIcd10Code,
    icd10Codes,
    acute,
    chronic,
    reasonOfAdmission
  };
};

// src/media/mapping.ts
import {
  createBundleEntry,
  createFhirDateTime as createFhirDateTime2,
  getContentType
} from "@quromedical/fhir-common";
import { compact as compact6 } from "@quromedical/utils";
var mapModelToFhirMedia = (storageClient, model) => {
  const { contentType, createdBy, createdDateTime, title, key, patient, admission, partOf } = model;
  const url = storageClient.getFullFileUrl(key);
  return {
    status: "completed",
    resourceType: "Media",
    subject: patient,
    operator: createdBy,
    createdDateTime,
    encounter: admission,
    partOf,
    content: {
      title,
      contentType,
      url
    }
  };
};
var mapModelToFhirMediaUpdate = (storageClient, model) => {
  const { contentType, createdBy, createdDateTime, title, key, patient, admission, partOf } = model;
  const url = key ? storageClient.getFullFileUrl(key) : void 0;
  return {
    status: "completed",
    resourceType: "Media",
    subject: patient,
    operator: createdBy,
    createdDateTime,
    encounter: admission,
    partOf,
    content: {
      title,
      contentType,
      url
    }
  };
};
var mapFhirToModelMedia = (storageClient, fhir) => {
  const { content, createdDateTime, operator, subject, encounter, partOf: fhirPartOf } = fhir;
  const createdBy = getValidReference(operator);
  const patient = getValidReference(subject);
  const admission = getValidReference(encounter);
  const partOf = compact6(fhirPartOf?.map(getValidReference));
  const { title } = content;
  const key = content.url ? storageClient.getKeyFromUrl(content.url) : void 0;
  const contentType = getContentType(content.contentType);
  return {
    createdDateTime,
    createdBy,
    key,
    contentType,
    title,
    patient,
    admission,
    partOf
  };
};
var mapFhirToModelMediaWithId = (storageClient, fhir) => {
  const model = mapFhirToModelMedia(storageClient, fhir);
  return {
    ...model,
    id: fhir.id
  };
};
var mapCreateToMedia = (media, patient, createdBy, admission, createdDate = /* @__PURE__ */ new Date(), partOf) => ({
  ...media,
  createdBy,
  patient,
  createdDateTime: createFhirDateTime2(createdDate),
  admission,
  partOf
});
var mapCreateToFhirMedia = ({
  patient,
  practitioner,
  admission,
  createdDate = /* @__PURE__ */ new Date(),
  storageClient,
  partOf
}) => (media) => {
  const model = mapCreateToMedia(media, patient, practitioner, admission, createdDate, partOf);
  const fhir = mapModelToFhirMedia(storageClient, model);
  return fhir;
};
var createMediaResources = ({
  admission,
  patient,
  practitioner,
  attachments,
  storageClient
}) => {
  const createMediaResource = mapCreateToFhirMedia({
    patient,
    practitioner,
    admission,
    storageClient
  });
  const result = attachments.map((media) => {
    const res = createMediaResource(media);
    const entry = createBundleEntry("POST", res);
    return entry;
  });
  return result;
};

// src/notes/mapping.ts
var createAnnotation = (code, text) => ({
  text,
  extension: [
    {
      url: ExtensionUrl5.clinicalImpressionNoteCode,
      valueCode: code
    }
  ]
});
var createStructuredContentJSONExtension = createExtensionValue4(
  ExtensionUrl5.clinicalImpressionStructuredContentJSON
);
var createNotePinnedExtension = createExtensionValue4(ExtensionUrl5.clinicalImpressionPinned);
var createStructuredContentExtension = (structuredContent) => createStructuredContentJSONExtension(JSON.stringify(structuredContent));
var mapNoteToClinicalImpression = (soapNote, code, locationCode, subject, practitioner, practitionersToNotify, effectiveDateTime, pinned, encounter, structuredContent) => {
  const note = [
    createAnnotation(ClinicalImpressionNoteCode.Subjective, soapNote.subjective),
    soapNote.objective && createAnnotation(ClinicalImpressionNoteCode.Objective, soapNote.objective),
    soapNote.assessment && createAnnotation(ClinicalImpressionNoteCode.Assessment, soapNote.assessment),
    soapNote.plan && createAnnotation(ClinicalImpressionNoteCode.Plan, soapNote.plan)
  ];
  const notificationExtensions = practitionersToNotify.map((ref) => ({
    url: ExtensionUrl5.practitionerToNotify,
    valueReference: ref
  }));
  const structuredContentExtension = structuredContent && createStructuredContentExtension(structuredContent);
  return {
    extension: compact7([
      {
        url: ExtensionUrl5.clinicalImpressionLocationCode,
        valueCode: locationCode
      },
      {
        url: ExtensionUrl5.clinicalImpressionAssessmentCode,
        valueCode: soapNote.assessmentCode
      },
      {
        url: ExtensionUrl5.clinicalImpressionPlanCode,
        valueCode: soapNote.planCode
      },
      ...notificationExtensions,
      createNotePinnedExtension(pinned),
      structuredContentExtension
    ]),
    resourceType: "ClinicalImpression",
    status: "completed",
    subject,
    assessor: practitioner,
    effectiveDateTime,
    date: effectiveDateTime,
    code: createCodeableConcept6(code),
    note: compact7(note),
    encounter
  };
};
var findSubjectiveNotes = (noteCode, notes) => {
  const subjective = notes.filter((annotation) => {
    const extension = findExtension(ExtensionUrl5.clinicalImpressionNoteCode, annotation.extension);
    return extension?.valueCode === noteCode;
  });
  return subjective.map((note) => note.text);
};
var findNote = (noteCode, notes) => {
  const note = notes.find((annotation) => {
    const extension = findExtension(ExtensionUrl5.clinicalImpressionNoteCode, annotation.extension);
    return extension?.valueCode === noteCode;
  });
  return note?.text;
};
var isStructuredContent = (data) => data?.type === "nested-comment-nodes";
var parseStructuredContent = (extension = []) => {
  const json = findExtensionValue4(ExtensionUrl5.clinicalImpressionStructuredContentJSON)(extension);
  if (!json) {
    return void 0;
  }
  try {
    const parsed = JSON.parse(json);
    const isValid3 = isStructuredContent(parsed);
    if (!isValid3) {
      return void 0;
    }
    return parsed;
  } catch (err) {
    console.error("error parsing structured note data", err);
    return void 0;
  }
};
var mergeResourcesToNote = (clinicalImpression, allMedia, observations, encounters, storageClient) => {
  const {
    id,
    assessor,
    investigation = [],
    extension = [],
    note: clinicalImpressionNote = [],
    code: clinicalImpressionCode,
    effectiveDateTime: dateCreated
  } = clinicalImpression;
  const noteCode = clinicalImpressionCode?.text;
  const locationCode = findExtension(ExtensionUrl5.clinicalImpressionLocationCode, extension)?.valueCode;
  const practitionersToNotify = filterExtensions2(ExtensionUrl5.practitionerToNotify, extension).map(
    (ext) => ext.valueReference
  );
  const soapNote = {
    subjective: findSubjectiveNotes(ClinicalImpressionNoteCode.Subjective, clinicalImpressionNote),
    objective: findNote(ClinicalImpressionNoteCode.Objective, clinicalImpressionNote),
    assessment: findNote(ClinicalImpressionNoteCode.Assessment, clinicalImpressionNote),
    plan: findNote(ClinicalImpressionNoteCode.Plan, clinicalImpressionNote),
    assessmentCode: findExtension(ExtensionUrl5.clinicalImpressionAssessmentCode, extension)?.valueCode,
    planCode: findExtension(ExtensionUrl5.clinicalImpressionPlanCode, extension)?.valueCode
  };
  const mediaReferences = getInvestigationReferencesByCode(
    ClinicalImpressionInvestigationCode.Media,
    "Media",
    investigation
  );
  const vitalsReferences = getInvestigationReferencesByCode(
    ClinicalImpressionInvestigationCode.LatestVitals,
    "Observation",
    investigation
  );
  const referencedMedia = findReferencedResources(mediaReferences, allMedia);
  const referencedObservations = findReferencedResources(vitalsReferences, observations);
  const referencedEncounter = findReferencedResource(clinicalImpression.encounter, encounters);
  const media = referencedMedia.map((resource) => mapFhirToModelMediaWithId(storageClient, resource));
  const vitals = mapObservationsToLatestVitals(referencedObservations);
  const pinned = isNotePinned(clinicalImpression);
  const admission = referencedEncounter && mapEncounterToAdmissionWithId(referencedEncounter);
  const structuredContent = parseStructuredContent(extension);
  return {
    id,
    assessor,
    media,
    locationCode,
    vitals,
    note: soapNote,
    code: noteCode,
    practitionersToNotify: compact7(practitionersToNotify),
    dateCreated,
    pinned,
    admission,
    structuredContent
  };
};
var mapCreationEntriesToNote = (storageClient, entries) => {
  const clinicalImpression = findResourceByType(
    "ClinicalImpression",
    entries
  );
  const observations = filterResourcesByType("Observation", entries);
  const media = filterResourcesByType("Media", entries);
  const encounters = filterResourcesByType("Encounter", entries);
  if (!clinicalImpression) {
    return void 0;
  }
  return mergeResourcesToNote(clinicalImpression, media, observations, encounters, storageClient);
};

// src/notes/validation.ts
import {
  ClinicalImpressionAssessmentValueCode as ClinicalImpressionAssessmentValueCode3,
  ClinicalImpressionCode as ClinicalImpressionCode3,
  ClinicalImpressionLocationCode as ClinicalImpressionLocationCode3,
  ClinicalImpressionPlanValueCode as ClinicalImpressionPlanValueCode3,
  createReferenceValidationSchema
} from "@quromedical/fhir-common";
import * as yup5 from "yup";

// src/media/validation.ts
import { allowedContentTypes } from "@quromedical/fhir-common";
import * as yup4 from "yup";
var contentTypeSchema = yup4.string().required().oneOf(allowedContentTypes);
var titleSchema = yup4.string().required();
var createUploadUrlRequestSchema = yup4.object({
  title: titleSchema,
  contentType: contentTypeSchema
});
var createMediaDataSchema = yup4.object({
  title: titleSchema,
  contentType: contentTypeSchema,
  key: yup4.string().required()
});
var createMediaRequestSchema = yup4.object({
  media: yup4.array(createMediaDataSchema).required().min(1).max(5)
});

// src/notes/validation.ts
var clinicalImpressionCodes = Object.values(ClinicalImpressionCode3);
var codeSchema = yup5.string().required().oneOf(clinicalImpressionCodes);
var locationCodes = Object.values(ClinicalImpressionLocationCode3);
var locationCodeSchema = yup5.string().required().oneOf(locationCodes);
var assessmentCodes = Object.values(ClinicalImpressionAssessmentValueCode3);
var assessmentCodeSchema = yup5.string().required().oneOf(assessmentCodes);
var planCodes = Object.values(ClinicalImpressionPlanValueCode3);
var planCodeSchema = yup5.string().required().oneOf(planCodes);
var mediaListSchema = yup5.array(createMediaDataSchema).required().max(5);
var noteSchema = yup5.object({
  subjective: yup5.string().required(),
  assessmentCode: assessmentCodeSchema,
  planCode: planCodeSchema,
  assessment: yup5.string().notRequired(),
  plan: yup5.string().notRequired(),
  objective: yup5.string().notRequired()
});
var structuredContentTypes = ["nested-comment-nodes"];
var createNoteSchema = yup5.object({
  subject: yup5.string().required(),
  code: codeSchema,
  locationCode: locationCodeSchema,
  media: mediaListSchema,
  note: noteSchema,
  vitals: yup5.object().required(),
  // this should be updated to just be strings once the practitioner list is made just ids
  practitionersToNotify: yup5.array(createReferenceValidationSchema("Practitioner")).required(),
  structuredContent: yup5.object({
    type: yup5.string().required().equals([structuredContentTypes]),
    data: yup5.mixed().required()
  }).optional().default(void 0)
});
var noteUpdateSchema = yup5.object({
  subjective: yup5.string().required()
});

// src/communication/mapping.ts
import {
  IdentifierCode as IdentifierCode5,
  createCodeableConcept as createCodeableConcept7,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts6,
  isValidReference as isValidReference3,
  createIdentifier as createIdentifier4
} from "@quromedical/fhir-common";
import { compact as compact8, first as first5 } from "@quromedical/utils";
var mapCreateModelToFhirCommunication = (communication, mediaItemReference) => {
  const {
    admission,
    category,
    channel,
    content,
    priority,
    recipient,
    sender,
    status: status2,
    patient,
    topic,
    inResponseTo,
    messageId
  } = communication;
  const payload = content.map((c) => ({
    contentString: c.content,
    contentReference: mediaItemReference
  }));
  return {
    resourceType: "Communication",
    subject: patient,
    encounter: admission,
    category: [createCodeableConcept7(category)],
    medium: channel.map((c) => createCodeableConcept7(c)),
    priority,
    sender,
    status: status2,
    topic: createCodeableConcept7(topic),
    payload,
    inResponseTo: inResponseTo ? [inResponseTo] : void 0,
    recipient: recipient ? [recipient] : void 0,
    identifier: messageId ? [createIdentifier4(IdentifierCode5.conversationMessage, messageId)] : void 0
  };
};
var mapModelToFhirCommunication = (communication) => {
  const {
    admission,
    category,
    channel,
    content,
    priority,
    recipient,
    sent,
    sender,
    status: status2,
    patient,
    topic,
    inResponseTo,
    received
  } = communication;
  const createFhirCommunication = mapCreateModelToFhirCommunication({
    admission,
    category,
    channel,
    content,
    priority,
    recipient,
    sender,
    status: status2,
    patient,
    topic,
    inResponseTo
  });
  return {
    ...createFhirCommunication,
    sent,
    received,
    recipient: recipient ? [recipient] : void 0
  };
};
var mapFhirCommunicationToModel = (communication) => {
  const {
    subject,
    encounter,
    category,
    medium,
    priority,
    sender,
    sent,
    status: status2,
    topic,
    payload,
    inResponseTo,
    received,
    recipient
  } = communication;
  const firstRecipient = first5(recipient);
  const firstInResponse = first5(inResponseTo);
  const content = payload?.map((p) => {
    if (!p.contentString) {
      return void 0;
    }
    const contentResult = isValidReference3(p.contentReference) ? {
      content: p.contentString,
      attachment: p.contentReference
    } : {
      content: p.contentString
    };
    return contentResult;
  });
  const channel = medium?.map((m) => {
    return getFirstCodeFromCodableConcepts6([m]);
  });
  return {
    admission: isValidReference3(encounter) ? encounter : void 0,
    category: getFirstCodeFromCodableConcepts6(category),
    channel: compact8(channel),
    priority,
    recipient: isValidReference3(firstRecipient) ? firstRecipient : void 0,
    sent,
    sender: isValidReference3(sender) ? sender : void 0,
    status: status2,
    patient: isValidReference3(subject) ? subject : void 0,
    inResponseTo: isValidReference3(firstInResponse) ? firstInResponse : void 0,
    topic: topic ? getFirstCodeFromCodableConcepts6([topic]) : void 0,
    received,
    content: compact8(content)
  };
};

// src/communication/schema.ts
import { ConversationMessageSchema } from "@quromedical/communication";
import { z as z21 } from "zod";
var CommunicationCategoryCode = /* @__PURE__ */ ((CommunicationCategoryCode2) => {
  CommunicationCategoryCode2["Notification"] = "communication.category.notification";
  CommunicationCategoryCode2["Incoming"] = "communication.category.incoming";
  return CommunicationCategoryCode2;
})(CommunicationCategoryCode || {});
var CommunicationTopicCode = /* @__PURE__ */ ((CommunicationTopicCode2) => {
  CommunicationTopicCode2["OperationsWelcomeTCP"] = "communication.operation.welcome.tcp";
  CommunicationTopicCode2["OperationsVitalCollectionTCP"] = "communication.operation.welcome.tcp";
  CommunicationTopicCode2["OperationsCollectionTCP"] = "communication.operation.collection.tcp";
  CommunicationTopicCode2["OperationsCollectionDischargeTCP"] = "communication.operation.collection.discharge";
  CommunicationTopicCode2["FreeChat"] = "communication.topic.free.chat";
  return CommunicationTopicCode2;
})(CommunicationTopicCode || {});
var CommunicationChannelCode = /* @__PURE__ */ ((CommunicationChannelCode2) => {
  CommunicationChannelCode2["Email"] = "communication.channel.email";
  CommunicationChannelCode2["SMS"] = "communication.channel.sms";
  CommunicationChannelCode2["WhatsApp"] = "communication.channel.whatsapp";
  return CommunicationChannelCode2;
})(CommunicationChannelCode || {});
var CommunicationChannelCodeDescription = {
  ["communication.channel.whatsapp" /* WhatsApp */]: "WhatsApp",
  ["communication.channel.email" /* Email */]: "Email",
  ["communication.channel.sms" /* SMS */]: "SMS"
};
var communicationStatus = [
  "preparation",
  "in-progress",
  "not-done",
  "on-hold",
  "stopped",
  "completed",
  "entered-in-error",
  "unknown"
];
var communicationPriority = ["routine", "urgent", "asap", "stat"];
var CommunicationStatusSchema = z21.enum(communicationStatus);
var CommunicationPrioritySchema = z21.enum(communicationPriority);
var CommunicationCategorySchema = z21.nativeEnum(CommunicationCategoryCode);
var CommunicationChannelSchema = z21.array(z21.nativeEnum(CommunicationChannelCode)).min(1);
var CommunicationTopicSchema = z21.nativeEnum(CommunicationTopicCode);
var AttachmentSchema = z21.object({
  data: z21.string(),
  contentType: ContentTypeSchema
});
var communicationContentSchema = z21.object({
  content: z21.string(),
  attachment: DisplayReferenceSchema.optional()
});
var communicationContentCreateSchema = z21.object({
  content: z21.string(),
  attachmentData: AttachmentSchema.optional()
});
var conversationContentSchema = z21.object({
  content: z21.string()
});
var CommunicationSchema = z21.object({
  inResponseTo: DisplayReferenceSchema.optional(),
  status: CommunicationStatusSchema,
  category: CommunicationCategorySchema,
  priority: CommunicationPrioritySchema,
  channel: CommunicationChannelSchema,
  patient: DisplayReferenceSchema,
  topic: CommunicationTopicSchema,
  admission: DisplayReferenceSchema,
  sent: DateTimeSchema,
  received: DateTimeSchema.optional(),
  recipient: DisplayReferenceSchema,
  sender: DisplayReferenceSchema,
  content: z21.array(communicationContentSchema).min(1)
});
var CommunicationGetResponseSchema = CommunicationSchema.partial();
var ConversationCreateRequestSchema = z21.object({
  patientId: UUIDSchema
});
var ConversationGetRequestSchema = ConversationCreateRequestSchema;
var ConversationGetResponseSchema = z21.object({
  id: z21.string()
});
var CommunicationGetAllResponseSchema = PaginatedBundleSchema.extend({
  conversations: ConversationMessageSchema.array()
});
var CommunicationCreateRequestSchema = z21.object({
  inResponseTo: DisplayReferenceSchema.optional(),
  status: CommunicationStatusSchema,
  category: CommunicationCategorySchema,
  priority: CommunicationPrioritySchema,
  channel: CommunicationChannelSchema,
  patient: DisplayReferenceSchema,
  topic: CommunicationTopicSchema,
  admission: DisplayReferenceSchema,
  recipient: DisplayReferenceSchema,
  sender: DisplayReferenceSchema,
  content: z21.array(communicationContentCreateSchema).min(1),
  messageId: z21.string().optional()
});
var CommunicationSearchSchema = PaginationSchema.extend({
  subject: UUIDSchema.optional().describe("The subject of the communication"),
  nextOfKin: UUIDSchema.optional().describe("The next of kin of the patient"),
  medium: z21.string().optional().describe("A channel of communication"),
  category: z21.string().optional().describe("Message category"),
  recipient: UUIDSchema.optional().describe("The recipient of the communication")
});

// src/allergy/mapping.ts
import {
  createCodeableConcept as createCodeableConcept8,
  createReference as createReference2,
  getDisplayFromCodableConcepts as getDisplayFromCodableConcepts2,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts7,
  medpraxSystem as medpraxSystem3
} from "@quromedical/fhir-common";
import { first as first6 } from "@quromedical/utils";
var mapPageResultToMedpraxAllergy = (pageResult) => {
  return {
    code: pageResult.Code,
    name: pageResult.Name,
    objectID: pageResult.objectID ? pageResult.objectID : ""
  };
};
var mapAllergyToFhir = (allergy) => {
  const { admission, type, patient, recorder, name, status: status2, code, id, severity } = allergy;
  const criticality = severity ? getAllergyCriticality(severity) : void 0;
  return {
    id,
    resourceType: "AllergyIntolerance",
    type: "allergy",
    code: createCodeableConcept8(code, name, name, medpraxSystem3),
    encounter: admission,
    clinicalStatus: createCodeableConcept8(status2),
    patient,
    recorder,
    category: [type],
    criticality
  };
};
var mapFhirToAllergy = (allergy) => {
  const { encounter, category, patient, recorder, clinicalStatus, code, id, criticality } = allergy;
  const mapped = {
    id,
    patient,
    name: code ? getDisplayFromCodableConcepts2([code]) : void 0,
    code: code ? getFirstCodeFromCodableConcepts7([code]) : void 0,
    admission: getValidReference(encounter),
    status: getFirstCodeFromCodableConcepts7(
      clinicalStatus ? [clinicalStatus] : void 0
    ),
    recorder: getValidReference(recorder),
    type: category ? first6(category) : void 0,
    severity: criticality ? allergySeverityMap[criticality] : void 0
  };
  const parsed = AllergyWithIdSchema.safeParse(mapped);
  if (parsed.success == false) {
    throw parsed.error;
  }
  return parsed.data;
};
var mapCreateAllergyToFhir = (allergy) => {
  const { admission, patient, recorder, name, code, severity } = allergy;
  const encounterReference = admission ? createReference2("Encounter", admission) : void 0;
  const encounterFullUrlReference = admission ? { reference: admission } : void 0;
  const encounter = admission?.includes("urn") ? encounterFullUrlReference : encounterReference;
  const criticality = getAllergyCriticality(severity);
  return {
    resourceType: "AllergyIntolerance",
    code: createCodeableConcept8(code, name, name, medpraxSystem3),
    encounter,
    clinicalStatus: createCodeableConcept8("active"),
    patient: createReference2("Patient", patient),
    recorder: recorder ? createReference2("Practitioner", recorder) : void 0,
    type: "allergy",
    category: ["medication"],
    criticality
  };
};
var mapUpdateAllergyToFhir = (allergy, status2, severity) => {
  return mapAllergyToFhir({
    ...allergy,
    status: status2,
    severity
  });
};
var allergySeverityMap = {
  low: "mild",
  high: "severe",
  "unable-to-assess": "unknown"
};
var getAllergyCriticality = (severity) => {
  return Object.keys(allergySeverityMap).find(
    (key) => allergySeverityMap[key] === severity
  );
};

// src/video-consult/schema.ts
import { z as z22 } from "zod";
var VideoConsultInvitationType = ["sms", "email", "whatsapp"];
var InvitationTypeSchema = z22.enum(VideoConsultInvitationType);
var VideoConsultSendInvitationRequestSchema = z22.object({
  roomId: z22.string(),
  type: InvitationTypeSchema,
  subject: UUIDSchema.describe("Patient ID")
});
var VideoConsultSendInvitationResponseSchema = z22.object({});
var VideoConsultCreateRoomResponseSchema = z22.object({
  token: z22.string(),
  roomId: z22.string(),
  joinRoomUrl: z22.string()
});
var VideoConsultCreateRoomRequestSchema = z22.object({
  subject: UUIDSchema.describe("Patient ID")
});

// src/medical-aid/mapping.ts
import {
  CoverageClassCode,
  createCodeableConcept as createCodeableConcept10,
  createMapWithId,
  getPlan,
  getReferenceType,
  valsOrNone as valsOrNone8
} from "@quromedical/fhir-common";
import { first as first8 } from "@quromedical/utils";

// src/organization/mapping.ts
import {
  createCodeableConcept as createCodeableConcept9,
  getAddressLines as getAddressLines2,
  getCity as getCity2,
  getCountry as getCountry2,
  getEmail as getEmail2,
  getPostalCode as getPostalCode2,
  OrganizationCode,
  OrganizationCodeDescription,
  getPrimaryPhoneNumber,
  IdentifierCode as IdentifierCode6,
  getIdentifier as getIdentifier2,
  getSecondaryNumbers,
  createIdentifier as createIdentifier5
} from "@quromedical/fhir-common";
import { compact as compact9, first as first7, last as last3 } from "@quromedical/utils";
var typeCodes = Object.values(OrganizationCode);
var getDescription = (code) => {
  if (!code) {
    return void 0;
  }
  return OrganizationCodeDescription[code];
};
var createAddress = (address) => {
  const {
    addressCity: city,
    addressPostalCode: postalCode,
    addressLines,
    addressCountry: country
  } = address;
  const line = addressLines ? addressLines : void 0;
  const extension = createAddressExtensionList(address);
  return createAddressList({
    line,
    postalCode,
    city,
    country,
    extension
  });
};
var createType = (type) => {
  if (!type) {
    return void 0;
  }
  const code = createCodeableConcept9(type);
  return [code];
};
var isGeneralInsurance = (general) => general?.type === OrganizationCode.InsuranceCompany;
var createGeneralIdentifier = (general) => {
  const isInsurance = isGeneralInsurance(general);
  const schemeCode = isInsurance && general.schemeCode;
  if (!schemeCode) {
    return void 0;
  }
  return [createIdentifier5(IdentifierCode6.organizationSchemeCode, schemeCode)];
};
var getOrganizationSchemeCode = (organization) => {
  const isInsurance = isGeneralInsurance(organization);
  if (!isInsurance) {
    return void 0;
  }
  return organization.schemeCode;
};
var mapModelToFhirOrganization = (form) => {
  const { general, contact } = form;
  const email = createEmailContactPoint(contact?.telecomEmail);
  const firstPhoneNumber = first7(contact?.telecomPhoneNumbers);
  const multiplePhoneNumbers = contact?.telecomPhoneNumbers && contact?.telecomPhoneNumbers.length > 1;
  const alternativePhoneNumber = multiplePhoneNumbers ? last3(contact?.telecomPhoneNumbers) : void 0;
  const primaryPhone = createPrimaryPhoneContactPoint(firstPhoneNumber);
  const secondaryPhone = createSecondaryPhoneContactPoint(alternativePhoneNumber);
  const telecom = mergeContactPoints([email, primaryPhone, secondaryPhone]);
  const contactAddress = {
    addressCity: contact?.addressCity,
    addressCountry: contact?.addressCountry,
    addressLines: compact9(contact?.addressLines),
    addressPostalCode: contact?.addressPostalCode,
    addressPlaceId: contact?.addressPlaceId,
    addressCoordinates: contact?.addressCoordinates
  };
  const address = createAddress(contactAddress);
  const type = createType(general?.type);
  const name = general?.name;
  const identifier = createGeneralIdentifier(general);
  return {
    resourceType: "Organization",
    identifier,
    name,
    type,
    address,
    telecom
  };
};
var getValidCode = (codings) => {
  const foundCode = codings?.find(({ code }) => {
    if (!code) {
      return false;
    }
    return typeCodes.includes(code);
  });
  return foundCode?.code;
};
var getTypeCode = (org) => {
  const code = org?.type?.find((type) => getValidCode(type.coding));
  return getValidCode(code?.coding);
};
var mapFhirToGeneral = (fhir) => {
  const type = getTypeCode(fhir);
  const { name, identifier } = fhir;
  if (type !== OrganizationCode.InsuranceCompany) {
    return {
      name,
      type
    };
  }
  const schemeCode = getIdentifier2(IdentifierCode6.organizationSchemeCode, identifier);
  return {
    name,
    type,
    schemeCode
  };
};
var mapFhirToModelOrganization = (fhir) => {
  const { telecom, address } = fhir;
  const telecomEmail = getEmail2(telecom);
  const telecomPhone = getPrimaryPhoneNumber(telecom);
  const secondaryPhone = getSecondaryNumbers(telecom);
  const addressCity = getCity2(address);
  const addressPostalCode = getPostalCode2(address);
  const addressLines = getAddressLines2(address);
  const firstAddress = first7(address);
  const addressCountry = getCountry2(firstAddress);
  const addressPlaceId = getAddressPlaceId(firstAddress?.extension);
  const addressCoordinates = getCoordinates(firstAddress);
  return {
    general: mapFhirToGeneral(fhir),
    contact: {
      telecomEmail,
      telecomPhoneNumbers: compact9([telecomPhone, secondaryPhone]),
      addressCity,
      addressCountry,
      addressLines: compact9(addressLines),
      addressPostalCode,
      addressPlaceId,
      addressCoordinates
    }
  };
};
var mapFhirToModelOrganizationWithId = (fhir) => {
  const organization = mapFhirToModelOrganization(fhir);
  return {
    ...organization,
    id: fhir.id
  };
};
var mapUpdateModelToFhirOrganization = (form) => {
  const organization = mapModelToFhirOrganization(form);
  return {
    ...organization,
    id: form.id
  };
};
var mapUpdateGeneralModelToFhirOrganization = (fhir, general) => {
  const modelOrganization = mapFhirToModelOrganizationWithId(fhir);
  const { contact } = modelOrganization;
  const fhirOrganization = mapModelToFhirOrganization({
    contact,
    general
  });
  return { ...fhirOrganization, id: fhir.id };
};
var mapUpdateContactModelToFhirOrganization = (fhir, contact) => {
  const modelOrganization = mapFhirToModelOrganizationWithId(fhir);
  const { general } = modelOrganization;
  const fhirOrganization = mapModelToFhirOrganization({
    contact: contact.contact,
    general
  });
  return { ...fhirOrganization, id: fhir.id };
};

// src/medical-aid/mapping.ts
var allowedSubscriberTypes = ["Patient", "RelatedPerson"];
var getSubscriberType = (coverage) => {
  if (!coverage) {
    return void 0;
  }
  const subscriberType = getReferenceType(coverage.subscriber);
  const resolvedType = allowedSubscriberTypes.find((resourceType) => resourceType === subscriberType);
  return resolvedType;
};
var createCoverageClasses = (plan, option) => {
  const planClass = plan ? {
    type: createCodeableConcept10(CoverageClassCode.Plan),
    value: plan
  } : void 0;
  const optionClass = option ? {
    type: createCodeableConcept10(CoverageClassCode.SubPlan),
    value: option
  } : void 0;
  return valsOrNone8([planClass, optionClass]);
};
var mapPatientMembershipToCoverage = (membership) => {
  const { dependantCode, mainMember, memberNumber, patient, scheme, status: status2, plan } = membership;
  const classes = createCoverageClasses(plan);
  return {
    resourceType: "Coverage",
    status: status2,
    class: classes,
    beneficiary: patient,
    subscriber: mainMember,
    payor: [scheme],
    subscriberId: memberNumber,
    dependent: dependantCode
  };
};
var mapCoverageToPatientMembership = (coverage) => {
  const { status: status2, beneficiary, subscriber, payor, subscriberId, dependent } = coverage;
  return {
    status: status2,
    dependantCode: dependent,
    memberNumber: subscriberId,
    mainMember: getValidReference(subscriber),
    patient: getValidReference(beneficiary),
    scheme: getValidReference(first8(payor)),
    plan: getPlan(coverage)?.value
  };
};
var mapCoverageToPatientMembershipWithId = createMapWithId(mapCoverageToPatientMembership);
var mapCoverageToIdentifiers = (coverage, scheme) => {
  const { dependantCode, memberNumber } = mapCoverageToPatientMembership(coverage);
  const { general } = mapFhirToModelOrganization(scheme);
  const isInsuranceOrg = isGeneralInsurance(general);
  if (!isInsuranceOrg) {
    return void 0;
  }
  const { schemeCode } = general;
  const hasAllIdentifiers = schemeCode && dependantCode && memberNumber;
  if (!hasAllIdentifiers) {
    return void 0;
  }
  return {
    dependantCode,
    schemeCode,
    subscriberId: memberNumber
  };
};
var getMedicalAidIdentifiers = (membership) => {
  const { medicalAid, dependantCode } = membership.medicalAidMembership || {};
  const { membershipNumber, plan } = medicalAid || {};
  const hasMedicalAidIdentifiers = dependantCode && plan && membershipNumber;
  if (!hasMedicalAidIdentifiers) {
    return void 0;
  }
  return {
    dependantCode,
    plan,
    memberNumber: membershipNumber
  };
};

// src/device/mapping.ts
import {
  IdentifierCode as IdentifierCode7,
  isDevicePatch,
  createCodeableConcept as createCodeableConcept11,
  getCodes as getCodes2,
  createIdentifier as createIdentifier6
} from "@quromedical/fhir-common";
import { first as first9 } from "@quromedical/utils";

// src/device/schema.ts
import { DeviceCode } from "@quromedical/fhir-common";
import { z as z23 } from "zod";
var deviceStatus = ["active", "inactive", "entered-in-error", "unknown"];
var DeviceStatusSchema = z23.enum(deviceStatus).describe("The status of the device");
var DeviceCodeSchema = z23.nativeEnum(DeviceCode).describe("The code of the device");
var RelayNameRegex = /^((samsung-a(12|13|14|15|16|24))-[0-9]{1,3}|friendlyelec-nanopim4b-[0-9]{1,3})$/;
var RelayNameSchema = z23.string().regex(RelayNameRegex).describe("The name of the relay device");
var PatchNameRegex = /^VC2B008BF_[0-9-A-Z]{6}$/;
var PatchNameSchema = z23.string().regex(PatchNameRegex).describe("The name of the patch device");
var BandNameRegex = /^[0-9]{7}$/;
var BandNameSchema = z23.string().regex(BandNameRegex).describe("The name of the vital band device");
var GlucoseDeviceNameRegex = /^(F).{11}$/;
var GlucoseDeviceNameSchema = z23.string().regex(GlucoseDeviceNameRegex).describe("The name of the glucose device");
var BloodPressureSchema = z23.string().length(5).describe("The name of the blood pressure device");
var patchNameError = "A patch name should start with VC2B008BF_ and have a total of 16 characters.";
var bandNameError = "A band name should have 7 digits.";
var relayNameError = "A relay name should start with samsung-a12, samsung-a13, samsung-a14, samsung-a24 or friendlyelec-nanopim4b and end with 1, 2 or 3 digits.";
var glucoseNameError = "A glucose name should start with F and 11 characters long. ";
var DeviceSchemaMap = {
  [DeviceCode.relay]: {
    schema: RelayNameSchema,
    message: relayNameError
  },
  [DeviceCode.patch]: {
    schema: PatchNameSchema,
    message: patchNameError
  },
  [DeviceCode.band]: {
    schema: BandNameSchema,
    message: bandNameError
  },
  [DeviceCode.glucose]: {
    schema: GlucoseDeviceNameSchema,
    message: glucoseNameError
  },
  [DeviceCode.bloodPressure]: {
    schema: BloodPressureSchema
  }
};
var BaseDeviceSchema = z23.object({
  name: z23.string(),
  type: DeviceCodeSchema,
  status: DeviceStatusSchema,
  serialNumber: z23.string().optional()
});
var deviceNameRefinement = (obj, ctx) => {
  if (!obj.name && !obj.type) {
    return;
  }
  if (obj.type === DeviceCode.relay) {
    if (!obj.serialNumber) {
      ctx.addIssue({
        code: "custom",
        message: "Serial number is required",
        path: ["serialNumber"]
      });
      return;
    }
  }
  if (!obj.type || !obj.name) {
    ctx.addIssue({
      code: "custom",
      message: "You must provide both name and type",
      path: ["name"]
    });
    return;
  }
  const name = obj.name.replace(/\\[ntr"]/g, "");
  const { schema, message } = DeviceSchemaMap[obj.type];
  const parsed = schema.safeParse(name);
  if (!parsed.success) {
    ctx.addIssue({
      code: "custom",
      message: message || parsed.error.message,
      path: ["name"]
    });
  }
};
var CreateDeviceSchema = BaseDeviceSchema.superRefine(deviceNameRefinement);
var CreateBulkDevicesSchema = z23.object({
  devices: CreateDeviceSchema.array()
});
var CreateBulkDevicesResponseSchema = CreateDeviceSchema.array();
var DeviceSchema = BaseDeviceSchema.extend({
  id: UUIDSchema
});
var DeviceGetByIdSchema = DeviceSchema.optional();
var UpdateDeviceSchema = z23.object({
  id: UUIDSchema,
  name: z23.string().optional(),
  type: DeviceCodeSchema.optional(),
  status: DeviceStatusSchema.optional(),
  serialNumber: z23.string().optional()
}).superRefine(deviceNameRefinement);
var DeviceGetAllResponseSchema = PaginatedBundleSchema.extend({
  devices: DeviceSchema.array()
});
var DeviceSearchSchema = PaginationSchema.extend({
  id: z23.string().optional().describe("The id of the devices"),
  sort: z23.string().optional().describe("The field to sort by"),
  "device-name": z23.string().optional().describe("The name of the device"),
  type: z23.nativeEnum(DeviceCode).optional().describe("The type of the device"),
  identifier: z23.nativeEnum(DeviceCode).optional().describe("The type of the device"),
  status: z23.string().optional().describe("The status of the device"),
  serialNumber: z23.string().optional().describe("The serial number of the device")
});
var patchCalibrateRequestSchema = z23.object({
  id: UUIDSchema,
  posture: PatchPostureCalibrateValueSchema.optional(),
  temperature: z23.number().optional()
});
var PatchCalibrateResponseSchema = z23.object({});

// src/device/mapping.ts
var makeStatusInactive = (device) => {
  const status2 = "inactive";
  const result = {
    ...device,
    status: status2
  };
  return result;
};
var makePatchDevicesInactive = (devices) => {
  const patchDevices = devices.filter((device) => isDevicePatch(device));
  const inactivePatchDevices = patchDevices.map(makeStatusInactive);
  return inactivePatchDevices;
};
var mapDeviceToNameAndId = (device) => ({
  id: device.id,
  name: device.deviceName && device.deviceName[0].name || void 0
});
var mapUserFriendlyNameToDeviceName = (userFriendlyName) => [
  {
    name: userFriendlyName,
    type: "user-friendly-name"
  }
];
var mapDeviceNameTopUserFriendlyName = (deviceName) => first9(deviceName)?.name;
var mapDeviceCreateModelToFhir = (device) => ({
  resourceType: "Device",
  identifier: [createIdentifier6(IdentifierCode7.deviceType, device.type)],
  deviceName: mapUserFriendlyNameToDeviceName(device.name),
  status: device.status,
  serialNumber: device.serialNumber,
  type: createCodeableConcept11(device.type)
});
var mapDeviceToFhir = (device) => {
  const base = mapDeviceCreateModelToFhir(device);
  return {
    id: device.id,
    ...base
  };
};
var mapFhirToDevice = (device) => {
  const item = {
    id: device.id,
    name: mapDeviceNameTopUserFriendlyName(device.deviceName),
    status: device.status,
    serialNumber: device.serialNumber,
    type: first9(getCodes2(device.type))
  };
  const parsed = DeviceSchema.safeParse(item);
  if (!parsed.success) {
    return void 0;
  }
  return parsed.data;
};

// src/resource-note/schema.ts
import { z as z24 } from "zod";
var ResourceNoteSchema = z24.object({
  author: DisplayReferenceSchema,
  text: z24.string(),
  time: z24.string()
});

// src/resource-note/mapping.ts
var addResourceNote = (resourceNotes, newNote) => resourceNotes ? [...resourceNotes, mapResourceNoteToFhir(newNote)] : [mapResourceNoteToFhir(newNote)];
var mapResourceNoteToFhir = (note) => ({
  text: note.text,
  authorReference: note.author,
  time: note.time
});
var mapFhirToResourceNote = (annotation) => {
  const { authorReference, text, time } = annotation;
  const result = ResourceNoteSchema.safeParse({
    author: authorReference,
    text,
    time: time ? time : void 0
  });
  if (!result.success) {
    return;
  }
  return result.data;
};

// src/value-set/schema.ts
import { z as z25 } from "zod";
var ValueSetSearchSchema = PaginationSchema.extend({
  search: z25.string().optional(),
  isChronic: z25.boolean().optional(),
  include: z25.string().optional()
});
var SelectOptionSchema = z25.object({
  value: z25.string(),
  display: z25.string()
});

// src/value-set/mapping.ts
import { medpraxSystem as medpraxSystem4 } from "@quromedical/fhir-common";
var mapToValueSetComposeIncludeConcept = (icd10Code) => {
  const valueSetComposeIncludeConcept = {
    code: icd10Code.Code,
    display: icd10Code.Description
  };
  return valueSetComposeIncludeConcept;
};
var mapToValueSet = (valueSetComposeIncludeConcept) => {
  const valueSetComposeInclude = {
    system: medpraxSystem4,
    concept: valueSetComposeIncludeConcept,
    version: "10"
  };
  const valueSet = {
    resourceType: "ValueSet",
    status: "active",
    name: "ICD-10",
    compose: {
      include: [valueSetComposeInclude]
    }
  };
  return valueSet;
};

// src/practitioner/schema.ts
import { z as z26 } from "zod";
var PractitionerQualificationCodeSchema = z26.object({
  code: DisplayCodeableConceptSchema,
  issuer: DisplayReferenceSchema.optional(),
  period: PeriodSchema.optional()
});
var QualificationFieldsSchema = z26.object({
  qualificationCode: PractitionerQualificationCodeSchema,
  registrationNumber: z26.string()
});
var CreateQualificationFieldsSchema = z26.object({
  qualificationCode: z26.string(),
  registrationNumber: z26.string()
});
var PractitionerQualificationUpdateRequestSchema = CreateQualificationFieldsSchema.extend({
  id: UUIDSchema
});
var PractitionerExternalSchema = z26.object({
  ampathMnemonic: z26.string().optional()
});
var UpdatePractitionerExternalSchema = PractitionerExternalSchema.extend({
  id: UUIDSchema
});
var PractitionerSettingsSchema = z26.object({
  canPrescribe: z26.boolean().optional()
});
var UpdatePractitionerSettingsSchema = PractitionerSettingsSchema.extend({
  id: UUIDSchema
});
var PractitionerUpdateStatusSchema = z26.object({
  id: UUIDSchema,
  active: z26.boolean()
});
var PractitionerGeneralSchema = GeneralSchema.omit({
  birthDate: true,
  identifierCode: true,
  identifierValue: true
});
var PractitionerSchema = z26.object({
  qualification: z26.object({
    qualificationCode: z26.string(),
    registrationNumber: z26.string()
  }),
  contact: ContactSchema,
  general: PractitionerGeneralSchema,
  active: z26.boolean(),
  groups: DisplayReferenceSchema.array(),
  organizations: DisplayReferenceSchema.array(),
  external: PractitionerExternalSchema.optional(),
  settings: PractitionerSettingsSchema.optional()
});
var PartialPractitionerSchema = z26.object({
  qualification: QualificationFieldsSchema.partial(),
  contact: ContactGetSchema.partial(),
  general: PractitionerGeneralSchema.partial(),
  active: z26.boolean(),
  external: PractitionerExternalSchema.optional(),
  settings: PractitionerSettingsSchema.optional()
});
var PractitionerWithScopesSchema = PartialPractitionerSchema.extend({
  groups: DisplayReferenceSchema.array(),
  organizations: DisplayReferenceSchema.array(),
  assignedPatients: DisplayReferenceSchema.array()
});
var PractitionerGetOneResponseSchema = PractitionerWithScopesSchema.extend({
  id: UUIDSchema
});
var PartialPractitionerWithIdSchema = PartialPractitionerSchema.extend({
  id: UUIDSchema
});
var PractitionerGetResponseSchema = PartialPractitionerWithIdSchema;
var PractitionerContactUpdateRequestSchema = ContactSchema.extend({
  id: UUIDSchema
});
var PractitionerGeneralUpdateRequestSchema = PractitionerGeneralSchema.extend({
  id: UUIDSchema
});
var PractitionerGetAllResponseSchema = PaginatedBundleSchema.extend({
  practitioners: PractitionerGetResponseSchema.array()
});
var PractitionerSearchSchema = PaginationSchema.extend({
  sort: z26.string().optional().describe("The field to sort by"),
  name: z26.string().optional().describe("The name of the practitioner"),
  email: z26.string().optional().describe("The email of the practitioner"),
  id: z26.string().optional().describe("A single practitioner id or Fhir Id Query string")
});
var CreateInitialGroupSchema = z26.object({
  group: z26.string()
});
var ExternalSchema = z26.object({
  ampathMnemonic: z26.string().optional()
});
var SettingsSchema = z26.object({
  canPrescribe: z26.boolean().optional()
});
var PractitionerCreateSchema = z26.object({
  qualification: CreateQualificationFieldsSchema,
  contact: ContactSchema,
  general: PractitionerGeneralSchema,
  initialGroup: CreateInitialGroupSchema,
  external: ExternalSchema.optional(),
  settings: SettingsSchema.optional()
});

// src/practitioner/mapping.ts
import {
  IdentifierCode as IdentifierCode8,
  getIdentifier as getIdentifier3,
  valsOrNone as valsOrNone9,
  createFullHumanName as createFullHumanName2,
  createExtensionValue as createExtensionValue5,
  ExtensionUrl as ExtensionUrl6,
  findExtensionValue as findExtensionValue5,
  updateExtensions as updateExtensions2
} from "@quromedical/fhir-common";
import { compact as compact10, first as first10 } from "@quromedical/utils";
var isRequiredPractitionerQualification = (coding) => isRequiredCoding(first10(coding.code.coding));
var createCanPrescribeExtension = createExtensionValue5(ExtensionUrl6.medicationRequestPrescriber);
var isPrescriber = findExtensionValue5(ExtensionUrl6.medicationRequestPrescriber);
var mapFhirToQualificationModel = (fhir) => {
  const registrationNumber = getIdentifier3(IdentifierCode8.registrationNumber, fhir.identifier);
  const qualificationCode = fhir.qualification?.find(isRequiredPractitionerQualification);
  return { registrationNumber, qualificationCode };
};
var mapFhirToExternalModel = (fhir) => {
  const ampathMnemonic = getIdentifier3(IdentifierCode8.ampathMnemonic, fhir.identifier);
  return { ampathMnemonic };
};
var mapFhirToSettingsModel = (fhir) => {
  const canPrescribe = isPrescriber(fhir.extension);
  return { canPrescribe };
};
var mapPractitionerGeneral = (fhir) => {
  const generalModel = mapFhirPersonToGeneralModel(fhir);
  const { familyName, givenName, gender, prefixName } = generalModel;
  return { familyName, givenName, gender, prefixName };
};
var mapFhirToModelPractitioner = (fhir) => {
  const contact = mapFhirPersonToContactModel(fhir);
  const general = mapPractitionerGeneral(fhir);
  const qualification = mapFhirToQualificationModel(fhir);
  const active = fhir.active || false;
  const external = mapFhirToExternalModel(fhir);
  const settings = mapFhirToSettingsModel(fhir);
  return {
    qualification,
    contact,
    general,
    active,
    external,
    settings
  };
};
var createPractitionerQualification = (qualification) => ({ code: qualification });
var getQualificationCode = (qualification) => valsOrNone9(compact10([qualification.qualificationCode]));
var fhirGeneral = (general) => {
  const name = createFullHumanName2({
    given: general.givenName,
    family: general.familyName,
    prefix: general.prefixName,
    use: "official"
  });
  const { gender } = general;
  return {
    name,
    gender
  };
};
var mapModelToFhirPractitioner = (practitioner) => {
  const { contact, general, qualification, active, settings = {} } = practitioner;
  const { canPrescribe } = settings;
  const qualificationCode = getQualificationCode(qualification);
  const extensionList = [
    canPrescribe !== void 0 ? createCanPrescribeExtension(canPrescribe) : void 0
  ];
  const fhir = {
    ...mapPersonContactToFhirPartial(contact),
    ...fhirGeneral(general),
    resourceType: "Practitioner",
    qualification: qualificationCode,
    active,
    extension: compact10(extensionList)
  };
  const registrationNumber = createOptionalIdentifier(
    IdentifierCode8.registrationNumber,
    practitioner.qualification.registrationNumber
  );
  const ampathMnemonic = createOptionalIdentifier(
    IdentifierCode8.ampathMnemonic,
    practitioner.external?.ampathMnemonic
  );
  const withRegisterNumber = attachIdentifiers(fhir, registrationNumber, ampathMnemonic);
  return withRegisterNumber;
};
var mapFhirToModelPractitionerWithId = (fhir) => {
  const practitioner = mapFhirToModelPractitioner(fhir);
  return {
    ...practitioner,
    id: fhir.id
  };
};
var mapFhirToModelOnePractitionerWithId = (fhir, groups, organizations, assignedPatients) => {
  const practitioner = mapFhirToModelPractitioner(fhir);
  return {
    ...practitioner,
    id: fhir.id,
    groups,
    organizations,
    assignedPatients
  };
};
var mapUpdateGeneralModelToFhirPractitioner = (fhir, form) => {
  const modelPractitioner = mapFhirToModelPractitioner(fhir);
  const { contact, qualification, active, external, settings } = modelPractitioner;
  const fhirPractitioner = mapModelToFhirPractitioner({
    contact,
    general: form,
    qualification,
    external,
    active,
    settings
  });
  return { ...fhirPractitioner, id: fhir.id };
};
var mapUpdateContactModelToFhirPractitioner = (fhir, form) => {
  const modelPractitioner = mapFhirToModelPractitioner(fhir);
  const { general, qualification, active, external, settings } = modelPractitioner;
  const fhirPractitioner = mapModelToFhirPractitioner({
    contact: form,
    general,
    qualification,
    external,
    active,
    settings
  });
  return { ...fhirPractitioner, id: fhir.id };
};
var mapUpdateQualificationModelToFhirPractitioner = (fhir, form) => {
  const modelPractitioner = mapFhirToModelPractitioner(fhir);
  const { contact, general, active, external, settings } = modelPractitioner;
  const fhirPractitioner = mapModelToFhirPractitioner({
    contact,
    general,
    qualification: form,
    external,
    active,
    settings
  });
  return { ...fhirPractitioner, id: fhir.id };
};
var updatePractitionerExternal = (fhir, form) => {
  const modelPractitioner = mapFhirToModelPractitioner(fhir);
  const fhirPractitioner = mapModelToFhirPractitioner({
    ...modelPractitioner,
    external: form
  });
  return { ...fhirPractitioner, id: fhir.id };
};
var mapUpdatePractitionerSettings = (fhir, form) => {
  const { canPrescribe } = form;
  const canPrescribeExtension = canPrescribe !== void 0 ? createCanPrescribeExtension(canPrescribe) : void 0;
  const extension = updateExtensions2(
    ExtensionUrl6.medicationRequestPrescriber,
    fhir.extension,
    compact10([canPrescribeExtension])
  );
  return { ...fhir, extension };
};

// src/practitioner/practitioner-qualification.ts
import { createCodeableConcept as createCodeableConcept12, medpraxSystem as medpraxSystem5 } from "@quromedical/fhir-common";
var mapMedpraxSpecialtyToPractitionerQualification = (specialty) => {
  const codeableConcept = createCodeableConcept12(
    specialty.Code,
    specialty.Description,
    specialty.Description,
    medpraxSystem5
  );
  const qualification = {
    code: codeableConcept
  };
  return qualification;
};

// src/practitioner-role/schema.ts
import { z as z27 } from "zod";
var PractitionerRoleSchema = z27.object({
  id: UUIDSchema,
  active: z27.boolean(),
  locations: ReferenceViewSchema.array().min(1),
  organization: ReferenceViewSchema,
  period: PeriodSchema,
  practitioner: ReferenceViewSchema
});
var GetPractitionerRolesInputSchema = z27.object({
  practitioner: UUIDSearchSchema.optional(),
  organization: UUIDSearchSchema.optional(),
  location: UUIDSearchSchema.optional()
});
var GetPractitionerRolesByPractitionerInputSchema = z27.object({
  id: UUIDSchema,
  organization: BaseUUIDSearchSchema.optional(),
  location: BaseUUIDSearchSchema.optional()
});
var GetPractitionerRolesResponseSchema = createPaginatedResponse(PractitionerRoleSchema);

// src/practitioner-role/mappings.ts
import { createReference as createReference3, createOptionalFhirDate } from "@quromedical/fhir-common";

// src/domain/mappings.ts
import { getReferenceId as getReferenceId4, getReferenceType as getReferenceType2 } from "@quromedical/fhir-common";
var mapReferenceToViewModel = (ref) => {
  if (!ref) {
    return void 0;
  }
  const type = getReferenceType2(ref);
  const d = {
    id: getReferenceId4(ref),
    type,
    name: ref.display || ""
  };
  const parsed = ReferenceViewSchema.safeParse(d);
  if (!parsed.success) {
    return void 0;
  }
  return parsed.data;
};

// src/practitioner-role/mappings.ts
var mapPractitionerRoleToFhir = (item) => {
  return {
    resourceType: "PractitionerRole",
    id: item.id,
    active: item.active,
    location: item.locations.map(
      (location) => createReference3("Location", location.id, location.name)
    ),
    organization: createReference3("Organization", item.organization.id, item.organization.name),
    period: {
      start: createOptionalFhirDate(item.period.start),
      end: createOptionalFhirDate(item.period.end)
    },
    practitioner: createReference3("Practitioner", item.practitioner.id, item.practitioner.name)
  };
};
var mapFhirToPractitionerRole = (items) => {
  const data = {
    id: items.id,
    active: items.active,
    locations: items.location?.map(mapReferenceToViewModel),
    organization: mapReferenceToViewModel(items.organization),
    period: {
      start: items.period?.start,
      end: items.period?.end
    },
    practitioner: mapReferenceToViewModel(items.practitioner)
  };
  const parsed = PractitionerRoleSchema.safeParse(data);
  if (!parsed.success) {
    throw parsed.error;
  }
  return parsed.data;
};

// src/group/schema.ts
import { GroupCode, GroupSecurity } from "@quromedical/fhir-common";
import { z as z28 } from "zod";
var GroupAddMembersResponseSchema = z28.object({
  members: z28.array(DisplayReferenceSchema)
});
var GroupUpdateMemberRequestSchema = z28.object({
  id: UUIDSchema,
  members: z28.array(UUIDSchema),
  active: z28.boolean()
});
var GroupTypeSchema = z28.nativeEnum(GroupCode);
var GroupSecuritySchema = z28.nativeEnum(GroupSecurity);
var GroupSearchSchema = PaginationSchema.extend({
  code: GroupTypeSchema.optional()
});
var MemberSchema = z28.object({
  id: UUIDSchema,
  active: z28.boolean()
});
var GroupSchema = z28.object({
  active: z28.boolean(),
  members: MemberSchema.array(),
  name: z28.string(),
  type: GroupTypeSchema,
  organization: DisplayReferenceSchema,
  permissions: GroupSecuritySchema.array()
});
var GroupGetResponseSchema = GroupSchema.partial().extend({
  id: UUIDSchema
});
var GroupGetAllResponseSchema = z28.object({
  groups: GroupGetResponseSchema.array()
});
var GroupCreateRequestSchema = GroupSchema.omit({
  organization: true,
  members: true
}).extend({
  organization: UUIDSchema
});
var GroupUpdateRequestSchema = GroupCreateRequestSchema.extend({
  id: UUIDSchema
});

// src/group/mapping.ts
import {
  GroupSecurity as GroupSecurity2,
  GroupCode as GroupCode2,
  createReference as createReference4,
  valsOrNone as valsOrNone10,
  getReferenceId as getReferenceId5
} from "@quromedical/fhir-common";
import * as quro from "@quromedical/fhir-common";
import { compact as compact11 } from "@quromedical/utils";
var securityCodes = Object.values(GroupSecurity2);
var groupCodes = Object.values(GroupCode2);
var getGroupCodes = (group) => {
  const coding = group.code?.coding || [];
  const codes = coding.map((c) => c.code);
  const filteredCodes = compact11(codes).filter((c) => {
    const hasCode = securityCodes.includes(c);
    return hasCode;
  });
  return filteredCodes;
};
var getGroupType = (fhir) => {
  const coding = fhir.code?.coding;
  if (!coding) {
    return void 0;
  }
  const foundCoding = coding.find(({ code }) => {
    if (!code) {
      return false;
    }
    return groupCodes.includes(code);
  });
  return foundCoding?.code;
};
var mapFhirToModelGroupMembers = (groupMember = []) => {
  const memberships = groupMember.map(({ entity, inactive }) => {
    const id = getReferenceId5(entity);
    if (!id) {
      return void 0;
    }
    return {
      id,
      active: !inactive
    };
  });
  return compact11(memberships);
};
var mapGroupToFhirMembers = (groupMembers = []) => {
  return groupMembers.map(({ id, active }) => ({
    entity: createReference4("Practitioner", id),
    inactive: !active
  }));
};
var createCoding4 = (code) => quro.createCoding(code);
var createGroupType = (type, permissions) => {
  const jointCodes = [...permissions, type];
  const coding = jointCodes.map(createCoding4);
  return { coding };
};
var mapGroupToFhir = (form) => {
  const { permissions, name, active, organization, type, members } = form;
  const code = createGroupType(type, permissions);
  const member = mapGroupToFhirMembers(members);
  return {
    name,
    resourceType: "Group",
    actual: true,
    type: "practitioner",
    active,
    code,
    managingEntity: organization,
    member
  };
};
var mapCreateModelToFhirGroup = (createModel) => {
  return mapGroupToFhir({
    ...createModel,
    members: [],
    organization: createReference4("Organization", createModel.organization)
  });
};
var mapUpdateModelToFhirGroup = (fhir, form) => {
  const members = mapFhirToModelGroupMembers(fhir.member);
  const group = mapGroupToFhir({
    ...form,
    members,
    organization: createReference4("Organization", form.organization)
  });
  return {
    ...group,
    id: fhir.id
  };
};
var mapFhirToModelGroup = (fhir) => {
  const { name, active, managingEntity, member } = fhir;
  const codes = getGroupCodes(fhir);
  const organization = getValidReference(managingEntity);
  const type = getGroupType(fhir);
  const members = mapFhirToModelGroupMembers(member);
  return {
    name,
    active,
    permissions: codes,
    type,
    organization,
    members
  };
};
var mapFhirToModelGroupWithId = (fhir) => {
  const group = mapFhirToModelGroup(fhir);
  return {
    ...group,
    id: fhir.id
  };
};
var createGroupMember = (active, entity) => ({
  inactive: !active,
  entity
});
var isMemberInList = (member, members = []) => {
  const found = members.find((m) => m.reference !== member.entity.reference);
  return !!found;
};
var updateGroupMembers = (group, areMembersActive, membersToUpdate) => {
  const groupMembers = group.member || [];
  const existingGroupMembers = groupMembers.filter(
    (groupMember) => isMemberInList(groupMember, membersToUpdate)
  );
  const updatedGroupMembers = membersToUpdate.map((ref) => createGroupMember(areMembersActive, ref));
  const mergedMembers = valsOrNone10([...updatedGroupMembers, ...existingGroupMembers]);
  return {
    ...group,
    member: mergedMembers
  };
};

// src/organization/schema.ts
import { OrganizationCode as OrganizationCode2 } from "@quromedical/fhir-common";
import { AdministratorSchema as AdministratorSchema2 } from "@quromedical/healthbridge";
import { z as z29 } from "zod";
var OrganizationTypeSchema = z29.nativeEnum(OrganizationCode2);
var OrganizationGeneralBaseSchema = z29.object({
  name: z29.string(),
  type: OrganizationTypeSchema,
  schemeCode: z29.string().optional()
});
var OrganizationGeneralSchema = OrganizationGeneralBaseSchema;
var OrganizationGeneralUpdateSchema = OrganizationGeneralBaseSchema.extend({
  id: UUIDSchema,
  schemeCode: z29.string().optional()
});
var OrganizationSchema = z29.object({
  contact: ContactSchema,
  general: OrganizationGeneralSchema
});
var PartialOrganizationSchema = z29.object({
  contact: ContactGetSchema.partial(),
  general: OrganizationGeneralBaseSchema.partial()
});
var InsuranceOrganizationSchema = AdministratorSchema2.extend({
  id: UUIDSchema
});
var OrganizationGetResponseSchema = PartialOrganizationSchema.extend({
  id: UUIDSchema
});
var OrganizationGetAllResponseSchema = PaginatedBundleSchema.extend({
  organizations: OrganizationGetResponseSchema.array()
});
var OrganizationBaseSearchSchema = PaginationSchema.extend({
  name: z29.string().optional().describe("The name of the organization"),
  address: z29.string().optional().describe("Organization address")
});
var OrganizationSearchSchema = OrganizationBaseSearchSchema.extend({
  type: z29.string().optional().describe("The type of organization, separated by a comma")
});
var OrganizationGetAllInsuranceParamsSchema = OrganizationBaseSearchSchema;
var OrganizationGetAllInsurancesResponseSchema = PaginatedBundleSchema.extend({
  organizations: InsuranceOrganizationSchema.array()
});

// src/user-preference/schema.ts
import { z as z30 } from "zod";
var Themes = ["light", "dark", "contrast-dark", "system"];
var ThemeSchema = z30.enum(Themes);
var BaseItemSchema = z30.object({
  pushTokens: z30.array(z30.string()).optional(),
  theme: ThemeSchema.optional(),
  wards: z30.array(z30.string())
});
var UserPreferenceItemSchema = BaseItemSchema.extend({
  pk: z30.string()
});
var PreferenceItemSchema = BaseItemSchema.extend({
  userReference: z30.string().describe("This field is deprecated, use pk instead")
});
var UserPreferenceWardSchema = z30.object({
  id: UUIDSchema,
  name: z30.string()
});

// src/user/schema.ts
import { GroupSecurity as GroupSecurity3 } from "@quromedical/fhir-common";
import { z as z31 } from "zod";
var PreferenceSchema = z31.object({
  theme: ThemeSchema
  // TODO: Uncomment when preferences are deleted on fhir gateway and insight is updated
  // wards: WardSchema.array().optional(),
});
var UpdateHospitalSettingsSchema = z31.object({
  wards: UUIDSchema.array().min(1)
});
var PermissionSchema = z31.nativeEnum(GroupSecurity3);
var ProfileSchema = z31.object({
  id: UUIDSchema,
  permissions: PermissionSchema.array(),
  groups: DisplayReferenceSchema.array(),
  organizations: DisplayReferenceSchema.array(),
  preference: PreferenceSchema.partial()
});
var UserSchema = z31.object({
  practitioner: PartialPractitionerSchema,
  preference: PreferenceSchema.partial(),
  groups: DisplayReferenceSchema.array(),
  organizations: DisplayReferenceSchema.array(),
  permissions: PermissionSchema.array(),
  wards: UserPreferenceWardSchema.array()
});
var UserWithIdSchema = UserSchema.extend({
  id: UUIDSchema
});

// src/user/validation.ts
import * as yup6 from "yup";
var themes = ["dark", "light", "system", "contrast-dark"];
var preferenceUpdateSchema = yup6.object({
  theme: yup6.string().oneOf(themes).required()
});

// src/next-of-kin/validation.ts
import { IdentifierCode as IdentifierCode9 } from "@quromedical/fhir-common";
import * as yup7 from "yup";
var identifierCodeSchema2 = yup7.string().oneOf([
  IdentifierCode9.southAfricanIdNumber,
  IdentifierCode9.passportNumber,
  IdentifierCode9.namibianIdNumber
]).optional();
var nextOfKinGeneralSchema = yup7.object({
  ...personGeneralFieldSchemas,
  identifierCode: identifierCodeSchema2,
  identifierValue: yup7.string().optional(),
  birthDate: yup7.string().optional()
});
var nextOfKinContactSchema = yup7.object({
  ...personContactFieldSchemas,
  telecomEmail: yup7.string().lowercase().email().optional()
});
var nextOfKinSchema = yup7.object({
  contact: nextOfKinContactSchema,
  general: nextOfKinGeneralSchema
});

// src/medication/mapping.ts
var getMedicationDisplay = (medication) => {
  if (medication.FullDescription) {
    return medication.FullDescription;
  }
  if (medication.StrengthMetric1) {
    return `${medication.Name} ${medication.StrengthMetric1} ${medication.UnitOfMeasure1}`;
  }
  if (medication.VolumeMetric2) {
    return `${medication.Name} ${medication.VolumeMetric2} ${medication.UnitOfMeasure2}`;
  }
  if (medication.VolumeMetric3) {
    return `${medication.Name} ${medication.VolumeMetric3} ${medication.UnitOfMeasure3}`;
  }
  return medication.Name;
};
var createMeasure = (unit, value) => {
  if (!(unit && value)) {
    return void 0;
  }
  return { unit, value };
};
var mapMedpraxToMedicationModel = (med) => {
  return {
    code: med.Code,
    name: med.Name,
    nappiCode: med.NappiCodeFirstSixDigits,
    schedule: med.Schedule,
    dispensingVolumePacksize: med.DispensingVolumePacksize,
    strength: createMeasure(med.UnitOfMeasure1, med.StrengthMetric1),
    volume: createMeasure(med.UnitOfMeasure2, med.VolumeMetric2),
    individualPackSize: createMeasure(med.UnitOfMeasure3, med.VolumeMetric3),
    // PresentationCode is always defined
    route: med.PresentationCode
  };
};
var mapMedpraxConsumableModel = (med) => ({
  medpraxCode: med.Code,
  name: getMedicationDisplay(med),
  nappiCode: med.NappiCodeFirstSixDigits
});

// src/task/validation.ts
import { TaskCode, isValidReference as isValidReference4 } from "@quromedical/fhir-common";
import * as yup8 from "yup";
var statuses = [
  "draft",
  "requested",
  "received",
  "accepted",
  "rejected",
  "ready",
  "cancelled",
  "in-progress",
  "on-hold",
  "failed",
  "completed",
  "entered-in-error"
];
var updateTaskStatusSchema = yup8.object({
  note: yup8.string().required(),
  status: yup8.string().equals(statuses).required()
});
var optionalReferenceSchema = yup8.object().default(void 0).test({
  name: "is-value-reference",
  message: (d) => `${d.path} is not a valid reference`,
  test: (value) => value ? isValidReference4(value) : true
});
var requiredReferenceSchema = yup8.object().test({
  name: "is-value-reference",
  message: (d) => `${d.path} is not a valid reference`,
  test: isValidReference4
});
var assignTaskSchema = yup8.object({
  code: yup8.string().required().equals(Object.values(TaskCode)),
  description: yup8.string().required(),
  performer: requiredReferenceSchema,
  requestedPeriodEnd: yup8.string().optional()
});
var createTaskAssignmentSchema = yup8.object({
  ...assignTaskSchema.fields,
  focus: optionalReferenceSchema
});

// src/task/mapping.ts
import {
  createCodeableConcept as createCodeableConcept13,
  createMapWithId as createMapWithId2,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts8,
  getIdQueryString,
  mapFhirToModelPeriod,
  valsOrNone as valsOrNone11,
  ExtensionUrl as ExtensionUrl7,
  createExtensionValue as createExtensionValue6,
  findExtensionValue as findExtensionValue6,
  updateExtensions as updateExtensions3,
  filterExtensionValues,
  isValidReference as isValidReference5,
  createFhirDate as createFhirDate3
} from "@quromedical/fhir-common";
import { compact as compact12 } from "@quromedical/utils";

// src/task/schema.ts
import { TaskCode as TaskCode2, TaskReferenceType } from "@quromedical/fhir-common";
import { z as z32 } from "zod";
var TaskStatusSchema = [
  "draft",
  "requested",
  "received",
  "accepted",
  "rejected",
  "ready",
  "cancelled",
  "in-progress",
  "on-hold",
  "failed",
  "completed",
  "entered-in-error"
];
var TaskIntentSchema = [
  "unknown",
  "proposal",
  "plan",
  "order",
  "original-order",
  "reflex-order",
  "filler-order",
  "instance-order",
  "option"
];
var TaskNoteScheme = z32.object({
  author: DisplayReferenceSchema,
  text: z32.string(),
  time: z32.string()
});
var TaskReferenceSchema = z32.object({
  type: z32.nativeEnum(TaskReferenceType),
  value: DisplayReferenceSchema
});
var TaskSchema = z32.object({
  created: z32.string(),
  updated: z32.string(),
  status: z32.enum(TaskStatusSchema),
  requester: DisplayReferenceSchema,
  intent: z32.enum(TaskIntentSchema),
  description: z32.string(),
  code: z32.nativeEnum(TaskCode2),
  admission: DisplayReferenceSchema,
  period: PeriodSchema,
  requestedPeriod: PeriodSchema,
  input: TaskReferenceSchema.array(),
  output: TaskReferenceSchema.array(),
  focus: DisplayReferenceSchema,
  for: DisplayReferenceSchema,
  notes: TaskNoteScheme.array(),
  owner: DisplayReferenceSchema,
  performers: DisplayReferenceSchema.array()
});
var PartialTaskSchema = TaskSchema.partial().extend({
  status: z32.enum(TaskStatusSchema),
  intent: z32.enum(TaskIntentSchema),
  notes: TaskNoteScheme.array(),
  input: TaskReferenceSchema.array(),
  output: TaskReferenceSchema.array(),
  performers: DisplayReferenceSchema.array()
});
var PartialTaskWithIdSchema = PartialTaskSchema.extend({
  id: UUIDSchema
});
var taskCodeSchema = z32.nativeEnum(TaskCode2);
var TaskAssignmentSchema = z32.object({
  description: z32.string(),
  performer: DisplayReferenceSchema,
  code: taskCodeSchema,
  requestedPeriodEnd: z32.string().optional()
});
var TaskGetResponseSchema = PartialTaskWithIdSchema;
var TaskGetAllResponseSchema = PaginationSchema.extend({
  tasks: PartialTaskWithIdSchema.array()
});
var status = [
  "draft",
  "requested",
  "received",
  "accepted",
  "rejected",
  "ready",
  "cancelled",
  "in-progress",
  "on-hold",
  "failed",
  "completed",
  "entered-in-error"
];
var taskSearchStatus = [...status, "active", "inactive", "overdue"];
var statusSchema = z32.enum(status).describe("The state of the task");
var TaskSearchStatusSchema = z32.enum(taskSearchStatus).describe("The state of the task");
var BaseTaskSearchSchema = PaginationSchema.extend({
  code: z32.nativeEnum(TaskCode2).optional().describe("The type of task to search by"),
  encounter: z32.string().optional().describe("Reference to the Encounter Resource"),
  status: TaskSearchStatusSchema.optional()
});
var BaseTaskUpdateSchema = z32.object({
  id: z32.string(),
  status: statusSchema,
  dueDate: dateSchema,
  note: z32.string()
});
var TaskUpdateOverdueSchema = z32.object({
  id: z32.string(),
  dueDate: dateSchema,
  note: z32.string()
});
var TaskUpdateStatusSchema = z32.object({
  id: z32.string(),
  status: statusSchema,
  note: z32.string()
});
var TaskUpdateSchema = z32.object({
  id: z32.string(),
  status: z32.enum(status).describe("The state of the task").optional(),
  dueDate: dateSchema.optional(),
  note: z32.string()
});
var TaskCreateSchema = z32.object({
  patientId: z32.string().uuid(),
  description: z32.string(),
  performer: DisplayReferenceSchema,
  code: z32.nativeEnum(TaskCode2).optional().describe("The type of task to search by"),
  requestedPeriodEnd: z32.string().optional(),
  focus: DisplayReferenceSchema.optional()
});
var TaskSearchSchema = BaseTaskSearchSchema.extend({
  owner: z32.string().optional().describe("Reference(Practitioner | Organization)"),
  subject: z32.string().optional().describe("The subject of the task"),
  focus: z32.string().optional().describe("What task is acting on")
});
var PractitionerTaskSearchSchema = BaseTaskSearchSchema.extend({
  owner: z32.string().optional().describe("Reference(Practitioner | Organization)")
});
var PatientTaskSearchSchema = BaseTaskSearchSchema.extend({
  subject: z32.string().describe("The subject of the task"),
  focus: z32.string().optional().describe("What task is acting on")
});

// src/task/mapping.ts
var mapTaskReferenceToFhir = (taskReference) => ({
  type: createCodeableConcept13(taskReference.type),
  valueReference: taskReference.value
});
var mapFhirToTaskReference = (fhir) => {
  const value = getValidReference(fhir.valueReference);
  const type = getFirstCodeFromCodableConcepts8([fhir.type]);
  const hasData = value && type;
  if (!hasData) {
    return void 0;
  }
  return {
    type,
    value
  };
};
var createRequestedPeriodExtension = (requestedPeriod) => createExtensionValue6(ExtensionUrl7.TaskRequestedPeriod)(requestedPeriod);
var createRequestedPerformerExtension = (performers) => performers.map((performer) => {
  return createExtensionValue6(ExtensionUrl7.TaskRequestedPerformer)(performer);
});
var createExtensionValues = (extensions) => {
  const requestedPeriodExtension = extensions.requestedPeriod ? createRequestedPeriodExtension(extensions.requestedPeriod) : void 0;
  const requestedPerformerExtension = createRequestedPerformerExtension(extensions.performers);
  const result = requestedPeriodExtension ? [requestedPeriodExtension, ...requestedPerformerExtension] : requestedPerformerExtension;
  return result;
};
var mapTaskToFhir = (model) => {
  const {
    input,
    intent,
    notes,
    output,
    status: status2,
    admission,
    code,
    created,
    description,
    focus,
    for: taskFor,
    owner,
    period: period2,
    requester,
    updated,
    requestedPeriod,
    performers
  } = model;
  const extension = createExtensionValues({ requestedPeriod, performers });
  return {
    resourceType: "Task",
    intent,
    status: status2,
    authoredOn: created,
    lastModified: updated,
    description,
    encounter: admission,
    for: taskFor,
    focus,
    owner,
    executionPeriod: period2,
    requester,
    code: code ? createCodeableConcept13(code) : void 0,
    output: valsOrNone11(output.map(mapTaskReferenceToFhir)),
    input: valsOrNone11(input.map(mapTaskReferenceToFhir)),
    note: valsOrNone11(notes.map(mapResourceNoteToFhir)),
    extension
  };
};
var getRequestedPeriodExtensionValue = findExtensionValue6(ExtensionUrl7.TaskRequestedPeriod);
var getPerformersExtensionValue = filterExtensionValues(ExtensionUrl7.TaskRequestedPerformer);
var getValuesFromExtensions = (extensions) => {
  const performers = getPerformers(extensions).filter(isValidReference5);
  return {
    requestedPeriod: getRequestedPeriod(extensions),
    performers: performers ? performers : []
  };
};
var getRequestedPeriod = (extension) => mapFhirToModelPeriod(getRequestedPeriodExtensionValue(extension));
var getPerformers = (extension) => getPerformersExtensionValue(extension);
var mapFhirToTask = (fhir) => {
  const {
    intent,
    status: status2,
    authoredOn,
    lastModified,
    description,
    encounter,
    for: taskFor,
    focus,
    owner,
    executionPeriod,
    requester,
    code,
    output = [],
    input = [],
    note = [],
    extension
  } = fhir;
  const extensionValues = getValuesFromExtensions(extension);
  return {
    requestedPeriod: extensionValues.requestedPeriod,
    intent,
    status: status2,
    description,
    created: authoredOn,
    updated: lastModified,
    admission: getValidReference(encounter),
    for: getValidReference(taskFor),
    code: getFirstCodeFromCodableConcepts8(code ? [code] : void 0),
    focus: getValidReference(focus),
    owner: getValidReference(owner),
    period: mapFhirToModelPeriod(executionPeriod),
    requester: getValidReference(requester),
    notes: compact12(note.map(mapFhirToResourceNote)),
    input: compact12(input.map(mapFhirToTaskReference)),
    output: compact12(output.map(mapFhirToTaskReference)),
    performers: extensionValues.performers
  };
};
var mapFhirToTaskWithId = createMapWithId2(mapFhirToTask);
var mapTaskToFhirWithId = createMapWithId2(mapTaskToFhir);
var createPeriodExtension = (period2, existingExtensions) => updateExtensions3(ExtensionUrl7.TaskRequestedPeriod, existingExtensions, [
  createExtensionValue6(ExtensionUrl7.TaskRequestedPeriod)(period2)
]);
var customStatusToFhirMap = {
  active: ["requested", "received", "accepted", "ready", "in-progress", "on-hold", "failed"],
  inactive: ["completed", "draft", "rejected", "cancelled", "entered-in-error"],
  overdue: ["failed"]
};
var customStatuses = Object.keys(customStatusToFhirMap);
var isCustomTaskStatus = (status2) => customStatuses.includes(status2);
var getTaskStatusQuery = (status2) => {
  if (!status2) {
    return void 0;
  }
  if (!isCustomTaskStatus(status2)) {
    return status2;
  }
  return getIdQueryString(customStatusToFhirMap[status2]);
};
var updateFhirTaskRequestedPeriod = (dueDate, extension = [], authoredOn) => {
  const startDate = authoredOn ? new Date(authoredOn) : /* @__PURE__ */ new Date();
  const period2 = {
    start: createFhirDate3(startDate),
    end: createFhirDate3(dueDate)
  };
  return createPeriodExtension(period2, extension);
};
var updateFhirTaskStatus = (task, status2) => ({
  ...task,
  status: status2
});
var updateFhirTaskNote = (task, note) => ({
  ...task,
  note: task.note ? [...task.note, note] : [note]
});
var isTaskUpdateOverdue = (input) => {
  return TaskUpdateOverdueSchema.safeParse(input).success;
};
var isTaskUpdateStatus = (input) => {
  return TaskUpdateStatusSchema.safeParse(input).success;
};
var mapFhirTaskUpdate = (fhirTask, input) => {
  const task = Object.assign({}, fhirTask);
  if (isTaskUpdateStatus(input)) {
    task.status = input.status;
  }
  if (isTaskUpdateOverdue(input)) {
    task.extension = updateFhirTaskRequestedPeriod(
      input.dueDate,
      fhirTask.extension,
      fhirTask.authoredOn
    );
  }
  return task;
};

// src/claim/mapping.ts
import {
  createCodeableConcept as createCodeableConcept16,
  createCoding as createCoding5,
  createExtensionValue as createExtensionValue9,
  createIdentifier as createIdentifier7,
  Currency as Currency5,
  ExtensionUrl as ExtensionUrl10,
  findExtensionValue as findExtensionValue7,
  getDisplayFromCodableConcepts as getDisplayFromCodableConcepts3,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts11,
  getIdentifier as getIdentifier4,
  IdentifierCode as IdentifierCode10,
  valsOrNone as valsOrNone14
} from "@quromedical/fhir-common";
import {
  PlaceServiceCode
} from "@quromedical/healthbridge";
import { compact as compact15, first as first13, partial as partial3, sum as sum2 } from "@quromedical/utils";
import { z as z38 } from "zod";

// src/admission/validation.ts
import { EncounterClass as EncounterClass3, EncounterService as EncounterService3 } from "@quromedical/fhir-common";
import * as yup10 from "yup";

// src/observation/validation.ts
import * as yup9 from "yup";
var PregnancySchema = yup9.object({
  isPregnant: yup9.boolean().optional(),
  lactation: yup9.boolean().optional(),
  edd: yup9.string().optional()
});

// src/admission/validation.ts
var medicalAidAuthorizationCreateSchemaFields = {
  code: yup10.string().optional(),
  date: yup10.date().optional()
};
var medicalAidAuthorizationCreateSchema = yup10.object(
  medicalAidAuthorizationCreateSchemaFields
).optional().default(void 0);
var period = yup10.object({
  start: yup10.date().required(),
  end: yup10.date().optional()
});
var statusHistorySchema = yup10.object({
  status: yup10.string().required(),
  period
});
var diagnosisSchema = yup10.object({
  condition: yup10.string().required(),
  type: yup10.string().optional()
});
var allergySchema = yup10.object({
  name: yup10.string(),
  code: yup10.string()
});
var admissionRequestSchemaFields = {
  periodStart: yup10.date().required(),
  class: yup10.string().oneOf(Object.values(EncounterClass3)).required(),
  service: yup10.string().oneOf(Object.values(EncounterService3)).required(),
  practitioners: generalPractitionerUpdateRequestSchema.required(),
  primaryIcd10Code: yup10.string().optional(),
  icd10Codes: yup10.array(yup10.string().required()).optional(),
  medicalAidAuthorization: medicalAidAuthorizationCreateSchema,
  oxygen: yup10.number().positive().optional(),
  attachments: yup10.array(createMediaDataSchema).optional(),
  statusHistory: yup10.array(statusHistorySchema).optional(),
  diagnosis: yup10.array(diagnosisSchema).optional(),
  pregnancy: PregnancySchema.optional(),
  acute: yup10.array(yup10.string().required()).optional(),
  chronic: yup10.array(yup10.string().required()).optional(),
  reasonOfAdmission: yup10.array(yup10.string().required()).min(1).required(),
  location: yup10.string().required(),
  allergy: yup10.array(allergySchema).optional()
};
var admissionCreateRequestSchema = yup10.object(admissionRequestSchemaFields).required();
var admissionUpdateRequestSchema = yup10.object({
  primaryIcd10Code: admissionRequestSchemaFields.primaryIcd10Code,
  icd10Codes: admissionRequestSchemaFields.icd10Codes,
  medicalAidAuthorization: admissionRequestSchemaFields.medicalAidAuthorization,
  oxygen: admissionRequestSchemaFields.oxygen,
  periodStart: admissionRequestSchemaFields.periodStart,
  reasonOfAdmission: admissionRequestSchemaFields.reasonOfAdmission
});
var admissionLocationUpdateRequestSchema = yup10.object({
  location: yup10.string().required()
}).required();

// src/admission/wards.ts
import { z as z33 } from "zod";
var MovePatientsToWardInputSchema = z33.object({
  wardId: UUIDSchema,
  patientIds: UUIDSchema.array()
});
var MovePatientsToWardResponseSchema = z33.object({});

// src/charge-item-definition/validation.ts
import { ChargeItemDefinitionCode as ChargeItemDefinitionCode2, Currency as Currency2 } from "@quromedical/fhir-common";
import * as yup11 from "yup";

// src/charge-item-definition/schema.ts
import { ChargeItemDefinitionCode, Currency } from "@quromedical/fhir-common";
import { z as z34 } from "zod";
var chargeItemDefinitionStatus = ["draft", "active", "retired", "unknown"];
var chargeItemDefinitionChargeLineType = [
  "base",
  "surcharge",
  "deduction",
  "discount",
  "tax",
  "informational"
];
var ChargeLineApplicabilitySchema = z34.object({
  administratorCode: z34.string().optional(),
  schemeCode: z34.string().optional(),
  description: z34.string().optional()
});
var ChargeItemDefinitionMoneyFactor = /* @__PURE__ */ ((ChargeItemDefinitionMoneyFactor2) => {
  ChargeItemDefinitionMoneyFactor2[ChargeItemDefinitionMoneyFactor2["cents"] = 0.01] = "cents";
  return ChargeItemDefinitionMoneyFactor2;
})(ChargeItemDefinitionMoneyFactor || {});
var MONEY_FACTOR = 0.01 /* cents */;
var ChargeItemDefinitionMoneyFactorSchema = z34.literal(MONEY_FACTOR);
var ChargeItemDefinitionCurrencySchema = z34.nativeEnum(Currency);
var ChargeItemDefinitionCodeSchema = z34.nativeEnum(ChargeItemDefinitionCode);
var ChargeItemDefinitionStatusSchema = z34.enum(chargeItemDefinitionStatus);
var ChargeItemDefinitionTypeSchema = z34.enum(chargeItemDefinitionChargeLineType);
var ChargeItemDefinitionMoneySchema = z34.object({
  /**
   * Always 0.01 since we're working with cents (100 cents = 1 currency) since we always store this
   * as cents
   */
  factor: ChargeItemDefinitionMoneyFactorSchema,
  currency: ChargeItemDefinitionCurrencySchema,
  value: z34.number()
});
var ChargeLineSchema = z34.object({
  type: ChargeItemDefinitionTypeSchema,
  applicability: ChargeLineApplicabilitySchema,
  code: z34.string(),
  amount: ChargeItemDefinitionMoneySchema
});
var ChargeItemDefinitionSchema = z34.object({
  /**
   * Code that represents the type of the charge item
   */
  code: ChargeItemDefinitionCodeSchema,
  title: z34.string(),
  description: z34.string(),
  status: ChargeItemDefinitionStatusSchema,
  /**
   * Date last changed
   */
  date: z34.string(),
  /**
   * Charge/Claim lines as they apply to different medical aids/etc.
   */
  chargeLines: z34.array(ChargeLineSchema).min(1),
  effectivePeriod: PeriodSchema
});
var ChargeItemDefinitionCreateSchema = ChargeItemDefinitionSchema.omit({
  status: true
});
var ChargeItemDefinitionSchemaPartial = z34.object({
  code: ChargeItemDefinitionCodeSchema,
  title: z34.string(),
  description: z34.string(),
  status: ChargeItemDefinitionStatusSchema,
  date: z34.string(),
  effectivePeriod: PeriodSchema
}).partial().extend({
  chargeLines: ChargeLineSchema.partial().array()
});
var ChargeItemDefinitionSchemaWithId = ChargeItemDefinitionSchema.extend({
  id: UUIDSchema
});
var ChargeItemDefinitionGetResponseSchema = ChargeItemDefinitionSchemaPartial.extend({
  id: UUIDSchema
});
var ChargeItemDefinitionGetAllResponseSchema = PaginatedBundleSchema.extend({
  chargeItemDefinitions: ChargeItemDefinitionGetResponseSchema.array()
});
var ChargeItemDefinitionGetAllParamsSchema = z34.object({
  ids: UUIDSchema.optional(),
  title: z34.string().optional(),
  description: z34.string().optional(),
  status: ChargeItemDefinitionStatusSchema.optional(),
  effectivePeriodStart: z34.string().optional(),
  effectivePeriodEnd: z34.string().optional()
});

// src/charge-item-definition/validation.ts
var codeSchema2 = yup11.string().equals(Object.values(ChargeItemDefinitionCode2)).required();
var moneySchema = yup11.object({
  factor: yup11.number().required().equals([MONEY_FACTOR]),
  currency: yup11.string().equals(Object.values(Currency2)).required(),
  value: yup11.number().integer().positive().required()
});
var applicabilitySchema = yup11.object({
  administratorCode: yup11.string().notRequired(),
  schemeCode: yup11.string().notRequired()
});
var chargeLineTypeSchema = yup11.string().equals([...chargeItemDefinitionChargeLineType]).required();
var chargeLineSchema = yup11.object({
  amount: moneySchema,
  type: chargeLineTypeSchema,
  applicability: applicabilitySchema,
  code: yup11.string().required()
}).required();
var createChargeItemDefinitionSchema = yup11.object({
  code: codeSchema2,
  title: yup11.string().required(),
  description: yup11.string().required(),
  date: yup11.date().required(),
  chargeLines: yup11.array(chargeLineSchema).min(1).required()
});
var updateChargeItemDefinitionSchema = createChargeItemDefinitionSchema;

// src/charge-item-definition/mapping.ts
import {
  createChargeItemDefinitionApplicability,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts9,
  createCodeableConcept as createCodeableConcept14,
  system as system3,
  valsOrNone as valsOrNone12,
  getChargeItemDefinitionApplicabilityConditions,
  createReference as createReference5,
  mapFhirToModelPeriod as mapFhirToModelPeriod2
} from "@quromedical/fhir-common";
import { createMapWithId as createMapWithId3 } from "@quromedical/fhir-common";
import { compact as compact13, first as first11 } from "@quromedical/utils";

// src/charge-item-definition/money.ts
import { Currency as Currency3, createExtensionValue as createExtensionValue7, ExtensionUrl as ExtensionUrl8 } from "@quromedical/fhir-common";
var currencies = Object.values(Currency3);
var isCurrency = (currency) => currencies.includes(currency);
var isCentsFactor = (factor) => factor === MONEY_FACTOR;
var mapFhirToMoney = (factor, money) => {
  const value = money?.value;
  const currency = money?.currency;
  const isCurrencyValid = isCurrency(currency);
  const isFactorValid = isCentsFactor(factor);
  const isValid3 = value && isFactorValid && isCurrencyValid;
  if (!isValid3) {
    return void 0;
  }
  return {
    currency,
    factor,
    value
  };
};
var createMoneyFactorExtension = createExtensionValue7(ExtensionUrl8.moneyFactor);
var mapMoneyToFhir = (money) => {
  const moneyFactor = createMoneyFactorExtension(money.factor);
  return {
    extension: [moneyFactor],
    currency: money.currency,
    value: money.value
  };
};

// src/charge-item-definition/mapping.ts
var createApplicabilityConditions = (applicability) => {
  const { administratorCode, schemeCode, description } = applicability;
  return {
    administratorCode: administratorCode ? {
      type: "equal",
      value: administratorCode,
      description
    } : void 0,
    schemeCode: schemeCode ? {
      type: "equal",
      value: schemeCode,
      description
    } : void 0
  };
};
var mapChargeLineToPropertyGroup = (chargeLine) => {
  const { amount, applicability, code, type } = chargeLine;
  const conditions = createApplicabilityConditions(applicability);
  return {
    applicability: createChargeItemDefinitionApplicability(conditions),
    priceComponent: [
      {
        type,
        code: createCodeableConcept14(code),
        factor: amount.factor,
        amount: {
          currency: amount.currency,
          value: amount.value
        }
      }
    ]
  };
};
var getChargeItemDefinitionUrl = (title) => `${system3}/ChargeItemDefinition/${encodeURIComponent(title)}`;
var mapChargeItemDefinitionToFhir = (model) => {
  const { chargeLines, code, date: date5, description, title, status: status2, effectivePeriod } = model;
  return {
    resourceType: "ChargeItemDefinition",
    status: status2,
    title,
    description,
    date: date5,
    code: createCodeableConcept14(code),
    url: getChargeItemDefinitionUrl(title),
    propertyGroup: valsOrNone12(chargeLines.map(mapChargeLineToPropertyGroup)),
    effectivePeriod
  };
};
var mapChargeItemDefinitionToFhirWithID = createMapWithId3(mapChargeItemDefinitionToFhir);
var getApplicabilityFromFhir = (applicability = []) => {
  console.log(applicability);
  const conditions = getChargeItemDefinitionApplicabilityConditions(applicability);
  console.log("conditions", JSON.stringify(conditions));
  return {
    administratorCode: conditions.administratorCode?.value,
    schemeCode: conditions.schemeCode?.value,
    description: conditions.schemeCode?.description || conditions.administratorCode?.description
  };
};
var mapPropertyGroupToChargeLine = (propertyGroup) => {
  const priceComponent = first11(propertyGroup.priceComponent);
  const amount = mapFhirToMoney(priceComponent?.factor, priceComponent?.amount);
  const type = priceComponent?.type;
  const code = getFirstCodeFromCodableConcepts9(compact13([priceComponent?.code]));
  const applicability = getApplicabilityFromFhir(propertyGroup.applicability);
  return {
    amount,
    applicability,
    code,
    type
  };
};
var mapChargeItemDefinitionToModel = (fhir) => {
  const { id, status: status2, date: date5, code, title, description, propertyGroup = [], effectivePeriod } = fhir;
  const period2 = mapFhirToModelPeriod2(effectivePeriod);
  return {
    id,
    status: status2,
    date: date5,
    title,
    description,
    code: getFirstCodeFromCodableConcepts9(compact13([code])),
    chargeLines: compact13(propertyGroup.map(mapPropertyGroupToChargeLine)),
    effectivePeriod: period2
  };
};
var getApplicableChargeLine = (model, coverageIdentifiers) => model.chargeLines.find((line) => line.applicability?.schemeCode === coverageIdentifiers.schemeCode);
var mapChargeItemDefinitionToChargeLine = (coverageIdentifiers, chargeLines, chargeItem) => {
  const quantity = chargeLines.find((line) => line.chargeLine === chargeItem.id);
  const chargeLine = getApplicableChargeLine(chargeItem, coverageIdentifiers);
  const name = chargeItem.title;
  const { amount, code } = chargeLine || {};
  const hasAllData = quantity && amount && code && name;
  if (!hasAllData) {
    return void 0;
  }
  const chargeItemRef = createReference5("ChargeItemDefinition", chargeItem.id, chargeItem.title);
  return {
    chargeItem: chargeItemRef,
    chargeLine: {
      code,
      amount,
      name
    },
    quantity: quantity?.quantity
  };
};

// src/claim/schema.ts
import { ClaimTypeCode as ClaimTypeCode3, ClaimPriorityCode } from "@quromedical/fhir-common";
import { z as z37 } from "zod";

// src/claim-response/mapping.ts
import {
  ClaimResponseItemAdjudicationCategory,
  ClaimResponseTotalCategory,
  ClaimResponseTypeCode,
  ClaimTypeCode,
  createCodeableConcept as createCodeableConcept15,
  createExtensionValue as createExtensionValue8,
  createFhirDateTime as createFhirDateTime3,
  createReference as createReference6,
  Currency as Currency4,
  ExtensionUrl as ExtensionUrl9,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts10,
  valsOrNone as valsOrNone13
} from "@quromedical/fhir-common";
import {
  StatusCode
} from "@quromedical/healthbridge";
import { compact as compact14, first as first12 } from "@quromedical/utils";
var claimToResponseTypeMap = {
  [ClaimTypeCode.Arthroplasty]: ClaimResponseTypeCode.Arthroplasty,
  [ClaimTypeCode.HealingHands]: ClaimResponseTypeCode.HealingHands,
  [ClaimTypeCode.InHospitalMonitoring]: ClaimResponseTypeCode.InHospitalMonitoring,
  [ClaimTypeCode.HospitalAtHome]: ClaimResponseTypeCode.HospitalAtHome,
  [ClaimTypeCode.TCP]: ClaimResponseTypeCode.TCP,
  [ClaimTypeCode.RemotePatientMonitoring]: ClaimResponseTypeCode.RemotePatientMonitoring,
  [ClaimTypeCode.HospitalAtHomeUpgrade]: ClaimResponseTypeCode.HospitalAtHomeUpgrade,
  [ClaimTypeCode.SPHERE]: ClaimResponseTypeCode.SPHERE
};
var createTraceNumberExtension = createExtensionValue8(ExtensionUrl9.claimResponseItemTraceNumber);
var getDisclaimerNotes = (num, disclaimer) => disclaimer ? [{ number: num, text: disclaimer }] : [];
var mapFinancialToMoney = (amount) => mapMoneyToFhir({
  currency: Currency4.ZAR,
  factor: MONEY_FACTOR,
  value: +amount
});
var getClaimResponseTotals = (claimFinancials) => [
  {
    category: createCodeableConcept15(ClaimResponseTotalCategory.TotalClaimedAmount),
    amount: mapFinancialToMoney(claimFinancials.TotalClaimedAmt)
  },
  {
    category: createCodeableConcept15(ClaimResponseTotalCategory.TotalPaidProviderAmount),
    amount: mapFinancialToMoney(claimFinancials.TotalPaidProvAmt)
  },
  {
    category: createCodeableConcept15(ClaimResponseTotalCategory.BalanceOutstandingAmount),
    amount: mapFinancialToMoney(claimFinancials.BalanceOutstanding)
  }
];
var getDispositionNote = (claimDetails) => claimDetails.Responses?.map((res) => res.Desc).join("\n");
var queuedCodes = [
  StatusCode.AwaitingResponse,
  StatusCode.ReceivedByHealthbridge,
  StatusCode.ValidatedByHealthbridge,
  StatusCode.ValidatedByFunder
];
var getOutcome = (claimDetails) => {
  const topLevelError = first12(claimDetails.Responses)?.["@Type"] === "Error";
  if (topLevelError) {
    return "error";
  }
  const isComplete = claimDetails.LineItems.every((line) => line.StatusCode === StatusCode.Processed);
  if (isComplete) {
    return "complete";
  }
  const isQueued = claimDetails.LineItems.every((line) => queuedCodes.includes(line.StatusCode));
  if (isQueued) {
    return "queued";
  }
  return "partial";
};
var createResponseItem = (item, itemNoteNumbers) => {
  const itemFinancials = item.LineItemFinancials;
  return {
    extension: item.MALineRefNum ? [createTraceNumberExtension(item.MALineRefNum)] : void 0,
    // this needs to be used in accordance with lookup via the claim to determine which item it relates to
    itemSequence: +item.UniqueID,
    noteNumber: valsOrNone13(itemNoteNumbers),
    adjudication: compact14([
      {
        category: createCodeableConcept15(ClaimResponseItemAdjudicationCategory.BilledAmount),
        amount: mapFinancialToMoney(itemFinancials.BilledAmt)
      },
      {
        category: createCodeableConcept15(ClaimResponseItemAdjudicationCategory.MedicalAidPayable),
        amount: mapFinancialToMoney(itemFinancials.MAPayable)
      },
      itemFinancials.MedicalAidRate ? {
        category: createCodeableConcept15(ClaimResponseItemAdjudicationCategory.MedicalAidRate),
        amount: mapFinancialToMoney(itemFinancials.MedicalAidRate)
      } : void 0
    ])
  };
};
var createResponseItems = (claimItems, disclaimer) => {
  const processNote = getDisclaimerNotes(1, disclaimer);
  const items = [];
  for (const item of claimItems) {
    const lastNumber = processNote[processNote.length - 1]?.number || 0;
    const itemNotes = item.Responses.map((res, index) => ({
      text: `${res.Code} - ${res.Desc}`,
      number: lastNumber + index + 1
    }));
    const itemNoteNumbers = itemNotes.map((note) => note.number);
    const fhirItem = createResponseItem(item, itemNoteNumbers);
    processNote.push(...itemNotes);
    items.push(fhirItem);
  }
  return { items, processNote };
};
var mapHealthbridgeResponseToFhir = (response, claim, createdDateTime = /* @__PURE__ */ new Date()) => {
  const claimDetails = response.MedicalAidMessage.Claim.Details;
  const typeCode = claimToResponseTypeMap[claim.type];
  const disposition = getDispositionNote(claimDetails);
  const total = getClaimResponseTotals(claimDetails.ClaimFinancials);
  const outcome = getOutcome(claimDetails);
  const type = createCodeableConcept15(typeCode);
  const request = createReference6("Claim", claim.id);
  const created = createFhirDateTime3(createdDateTime);
  const { items, processNote } = createResponseItems(
    claimDetails.LineItems,
    response.MedicalAidMessage.Claim.Disclaimer
  );
  return {
    resourceType: "ClaimResponse",
    status: "active",
    use: "claim",
    outcome,
    type,
    total,
    created,
    request,
    disposition,
    processNote,
    item: items,
    insurer: claim.provider,
    patient: claim.patient
  };
};
var hasClaimLinks = (claim) => !!(claim.type && claim.patient && claim.provider);
var getClaimInvoiceNumber = (response) => response.MedicalAidMessage.Claim.Transaction.InvoiceNum;
var getTotal = (total = []) => {
  const splitTotals = total.reduce((prev, current) => {
    const code = getFirstCodeFromCodableConcepts10([current.category]);
    if (!code) {
      return prev;
    }
    return {
      ...prev,
      [code]: mapFhirToMoney(MONEY_FACTOR, current.amount)
    };
  }, {});
  return {
    balanceOutstanding: splitTotals[ClaimResponseTotalCategory.BalanceOutstandingAmount],
    claimedAmount: splitTotals[ClaimResponseTotalCategory.TotalClaimedAmount],
    paidProvider: splitTotals[ClaimResponseTotalCategory.TotalPaidProviderAmount]
  };
};
var getItemTotal = (adjudication = []) => {
  const splitAdjudications = adjudication.reduce((prev, current) => {
    const code = getFirstCodeFromCodableConcepts10([
      current.category
    ]);
    if (!code) {
      return prev;
    }
    return {
      ...prev,
      [code]: mapFhirToMoney(MONEY_FACTOR, current.amount)
    };
  }, {});
  return {
    billedAmount: splitAdjudications[ClaimResponseItemAdjudicationCategory.BilledAmount],
    medicalAidPayable: splitAdjudications[ClaimResponseItemAdjudicationCategory.MedicalAidPayable],
    medicalAidRate: splitAdjudications[ClaimResponseItemAdjudicationCategory.MedicalAidRate]
  };
};
var includes = (list, num) => list?.includes(num);
var defined = (num) => num !== void 0;
var getNotes = (noteNumbers, notes) => notes.filter(({ number: noteNumber }) => includes(noteNumbers, noteNumber)).map((note) => note.text);
var getItem = (item, notes = []) => {
  const noteNumbers = item.noteNumber || [];
  return {
    sequence: item.itemSequence,
    notes: getNotes(noteNumbers, notes),
    total: getItemTotal(item.adjudication)
  };
};
var getUnlinkedNotes = (items = [], notes = []) => {
  const itemNotes = items.map((item) => item.noteNumber || []).flat();
  const allNotes = notes.map((note) => note.number).filter(defined);
  const unlinkedNotes = allNotes.filter((note) => !includes(itemNotes, note));
  return getNotes(unlinkedNotes, notes);
};
var mapFhirClaimResponseToModel = (fhir) => {
  const {
    patient,
    type,
    insurer,
    created,
    outcome,
    status: status2,
    id,
    request,
    total,
    processNote,
    disposition,
    item = []
  } = fhir;
  return {
    id,
    date: created,
    provider: getValidReference(insurer),
    claim: getValidReference(request),
    patient: getValidReference(patient),
    outcome,
    status: status2,
    statusDescription: disposition,
    type: getFirstCodeFromCodableConcepts10([type]),
    total: getTotal(total),
    notes: getUnlinkedNotes(item, processNote),
    items: item.map((i) => getItem(i, processNote))
  };
};

// src/claim-response/schema.ts
import { ClaimResponseTypeCode as ClaimResponseTypeCode2 } from "@quromedical/fhir-common";
import { z as z35 } from "zod";
var ClaimSupportedResponseStatus = ["active", "cancelled", "draft", "entered-in-error"];
var ClaimSupportedResponseOutcome = ["queued", "complete", "error", "partial"];
var ClaimResponseStatus = z35.enum(ClaimSupportedResponseStatus);
var ClaimResponseOutcome = z35.enum(ClaimSupportedResponseOutcome);
var ClaimTypeSchema = z35.nativeEnum(ClaimResponseTypeCode2);
var ClaimResponseItemTotalSchema = z35.object({
  billedAmount: ChargeItemDefinitionMoneySchema,
  medicalAidPayable: ChargeItemDefinitionMoneySchema,
  medicalAidRate: ChargeItemDefinitionMoneySchema
});
var ClaimResponseTotalSchema = z35.object({
  claimedAmount: ChargeItemDefinitionMoneySchema,
  paidProvider: ChargeItemDefinitionMoneySchema,
  balanceOutstanding: ChargeItemDefinitionMoneySchema
});
var ClaimResponseItemSchema = z35.object({
  sequence: z35.number(),
  total: ClaimResponseItemTotalSchema,
  notes: z35.string().array()
});
var ClaimResponseItemPartialSchema = z35.object({
  sequence: z35.number(),
  total: ClaimResponseItemTotalSchema.partial(),
  notes: z35.string().array()
});
var ClaimResponseModelSchema = z35.object({
  status: ClaimResponseStatus,
  statusDescription: z35.string(),
  outcome: ClaimResponseOutcome,
  type: ClaimTypeSchema,
  patient: DisplayReferenceSchema,
  provider: DisplayReferenceSchema,
  claim: DisplayReferenceSchema,
  date: z35.string(),
  items: ClaimResponseItemSchema.array(),
  total: ClaimResponseTotalSchema,
  notes: z35.string().array()
});
var ClaimResponseModelGetResponseSchema = ClaimResponseModelSchema.omit({
  patient: true,
  provider: true,
  claim: true,
  items: true,
  total: true
}).deepPartial().extend({
  id: z35.string(),
  patient: z35.optional(DisplayReferenceSchema),
  provider: z35.optional(DisplayReferenceSchema),
  claim: z35.optional(DisplayReferenceSchema),
  items: ClaimResponseItemPartialSchema.partial().array(),
  total: ClaimResponseTotalSchema.partial()
});
var ClaimResponseModelGetAllResponseSchema = PaginatedBundleSchema.extend({
  claimResponses: ClaimResponseModelGetResponseSchema.array()
});
var ClaimResponseGetAllParamsSchema = PaginationSchema.extend({
  outcome: ClaimResponseOutcome.optional(),
  status: ClaimResponseStatus.optional()
});

// src/claim-template/schema.ts
import { ClaimTypeCode as ClaimTypeCode2 } from "@quromedical/fhir-common";
import { z as z36 } from "zod";
var ClaimTemplateChargeLineQuantitySchema = z36.object({
  /**
   * ID of the `ChargeItemDefinition` in FHIR
   */
  chargeLine: UUIDSchema,
  quantity: z36.number()
});
var ClaimTemplateBaseSchema = z36.object({
  type: z36.nativeEnum(ClaimTypeCode2),
  chargeLines: ClaimTemplateChargeLineQuantitySchema.array()
});
var ClaimTemplateCreateRequestSchema = ClaimTemplateBaseSchema;
var ClaimTemplateUpdateRequestSchema = ClaimTemplateBaseSchema;
var ClaimTemplateSchema = ClaimTemplateBaseSchema.extend({
  lastModified: z36.number(),
  lastModifiedBy: UUIDSchema
});
var ClaimTemplateChargeLineReferenceQuantitySchema = z36.object({
  reference: DisplayReferenceSchema,
  quantity: z36.number()
});
var ClaimTemplateGetTypeRequestSchema = z36.object({
  type: z36.nativeEnum(ClaimTypeCode2)
});
var ClaimTemplateGetResponseSchema = z36.object({
  id: z36.nativeEnum(ClaimTypeCode2),
  lastModified: z36.number(),
  lastModifiedBy: z36.string(),
  type: z36.nativeEnum(ClaimTypeCode2),
  chargeLines: ClaimTemplateChargeLineReferenceQuantitySchema.array()
});
var ClaimTemplateGetAllResponseSchema = z36.object({
  claimTemplates: ClaimTemplateSchema.extend({
    id: z36.nativeEnum(ClaimTypeCode2)
  }).array()
});
var ClaimTemplateLambdaGetInputSchema = z36.object({
  type: z36.nativeEnum(ClaimTypeCode2)
});

// src/claim/schema.ts
var ChargeLineQuantityItemSchema = z37.object({
  code: z37.string(),
  amount: ChargeItemDefinitionMoneySchema,
  name: z37.string()
});
var ChargeLineQuantitySchema = z37.object({
  chargeItem: DisplayReferenceSchema,
  chargeLine: ChargeLineQuantityItemSchema,
  quantity: z37.number()
});
var ClaimTypeCodeSchema = z37.nativeEnum(ClaimTypeCode3);
var SupportedClaimStatus = ["active", "cancelled", "draft", "entered-in-error"];
var ClaimStatusSchema = z37.enum(SupportedClaimStatus);
var ClaimPriorityCodeSchema = z37.nativeEnum(ClaimPriorityCode);
var CoverageSchema = z37.object({
  reference: VersionedReferenceSchema
});
var ClaimSchema = z37.object({
  type: ClaimTypeCodeSchema,
  status: ClaimStatusSchema,
  created: z37.string(),
  provider: DisplayReferenceSchema,
  coverage: CoverageSchema,
  patient: DisplayReferenceSchema,
  priority: ClaimPriorityCodeSchema,
  admission: DisplayReferenceSchema,
  chargeLines: ChargeLineQuantitySchema.array(),
  invoiceNumber: z37.string().optional()
});
var PartialClaimSchema = ClaimSchema.partial();
var ClaimGetResponseSchema = PartialClaimSchema.extend({
  responses: ClaimResponseModelGetResponseSchema.array()
});
var PartialClaimWithIdSchema = PartialClaimSchema.extend({
  id: UUIDSchema
});
var ClaimGetResponseWithIdSchema = ClaimGetResponseSchema.extend({
  id: UUIDSchema
});
var ClaimResponseInputSchema = z37.object({
  id: UUIDSchema,
  responseId: UUIDSchema.optional()
});
var ClaimGetAllResponseSchema = PaginatedBundleSchema.extend({
  claims: PartialClaimWithIdSchema.array()
});
var ClaimGetAllParams = PaginationSchema.extend({
  identifier: z37.string().optional(),
  status: ClaimStatusSchema.optional(),
  patientName: z37.string().optional()
});
var ClaimUpdateRequestSchema = z37.object({
  id: UUIDSchema,
  chargeLines: ClaimTemplateChargeLineQuantitySchema.array()
});
var ClaimUpdateResponseSchema = PartialClaimSchema.extend({
  id: UUIDSchema
});
var SubmitClaimRequestSchema = z37.object({ id: UUIDSchema });

// src/claim/mapping.ts
var createChargeItemReferenceExtension = createExtensionValue9(
  ExtensionUrl10.claimItemChargeItemDefinition
);
var asHealthbridgeDate = (date5) => new Date(date5).toISOString().split("T")[0];
var asHealthbridgeDateTime = (date5) => new Date(date5).toISOString().split(".")[0];
var mapChargeLineToClaimItem = (encounter, item, index) => ({
  extension: [createChargeItemReferenceExtension(item.chargeItem)],
  encounter: [encounter],
  sequence: index + 1,
  quantity: {
    value: item.quantity
  },
  factor: item.chargeLine.amount.factor,
  unitPrice: {
    currency: item.chargeLine.amount.currency,
    value: item.chargeLine.amount.value
  },
  productOrService: {
    text: item.chargeLine.name,
    coding: [createCoding5(item.chargeLine.code, item.chargeLine.name)]
  }
});
var getChargeItemReferenceValue = findExtensionValue7(ExtensionUrl10.claimItemChargeItemDefinition);
var getChargeItemDefinitionReference = (extension = []) => {
  const ref = getChargeItemReferenceValue(extension);
  return getValidReference(ref);
};
var mapClaimItemToChargeLine = (claimItem) => {
  const { productOrService, unitPrice, factor, extension } = claimItem;
  const quantity = claimItem.quantity?.value;
  const code = getFirstCodeFromCodableConcepts11([productOrService]);
  const name = getDisplayFromCodableConcepts3([productOrService]);
  const value = unitPrice?.value;
  const currency = unitPrice?.currency;
  const isCurrencyValid = isCurrency(currency);
  const isFactorValid = isCentsFactor(factor);
  const chargeItem = getChargeItemDefinitionReference(extension);
  const isValid3 = quantity && code && value && isCurrencyValid && isFactorValid && chargeItem && name;
  if (!isValid3) {
    return void 0;
  }
  return {
    chargeItem,
    chargeLine: {
      amount: {
        currency,
        factor,
        value
      },
      name,
      code
    },
    quantity
  };
};
var createIdentifiers = (claim) => {
  const identifier = createOptionalIdentifier(
    IdentifierCode10.claimInvoiceNumber,
    claim.invoiceNumber
  );
  if (!identifier) {
    return void 0;
  }
  return [identifier];
};
var mapClaimToFhir = (claim) => {
  const { admission, chargeLines, coverage, created, patient, priority, status: status2, type, provider } = claim;
  const mapChargeLineToItem = partial3(mapChargeLineToClaimItem, admission);
  const item = valsOrNone14(chargeLines.map(mapChargeLineToItem));
  const identifier = createIdentifiers(claim);
  return {
    resourceType: "Claim",
    use: "claim",
    status: status2,
    created,
    patient,
    priority: createCodeableConcept16(priority),
    provider,
    type: createCodeableConcept16(type),
    insurance: [
      {
        coverage,
        focal: true,
        sequence: 1
      }
    ],
    item,
    identifier
  };
};
var mapClaimToModel = (claim) => {
  const { id, created, insurance, patient, priority, provider, status: status2, item = [], type } = claim;
  const coverage = getValidVersionReference(["Coverage"], first13(insurance)?.coverage);
  const admission = getValidReference(first13(first13(item)?.encounter));
  const chargeLines = compact15(item.map(mapClaimItemToChargeLine));
  const invoiceNumber = getIdentifier4(IdentifierCode10.claimInvoiceNumber, claim.identifier);
  return {
    id,
    created,
    status: status2,
    patient: getValidReference(patient),
    provider: getValidReference(provider),
    coverage,
    admission,
    priority: getFirstCodeFromCodableConcepts11([priority]),
    type: getFirstCodeFromCodableConcepts11([type]),
    chargeLines,
    invoiceNumber
  };
};
var mapClaimToFhirModel = (claim) => {
  const claimResponses = mapClaimToModel(claim);
  return {
    ...claimResponses,
    responses: []
  };
};
var isZARCents = (amount) => amount.currency === Currency5.ZAR && amount.factor === MONEY_FACTOR;
var getChargeLineCostCents = (quantity) => {
  const isAllowedAmount = isZARCents(quantity.chargeLine.amount);
  if (!isAllowedAmount) {
    return void 0;
  }
  if (quantity.quantity < 0) {
    return void 0;
  }
  return quantity.chargeLine.amount.value * quantity.quantity;
};
var isFilled = (items) => {
  const empty = items.includes(void 0);
  return !empty;
};
var getClaimFinancials = (chargeLines) => {
  const totals = chargeLines.map(getChargeLineCostCents);
  const allItemsDefined = isFilled(totals);
  if (!allItemsDefined) {
    return void 0;
  }
  const total = sum2(totals).toString();
  return {
    NettBilledAmt: total,
    TotalBilledAmt: total,
    TotalDiscount: "0"
  };
};
var getClaimLineItem = (dateOfService, diagnosisCodes, quantity, lineIndex) => {
  const total = getChargeLineCostCents(quantity);
  if (!total) {
    return void 0;
  }
  const totalAmount = total.toString();
  const itemId = (lineIndex + 1).toString();
  const chargeQuantity = quantity.quantity.toString();
  return {
    "@Type": "Procedure",
    PricingInd: "Private",
    ChargeQuantity: chargeQuantity,
    ChargeCode: {
      "@Type": "Procedure",
      Set: "Tariff",
      Value: quantity.chargeLine.code,
      Description: quantity.chargeLine.name
    },
    DOS: asHealthbridgeDate(dateOfService),
    DiagnosisCodes: diagnosisCodes,
    HospInd: "Yes",
    LineItemFinancials: {
      BilledAmt: totalAmount,
      GrossAmt: totalAmount
    },
    UniqueID: itemId,
    PlaceServiceCode: PlaceServiceCode.HomeCare
  };
};
var getClaimLineItems = (dateOfService, diagnosisCodes, chargeLines) => {
  const items = chargeLines.map(
    (line, index) => getClaimLineItem(dateOfService, diagnosisCodes, line, index)
  );
  if (!items.length) {
    return void 0;
  }
  const allItemsDefined = isFilled(items);
  if (!allItemsDefined) {
    return void 0;
  }
  return items;
};
var updateChargeLines = (claim, chargeLines, encounter) => {
  const mapChargeLineToItem = partial3(mapChargeLineToClaimItem, encounter);
  const item = valsOrNone14(chargeLines.map(mapChargeLineToItem));
  return {
    ...claim,
    item
  };
};
var mapRequiredCodingToDiagnosisCode = (type, code) => {
  return {
    Set: "ICD",
    Version: "10",
    Type: type,
    Value: code.code,
    Description: code.display
  };
};
var mapRequiredCodingToSecondaryDiagnosisCode = partial3(
  mapRequiredCodingToDiagnosisCode,
  "Secondary"
);
var getDiagnosisCodes = (primaryIcd10Code, icd10Codes = []) => {
  const primaryDiagnosisCode = mapRequiredCodingToDiagnosisCode("Primary", primaryIcd10Code);
  const additionalDiagnosisCodes = icd10Codes.map(
    mapRequiredCodingToSecondaryDiagnosisCode
  );
  return [primaryDiagnosisCode, ...additionalDiagnosisCodes];
};
var baseProvider = {
  PracticeNum: "0904287",
  PracticeName: "QM Partners",
  SpecialtyCode: "14",
  SubSpecialtyCode: "0"
};
var billingProvider = {
  ...baseProvider,
  "@Type": "Billing"
};
var treatingProvider = {
  ...baseProvider,
  "@Type": "Treating",
  DispensingDoctor: "No",
  HPCSA: "0904287"
};
var providers = [billingProvider, treatingProvider];
var pmaName = "Insight";
var pmaVersion = "1";
var ClaimMedicalAidMembershipSchema = MedicalAidSchema.pick({
  membershipNumber: true,
  schemeName: true
});
var ClaimPatientSchema = GeneralSchema.pick({
  birthDate: true,
  familyName: true,
  givenName: true,
  identifierValue: true
});
var ClaimMainMemberSchema = GeneralSchema.pick({
  birthDate: true,
  familyName: true,
  givenName: true
});
var getMember = (membership, mainMember) => ({
  DOB: asHealthbridgeDate(mainMember.birthDate),
  Name: mainMember.givenName,
  Surname: mainMember.familyName,
  SchemeName: membership.schemeName,
  MANum: membership.membershipNumber
});
var getPatient = (patient, dependantCode) => {
  const initials = patient.givenName[0];
  return {
    AccNum: patient.identifierValue,
    DOB: asHealthbridgeDate(patient.birthDate),
    DepCode: dependantCode,
    Name: patient.givenName,
    Surname: patient.familyName,
    Initials: initials,
    NewBornInd: "No"
  };
};
var ClaimAdmissionSchema = AdmissionSchema.pick({
  period: true,
  primaryIcd10Code: true,
  icd10Codes: true
});
var ClaimMembershipSchema = z38.object({
  medicalAid: ClaimMedicalAidMembershipSchema,
  dependantCode: z38.string()
});
var ClaimChargeLinesSchema = z38.object({
  chargeLines: ChargeLineQuantitySchema.array()
});
var ClaimToHealthbridgeRequestParamSchema = z38.object({
  invoiceNumber: z38.string().length(10).startsWith("QM"),
  transactionDate: z38.string(),
  routingCode: z38.string(),
  mainMember: ClaimMainMemberSchema,
  patient: ClaimPatientSchema,
  admission: ClaimAdmissionSchema,
  claim: ClaimChargeLinesSchema,
  membership: ClaimMembershipSchema
});
function assertClaimToHealthbridgeRequestParamsValid(params) {
  ClaimToHealthbridgeRequestParamSchema.parse(params);
}
var getTransaction = (invoiceNumber, routingCode, transactionDate) => ({
  DataSet: "01",
  PMAName: pmaName,
  InvoiceNum: invoiceNumber,
  RoutingCode: routingCode,
  PMAVersion: pmaVersion,
  SavedDateTime: asHealthbridgeDateTime(transactionDate)
});
var mapClaimToHealthbridgeRequest = ({
  admission,
  claim,
  mainMember,
  patient: patientGeneral,
  invoiceNumber,
  routingCode,
  transactionDate,
  membership
}) => {
  const claimFinancials = getClaimFinancials(claim.chargeLines);
  const diagnosisCodes = getDiagnosisCodes(admission.primaryIcd10Code, admission.icd10Codes);
  const member = getMember(membership.medicalAid, mainMember);
  const patient = getPatient(patientGeneral, membership.dependantCode);
  if (!claimFinancials) {
    return {
      type: "error",
      message: "Claim contains invalid financials" /* invalidFinancials */
    };
  }
  if (!diagnosisCodes) {
    return {
      type: "error",
      message: "Claim contains invalid diagnosis codes" /* invalidDiagnosisCodes */
    };
  }
  const lineItems = getClaimLineItems(admission.period.start, diagnosisCodes, claim.chargeLines);
  if (!lineItems) {
    return {
      type: "error",
      message: "Claim contains invalid line items" /* invalidLineItems */
    };
  }
  const transaction = getTransaction(invoiceNumber, routingCode, transactionDate);
  const result = {
    ProviderMessage: {
      "@Version": "6.0.0",
      "@xmlns:hb": "healthbridge.co.za/messaging",
      Claim: {
        Details: {
          ClaimFinancials: claimFinancials,
          DiagnosisCodes: diagnosisCodes,
          LineItems: lineItems,
          COIDInd: "No",
          HospInd: "Yes",
          PayWho: "Provider"
        },
        Member: member,
        Patient: patient,
        Providers: providers,
        Transaction: transaction
      }
    }
  };
  return {
    type: "success",
    result
  };
};
var markClaimAsActive = (claim, invoiceNumber) => {
  const activeClaim = {
    ...claim,
    status: "active"
  };
  return attachIdentifiers(
    activeClaim,
    createIdentifier7(IdentifierCode10.claimInvoiceNumber, invoiceNumber)
  );
};
var setClaimResponse = (claim, claimResponse) => {
  const { insurance } = claim;
  const insuranceWithResponse = insurance.map((ins) => ({
    ...ins,
    claimResponse
  }));
  return {
    ...claim,
    insurance: insuranceWithResponse
  };
};
var generateClaimInvoiceNumber = () => `QM${Math.random().toString().slice(2, 10)}`;

// src/claim-template/validation.ts
import { ClaimTypeCode as ClaimTypeCode4 } from "@quromedical/fhir-common";
import * as yup12 from "yup";
var templateChargeLineQuantitySchema = yup12.object({
  chargeLine: yup12.string().required(),
  // We allow 0 to ensure that we can create a template with no charge lines, there for no claims are generated.
  quantity: yup12.number().required().integer()
});
var claimTypes = Object.values(ClaimTypeCode4);
var claimTemplateRequestSchema = yup12.object({
  chargeLines: yup12.array(templateChargeLineQuantitySchema).required().min(1),
  type: yup12.string().equals(claimTypes).required()
});

// src/claim-reversal/schema.ts
import { z as z39 } from "zod";
var CreateClaimReversalSchema = z39.object({
  invoiceNumber: z39.string()
});

// src/claim-reversal/validation.ts
import * as yup13 from "yup";
var claimReversalCreateSchema = yup13.object({
  invoiceNumber: yup13.string().required()
});

// src/claim-reversal/mapping.ts
var asHealthbridgeDateTime2 = (date5) => new Date(date5).toISOString().split(".")[0];
var pmaName2 = "Insight";
var pmaVersion2 = "1";
var bonitasRoutingCode = "91982";
var mapClaimReversalToHBFormat = (claimReversal) => {
  return {
    ProviderMessage: {
      Reversal: {
        Transaction: {
          DataSet: "01",
          InvoiceNum: claimReversal.invoiceNumber,
          PMAName: pmaName2,
          PMAVersion: pmaVersion2,
          RoutingCode: bonitasRoutingCode,
          SavedDateTime: asHealthbridgeDateTime2((/* @__PURE__ */ new Date()).toISOString())
        }
      }
    }
  };
};

// src/consumable/mapping.ts
import {
  createCodeableConcept as createCodeableConcept17,
  createIdentifier as createIdentifier8,
  createQuantityWithValue,
  createReference as createReference7,
  createReferenceFromResource,
  getCodes as getCodes3,
  getDisplayFromCodableConcepts as getDisplayFromCodableConcepts4,
  getIdentifier as getIdentifier5,
  getQuantityWithValue as getQuantityWithValue2,
  IdentifierCode as IdentifierCode11,
  medpraxSystem as medpraxSystem6,
  QuantityCode,
  system as system4
} from "@quromedical/fhir-common";
import { first as first14 } from "@quromedical/utils";
var mapCreateModelToChargeItem = (consumable, performer) => {
  const { patient, admission, consumables } = consumable;
  const result = consumables.map((consumable2) => {
    const { medpraxCode, dateOfUse, quantity, name, nappiCode } = consumable2;
    const identifier = createIdentifier8(
      IdentifierCode11.chargeItemConsumableCode,
      medpraxCode,
      medpraxSystem6
    );
    const codingValue = nappiCode ? nappiCode : medpraxCode;
    const code = createCodeableConcept17(codingValue, name, name, system4);
    const chargeItem = {
      code,
      resourceType: "ChargeItem",
      identifier: [identifier],
      quantity: createQuantityWithValue({
        value: quantity,
        code: QuantityCode.ChargeItemConsumable
      }),
      subject: createReference7("Patient", patient),
      context: createReference7("Encounter", admission),
      performer: [
        {
          actor: createReferenceFromResource(performer)
        }
      ],
      occurrenceDateTime: dateOfUse,
      status: "billable"
    };
    return chargeItem;
  });
  return result;
};
var mapModelToChargeItem = (consumable) => {
  const {
    name,
    medpraxCode,
    quantity,
    nappiCode,
    patient,
    admission,
    dateOfUse,
    performer,
    status: status2
  } = consumable;
  const identifier = createIdentifier8(
    IdentifierCode11.chargeItemConsumableCode,
    medpraxCode,
    medpraxSystem6
  );
  const codableConcept = createCodeableConcept17(nappiCode, name, name);
  return {
    code: codableConcept,
    resourceType: "ChargeItem",
    identifier: [identifier],
    quantity,
    subject: patient,
    context: admission,
    performer: [
      {
        actor: performer
      }
    ],
    occurrenceDateTime: dateOfUse,
    status: status2
  };
};
var mapChargeItemToModel = (fhir) => {
  const { code, identifier, subject, context, quantity, performer, occurrenceDateTime, status: status2 } = fhir;
  const nappiCode = first14(getCodes3(code));
  const consumableCode = getIdentifier5(IdentifierCode11.chargeItemConsumableCode, identifier);
  const name = getDisplayFromCodableConcepts4([code]);
  const patient = getValidReference(subject);
  const admission = getValidReference(context);
  const actor = first14(performer)?.actor;
  const practitioner = getValidReference(actor);
  return {
    medpraxCode: consumableCode,
    nappiCode,
    name,
    quantity,
    patient,
    admission,
    performer: practitioner,
    dateOfUse: occurrenceDateTime,
    status: status2
  };
};
var mapChargeItemToUsageModel = (fhir) => {
  return fhir.map((chargeItem) => {
    const model = mapChargeItemToModel(chargeItem);
    const { name, quantity, status: status2 } = model;
    const numberQuantity = getQuantityWithValue2(quantity)?.value;
    return {
      id: chargeItem.id,
      name,
      quantity: numberQuantity,
      performer: model.performer,
      dateOfUse: model.dateOfUse,
      status: status2
    };
  });
};

// src/location/schema.ts
import { LocationAcuityCode, LocationTypeCode } from "@quromedical/fhir-common";
import { z as z40 } from "zod";
var locationVirtualHospitalRefinement = (obj, ctx) => {
  if (obj.type === LocationTypeCode.VirtualHospital && obj.parent) {
    ctx.addIssue({
      code: "custom",
      message: "Virtual Hospital must not have a parent",
      path: ["parent"]
    });
    return;
  }
  if (obj.type === LocationTypeCode.VirtualHospital && obj.acuity) {
    ctx.addIssue({
      code: "custom",
      message: "Virtual Hospital must not have an acuity",
      path: ["acuity"]
    });
  }
  if (obj.type === LocationTypeCode.VirtualHospital && obj.capacity) {
    ctx.addIssue({
      code: "custom",
      message: "Virtual Hospital must not have a count",
      path: ["capacity"]
    });
  }
  const parsed = BaseLocationSchema.safeParse(obj);
  if (!parsed.success) {
    ctx.addIssue({
      code: "custom",
      message: parsed.error.message
    });
  }
};
var SupportedLocationStatus = ["active", "suspended", "inactive"];
var LocationStatusSchema = z40.enum(SupportedLocationStatus);
var LocationTypeSchema = z40.nativeEnum(LocationTypeCode);
var LocationAcuitySchema = z40.nativeEnum(LocationAcuityCode);
var CapacitySchema = z40.coerce.number().int().gte(0);
var LocationSearchSchema = PaginationSchema.extend({
  name: z40.string().optional(),
  parent: BaseUUIDSearchSchema.optional(),
  status: LocationStatusSchema.optional(),
  type: LocationTypeSchema.optional()
});
var BaseLocationSchema = z40.object({
  status: LocationStatusSchema,
  name: z40.string(),
  description: z40.string(),
  type: LocationTypeSchema,
  acuity: LocationAcuitySchema.optional()
});
var CreateLocationSchema = BaseLocationSchema.extend({
  managingOrganization: UUIDSchema,
  parent: UUIDSchema.optional(),
  capacity: CapacitySchema.optional()
}).superRefine(locationVirtualHospitalRefinement);
var LocationSchema = BaseLocationSchema.extend({
  id: UUIDSchema,
  managingOrganization: DisplayReferenceSchema,
  parent: DisplayReferenceSchema.optional(),
  capacity: CapacitySchema
});
var UpdateLocationSchema = BaseLocationSchema.extend({
  id: UUIDSchema,
  managingOrganization: UUIDSchema.optional(),
  parent: UUIDSchema.optional(),
  capacity: CapacitySchema.optional()
}).superRefine(locationVirtualHospitalRefinement);
var GetLocationResponseSchema = LocationSchema;
var LocationGetAllResponseSchema = PaginatedBundleSchema.extend({
  locations: GetLocationResponseSchema.array()
});

// src/location/mapping.ts
import {
  IdentifierCode as IdentifierCode12,
  LocationAcuityCode as LocationAcuityCode2,
  LocationAcuityDescription,
  LocationCodeDescription,
  LocationTypeCode as LocationTypeCode2,
  createCodeableConcept as createCodeableConcept18,
  createIdentifier as createIdentifier9,
  createReference as createReference8,
  getCodingsFromCodableConcepts,
  getIdentifier as getIdentifier6,
  isValidReference as isValidReference6
} from "@quromedical/fhir-common";
import { compact as compact16, first as first15 } from "@quromedical/utils";
var createFHIRLocationIdentifiers = (location) => {
  const { capacity = 0 } = location;
  return [createIdentifier9(IdentifierCode12.locationCapacity, capacity.toString())];
};
var mapCreateModelToFhirLocation = (location) => {
  const { name, managingOrganization, status: status2, type, description, parent, acuity } = location;
  const identifier = createFHIRLocationIdentifiers(location);
  const fhirType = createCodeableConcept18(
    type,
    LocationCodeDescription[type],
    LocationCodeDescription[type]
  );
  const acuityType = acuity ? createCodeableConcept18(
    acuity,
    LocationAcuityDescription[acuity],
    LocationAcuityDescription[acuity]
  ) : void 0;
  const organization = createReference8("Organization", managingOrganization);
  const partOf = parent ? createReference8("Location", parent) : void 0;
  return {
    resourceType: "Location",
    name,
    managingOrganization: organization,
    status: status2,
    identifier,
    type: compact16([fhirType, acuityType]),
    description,
    partOf
  };
};
var mapUpdateModelToFhirLocation = (location) => {
  const { name, managingOrganization, status: status2, type, description, id, parent, acuity } = location;
  const identifier = createFHIRLocationIdentifiers(location);
  const fhirType = createCodeableConcept18(
    type,
    LocationCodeDescription[type],
    LocationCodeDescription[type]
  );
  const acuityType = acuity ? createCodeableConcept18(
    acuity,
    LocationAcuityDescription[acuity],
    LocationAcuityDescription[acuity]
  ) : void 0;
  const organization = managingOrganization ? createReference8("Organization", managingOrganization) : void 0;
  const partOf = parent ? createReference8("Location", parent) : void 0;
  return {
    id,
    resourceType: "Location",
    name,
    identifier,
    managingOrganization: organization,
    status: status2,
    type: compact16([fhirType, acuityType]),
    description,
    partOf
  };
};
var isLocationTypeCode = (code) => {
  return Object.values(LocationTypeCode2).includes(code);
};
var isAcuityTypeCode = (code) => {
  return Object.values(LocationAcuityCode2).includes(code);
};
var mapFhirLocationToModel = (fhir) => {
  const { name, managingOrganization, status: status2, type, description, id, partOf, identifier } = fhir;
  const modelType = type ? getCodingsFromCodableConcepts(type)?.map((c) => c.code || "").filter(isLocationTypeCode) : void 0;
  const acuity = type ? getCodingsFromCodableConcepts(type)?.map((c) => c.code || "").filter(isAcuityTypeCode) : void 0;
  const organization = isValidReference6(managingOrganization) ? managingOrganization : void 0;
  const partOfOrganization = isValidReference6(partOf) ? partOf : void 0;
  const capacity = getIdentifier6(IdentifierCode12.locationCapacity, identifier) || "0";
  const location = {
    id,
    name,
    managingOrganization: organization,
    status: status2,
    type: first15(modelType),
    description,
    parent: partOfOrganization,
    capacity,
    acuity: first15(acuity)
  };
  const parsed = LocationSchema.safeParse(location);
  if (!parsed.success) {
    throw parsed.error;
  }
  return parsed.data;
};

// src/external-integrations/ampath.ts
var ampath_exports = {};
__export(ampath_exports, {
  XML: () => xml_exports
});

// src/external-integrations/xml.ts
var xml_exports = {};

// src/external-integrations/medprax.ts
var MedpraxTariffMedicalTypeCode = /* @__PURE__ */ ((MedpraxTariffMedicalTypeCode2) => {
  MedpraxTariffMedicalTypeCode2["Mp"] = "MP";
  return MedpraxTariffMedicalTypeCode2;
})(MedpraxTariffMedicalTypeCode || {});

// src/google-places/schema.ts
import { z as z41 } from "zod";
var GooglePlacesPredictionSchema = z41.object({
  description: z41.string(),
  place_id: z41.string(),
  reference: ReferenceSchema
});
var GooglePlacesPlacePredictionSchema = z41.object({
  predictions: z41.array(GooglePlacesPredictionSchema)
});
var GooglePlacesCoordinateSchema = z41.object({
  lat: z41.number(),
  lng: z41.number()
});
var GooglePlacesGeometrySchema = z41.object({
  location: GooglePlacesCoordinateSchema
});
var GooglePlacesAddressComponentsSchema = z41.object({
  long_name: z41.string(),
  short_name: z41.string(),
  types: z41.array(z41.string())
});
var GooglePlacesPlaceSchema = z41.object({
  formatted_address: z41.string(),
  geometry: GooglePlacesGeometrySchema,
  place_id: z41.string(),
  name: z41.string(),
  address_components: z41.array(GooglePlacesAddressComponentsSchema)
});
var GooglePlacesPlaceResultSchema = z41.object({
  result: GooglePlacesPlaceSchema
});
var GooglePlacesSearchAddressQueryParamsSchema = z41.object({
  search: z41.string().optional()
});

// src/google-distance-matrix/validation.ts
import * as yup14 from "yup";
var getDistanceParamsSchema = yup14.object().shape({
  organization: yup14.string().required(),
  patient: yup14.string().required()
});

// src/diagnosis/schema.ts
import { z as z42 } from "zod";
var CreateDiagnosisSchema = z42.object({
  code: DisplayCodeableConceptSchema,
  encounter: DisplayReferenceSchema,
  subject: DisplayReferenceSchema,
  status: DisplayCodeableConceptSchema,
  category: DisplayCodeableConceptSchema
});
var DiagnosisSearchSchema = PaginationSchema.extend({
  code: z42.string().optional(),
  encounter: z42.string().optional(),
  subject: z42.string().optional(),
  status: z42.string().optional()
});
var DiagnosisSchema = CreateDiagnosisSchema.extend({
  id: z42.string().uuid(),
  encounter: DisplayReferenceSchema
});
var GetDiagnosisResponseSchema = DiagnosisSchema.partial();
var DiagnosisGetAllResponseSchema = PaginatedBundleSchema.extend({
  diagnoses: GetDiagnosisResponseSchema.array()
});

// src/diagnosis/mapping.ts
import {
  DiagnosisStatusCode,
  DiagnosisTypeCode as DiagnosisTypeCode3,
  createCodeableConcept as createCodeableConcept19,
  createCoding as createCoding6,
  createReferenceFromResource as createReferenceFromResource2,
  getCodes as getCodes4,
  isValidReference as isValidReference7,
  medpraxSystem as medpraxSystem7,
  createReference as createReference9
} from "@quromedical/fhir-common";
import { first as first16, compact as compact17, uniqby } from "@quromedical/utils";
var mapModelToFhirDiagnosis = (diagnosis) => {
  const { encounter, subject, code, id, category, status: status2 } = diagnosis;
  return {
    resourceType: "Condition",
    id,
    subject,
    code,
    encounter,
    clinicalStatus: status2,
    category: [category]
  };
};
var mapCreateDiagnosisToFhir = (diagnosis) => {
  const { code, encounter, subject, category, status: status2 } = diagnosis;
  return {
    resourceType: "Condition",
    subject,
    encounter,
    code,
    clinicalStatus: status2,
    category: [category]
  };
};
var mapICD10ToCoding = (codes = []) => codes.map((code) => createCoding6(code.Code, code.Description, medpraxSystem7));
var getCodeableConceptCode = (code) => {
  const coding = code ? first16(code.coding) : void 0;
  const codes = coding ? coding.code : void 0;
  const display = coding ? coding.display : void 0;
  const codeableConceptCode = codes ? createCodeableConcept19(codes, codes, display) : void 0;
  return codeableConceptCode;
};
var mapFhirToDiagnosisModel = (fhir) => {
  const { code, subject, encounter, id, category, clinicalStatus } = fhir;
  const subjectReference = isValidReference7(subject) ? subject : void 0;
  const encounterReference = isValidReference7(encounter) ? encounter : void 0;
  const getCode = getCodeableConceptCode(code);
  const getCategory = getCodeableConceptCode(first16(category));
  const getClinicalStatus = getCodeableConceptCode(clinicalStatus);
  return {
    id,
    code: getCode,
    encounter: encounterReference,
    subject: subjectReference,
    status: getClinicalStatus,
    category: getCategory
  };
};
var resolveConditionDifference = (patient, encounter, diagnoses, conditions) => {
  const newConditions = [];
  const currentConditions = [];
  for (const diagnosis of diagnoses) {
    const isDiagnosis = conditions.some((condition) => isSameDiagnosis(diagnosis, condition));
    if (!isDiagnosis) {
      newConditions.push(diagnosis);
    }
  }
  for (const condition of conditions) {
    const isCondition = diagnoses.some((diagnosis) => isSameDiagnosis(diagnosis, condition));
    if (isCondition) {
      currentConditions.push(condition);
    }
  }
  const createCondition = newConditions.map(
    (conditions2) => mapCreateDiagnosisToFhir(createDiagnoses(patient, encounter, conditions2))
  );
  const updatedConditions = compact17(
    conditions.map((condition) => {
      const isCondition = createCondition.find((diagnosis) => {
        const hasCondition = isConditionType(diagnosis, condition);
        if (hasCondition) {
          createCondition.splice(createCondition.indexOf(diagnosis), 1);
        }
        return hasCondition;
      });
      const updateConditionCode = {
        ...condition,
        code: isCondition?.code,
        clinicalStatus: createCodeableConcept19(DiagnosisStatusCode.active)
      };
      const updateConditionStatus = {
        ...condition,
        clinicalStatus: createCodeableConcept19(DiagnosisStatusCode.inactive)
      };
      return isCondition?.code ? updateConditionCode : updateConditionStatus;
    })
  );
  const mapActiveInActiveConditions = compact17(
    conditions.map((condition) => {
      const isCondition = diagnoses.some((diagnosis) => isSameDiagnosis(diagnosis, condition));
      return isCondition ? { ...condition, clinicalStatus: createCodeableConcept19(DiagnosisStatusCode.active) } : void 0;
    })
  );
  const mergeActiveInActiveConditions = [...mapActiveInActiveConditions, ...updatedConditions];
  const resolveConditions = conditions.filter((current) => {
    const updatedCondition2 = !mergeActiveInActiveConditions.some(
      (updatedCondition3) => updatedCondition3.id === current.id
    );
    return updatedCondition2;
  });
  const updatedCondition = [...resolveConditions, ...mergeActiveInActiveConditions];
  const uniqueUpdatedCondition = uniqby(updatedCondition, "id");
  return { postCondition: createCondition, putCondition: uniqueUpdatedCondition };
};
var isSameDiagnosis = (diagnosis, condition) => {
  const conditionCode = diagnosis.condition.code === first16(getCodes4(condition.code));
  const conditionType = diagnosis.type === first16(getCodes4(first16(condition.category)));
  return conditionCode && conditionType;
};
var isConditionType = (diagnosis, condition) => {
  const conditionType = first16(getCodes4(first16(diagnosis?.category))) === first16(getCodes4(first16(condition.category)));
  const conditionCode = first16(getCodes4(diagnosis?.code)) === first16(getCodes4(condition?.code));
  return conditionType && conditionCode;
};
var createDiagnoses = (patient, encounter, diagnoses) => {
  return {
    code: createCodeableConcept19(
      diagnoses.condition.code,
      diagnoses.condition.display,
      diagnoses.condition.display,
      diagnoses.condition.system
    ),
    subject: createReferenceFromResource2(patient),
    encounter: createReferenceFromResource2(encounter),
    status: createCodeableConcept19(DiagnosisStatusCode.active),
    category: createCodeableConcept19(diagnoses.type)
  };
};
var mapConditionToDiagnoses = (conditions) => {
  return conditions.map((c) => {
    return {
      condition: {
        reference: c.resource.id ? createReference9("Condition", c.resource.id).reference : c.fullUrl,
        display: `${first16(getCodes4(c.resource?.code))}: ${c.resource.code?.text}`
      },
      use: first16(c.resource.category)
    };
  });
};
var mapDiagnosesToMedprax = (diagnosesCode, diagnosesIcd10CodesMedprax) => {
  const mapConditionToDiagnosis = diagnosesIcd10CodesMedprax.map(
    (medpraxCode) => {
      const diagnoses = diagnosesCode.find((diagnosis) => {
        const isDiagnosisCode = diagnosis.condition === medpraxCode.code;
        if (isDiagnosisCode) {
          diagnosesCode.splice(diagnosesCode.indexOf(diagnosis), 1);
        }
        return isDiagnosisCode;
      });
      return {
        condition: medpraxCode,
        type: diagnoses?.type
      };
    }
  );
  return mapConditionToDiagnosis;
};
var mapActiveConditions = (diagnosis) => {
  const diagnoses = diagnosis.filter(
    (d) => first16(getCodes4(d.resource.clinicalStatus)) === DiagnosisStatusCode.active
  );
  return compact17(diagnoses);
};
var mapInactiveConditions = (diagnosis) => {
  const diagnoses = diagnosis.filter(
    (d) => first16(getCodes4(d.resource.clinicalStatus)) === DiagnosisStatusCode.inactive
  );
  return compact17(diagnoses);
};
var findReasonOfAdmission = (diagnosesIcd10CodesMedprax, diagnoses) => {
  const rfaDiagnosisIcd10Code = diagnoses?.find((a) => a.type === DiagnosisTypeCode3.rfa);
  return diagnosesIcd10CodesMedprax.find((c) => c.code === rfaDiagnosisIcd10Code?.condition);
};

// src/device-association/schema.ts
import { DeviceUseStatementCode } from "@quromedical/fhir-common";
import { z as z43 } from "zod";
var deviceAssociationStatus = [
  "active",
  "completed",
  "entered-in-error",
  "intended",
  "stopped",
  "on-hold"
];
var DeviceAssociationStatusSchema = z43.enum(deviceAssociationStatus);
var DeviceAssociationCodeSchema = z43.nativeEnum(DeviceUseStatementCode);
var DeviceAssociationSchema = z43.object({
  patient: DisplayReferenceSchema,
  device: DisplayReferenceSchema,
  code: DeviceAssociationCodeSchema,
  status: DeviceAssociationStatusSchema,
  period: PeriodSchema
});
var searchParamSchema = z43.object({
  device: z43.string().optional(),
  subject: z43.string().optional()
});
var tableStatement = z43.object({
  id: z43.string().uuid(),
  start: z43.date(),
  end: z43.date(),
  type: z43.string(),
  code: DeviceAssociationCodeSchema,
  device: DisplayReferenceSchema,
  patient: DisplayReferenceSchema,
  isActive: z43.boolean(),
  status: DeviceAssociationStatusSchema
});
var DeviceAssociationGetResponseSchema = DeviceAssociationSchema.partial().extend({
  id: UUIDSchema
});
var DeviceAssociationWithIdSchema = DeviceAssociationSchema.partial().extend({
  id: z43.string().uuid()
});
var DeviceAssociationCreateRequestSchema = z43.object({
  device: UUIDSchema,
  patient: UUIDSchema
});
var DeviceAssociationGetAllParamsSchema = PaginatedBundleSchema.extend({
  associations: DeviceAssociationGetResponseSchema.array()
});
var DeviceAssociationSearchSchema = PaginationSchema.merge(searchParamSchema);

// src/device-association/mapping.ts
import {
  createCodeableConcept as createCodeableConcept20,
  createFhirDate as createFhirDate4,
  createFhirDateTime as createFhirDateTime4,
  createReferenceFromResource as createReferenceFromResource3,
  DeviceLifecycleMap,
  DeviceUseStatementCode as DeviceUseStatementCode2,
  getDeviceFriendlyName,
  getDeviceUseStatementOverlapPeriods,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts12,
  getPatientNameDisplay,
  isDeviceUseStatementActive,
  isDeviceUseStatementOfType,
  mapFhirToModelPeriod as mapFhirToModelPeriod3
} from "@quromedical/fhir-common";
import { IntervalSchema } from "@quromedical/utils";
import { compact as compact18, concat } from "@quromedical/utils";
import { z as z44 } from "zod";
var deviceCodeToDeviceUseStatementCode = {
  "device.patch": DeviceUseStatementCode2.patch,
  "device.band": DeviceUseStatementCode2.band,
  "device.bloodpressure": DeviceUseStatementCode2.bloodPressure,
  "device.glucose": DeviceUseStatementCode2.glucose,
  "device.relay": DeviceUseStatementCode2.relay
};
var TimeSeriesPatientQuerySchema = z44.object({
  patientId: z44.string().uuid(),
  interval: IntervalSchema,
  measurementNames: MeasurementNameSchema.array().readonly()
});
var endDeviceUseStatement = (deviceUseStatement, status2) => {
  const { timingPeriod, timingDateTime } = deviceUseStatement;
  const end = createFhirDateTime4(/* @__PURE__ */ new Date());
  const start = timingPeriod?.start || timingDateTime;
  const newPeriod = {
    start,
    end
  };
  const result = {
    ...deviceUseStatement,
    status: status2,
    timingPeriod: newPeriod,
    timingDateTime: void 0
  };
  return result;
};
var completeDisposableDeviceUseStatements = (deviceUseStatements) => {
  const disposableDeviceUseStatements = deviceUseStatements.filter((deviceUseStatement) => {
    if (deviceUseStatement.status !== "active") {
      return false;
    }
    const deviceUseStatementCode = getFirstCodeFromCodableConcepts12(deviceUseStatement.reasonCode);
    if (!deviceUseStatementCode) {
      return false;
    }
    const recyclable = DeviceLifecycleMap[deviceUseStatementCode];
    return recyclable.lifecycle === "disposable";
  });
  const updatedDeviceUseStatements = disposableDeviceUseStatements.map(
    (deviceUseStatement) => endDeviceUseStatement(deviceUseStatement, "stopped")
  );
  return updatedDeviceUseStatements;
};
var completeReusableDeviceUseStatements = (deviceUseStatements) => {
  const reusableDeviceUseStatements = deviceUseStatements.filter((deviceUseStatement) => {
    if (deviceUseStatement.status !== "active") {
      return false;
    }
    const deviceUseStatementCode = getFirstCodeFromCodableConcepts12(deviceUseStatement.reasonCode);
    if (!deviceUseStatementCode) {
      return false;
    }
    const recyclable = DeviceLifecycleMap[deviceUseStatementCode];
    return recyclable.lifecycle === "reusable";
  });
  const updatedDeviceUseStatements = reusableDeviceUseStatements.map(
    (deviceUseStatement) => endDeviceUseStatement(deviceUseStatement, "completed")
  );
  return updatedDeviceUseStatements;
};
var endDeviceUseStatements = (deviceUseStatements) => {
  const deviceUseStatementsResult = concat(
    completeReusableDeviceUseStatements(deviceUseStatements),
    completeDisposableDeviceUseStatements(deviceUseStatements)
  );
  return deviceUseStatementsResult;
};
var hasAnyActiveDeviceOfType = (deviceUseStatements = [], deviceUseStatementType) => {
  const filteredDeviceUseStatements = deviceUseStatements.filter(
    (d) => isDeviceUseStatementOfType(d, deviceUseStatementType)
  );
  return filteredDeviceUseStatements.some(isDeviceUseStatementActive);
};
var mapCreateModelToDeviceUseStatement = (patient, device, code) => ({
  resourceType: "DeviceUseStatement",
  status: "active",
  device: createReferenceFromResource3(device, getDeviceFriendlyName(device)),
  subject: createReferenceFromResource3(patient, getPatientNameDisplay(patient)),
  reasonCode: [createCodeableConcept20(code)],
  timingPeriod: {
    start: createFhirDate4(/* @__PURE__ */ new Date())
  }
});
var mapModelToDeviceUseStatement = (model) => ({
  resourceType: "DeviceUseStatement",
  status: model.status,
  device: model.device,
  subject: model.patient,
  reasonCode: [createCodeableConcept20(model.code)],
  timingPeriod: model.period
});
var mapDeviceUseStatementToModel = (fhir) => ({
  id: fhir.id,
  status: fhir.status,
  device: getValidReference(fhir.device),
  patient: getValidReference(fhir.subject),
  code: getFirstCodeFromCodableConcepts12(fhir.reasonCode),
  period: mapFhirToModelPeriod3(fhir.timingPeriod)
});
var mapDeviceUseStatementsToTimeSeriesQueries = ({ deviceUseStatements, measurementNames, start, end }) => {
  const associations = getDeviceUseStatementOverlapPeriods({
    deviceUseStatements,
    from: start.getTime(),
    to: end.getTime()
  });
  const queries = compact18(associations).map(({ start: start2, end: end2, deviceName }) => ({
    interval: {
      start: start2,
      end: end2
    },
    sensorId: deviceName,
    measurementNames
  }));
  return queries;
};

// src/pathology/validation.ts
import * as yup15 from "yup";
var createPathologyOrderSchema = yup15.object({
  comment: yup15.string().optional(),
  sampleDateTime: yup15.date().required(),
  testCodes: yup15.array(yup15.string().required()).required().min(1),
  copyDoctors: yup15.array(yup15.string().notRequired()).optional()
}).required();

// src/pathology/schema.ts
import { ServiceRequestPerformerIdentifier } from "@quromedical/fhir-common";
import { z as z45 } from "zod";
var pathologyOrderStatus = [
  "draft",
  "active",
  "on-hold",
  "revoked",
  "completed",
  "entered-in-error",
  "unknown"
];
var PathologyOrderSchema = z45.object({
  patient: DisplayReferenceSchema,
  requestor: DisplayReferenceSchema,
  testCodes: z45.array(CodingSchema).min(1),
  orderNumber: z45.string().optional(),
  orderId: z45.string().optional(),
  status: z45.enum(pathologyOrderStatus),
  /**
   * The date/time that the physical sample was taken from the patient
   */
  sampleDateTime: z45.string().datetime(),
  copyDoctors: z45.array(DisplayReferenceSchema).optional(),
  comment: z45.string().optional()
});
var PathologyOrderGetResponseSchema = PathologyOrderSchema.partial();
var PathologyOrderGetResponseWithIdSchema = PathologyOrderGetResponseSchema.partial().extend({
  id: UUIDSchema
});
var PathologyOrderGetAllResponseSchema = PaginatedBundleSchema.extend({
  orders: PathologyOrderGetResponseWithIdSchema.array()
});
var PathologyOrderCreateRequestBaseSchema = z45.object({
  testCodes: z45.array(z45.string()).min(1),
  sampleDateTime: z45.string(),
  copyDoctors: z45.array(z45.string()).optional(),
  comment: z45.string().optional()
});
var PathologyOrderCreateRequestSchema = PathologyOrderCreateRequestBaseSchema.extend({
  patient: UUIDSchema
});
var PathologyPatientOrderCreateSchema = PathologyOrderCreateRequestSchema.extend({
  patient: UUIDSchema
});
var PathologyOrderGetAllParamsSchema = z45.object({
  subject: UUIDSchema.optional()
});
var pathologyReportStatus = [
  "registered",
  "partial",
  "preliminary",
  "final",
  "amended",
  "corrected",
  "appended",
  "cancelled",
  "entered-in-error",
  "unknown"
];
var pathologyReportContentTypes = ["application/pdf"];
var PathologyPerformerCodeSchema = z45.nativeEnum(ServiceRequestPerformerIdentifier);
var PathologyReportItemSchema = z45.object({
  contentType: z45.enum(pathologyReportContentTypes),
  hash: z45.string(),
  title: z45.string(),
  url: z45.string().url()
});
var PathologyReportSchema = z45.object({
  patient: DisplayReferenceSchema,
  orderNumber: z45.string().optional(),
  status: z45.enum(pathologyReportStatus),
  performer: PathologyPerformerCodeSchema,
  issued: z45.string().datetime(),
  order: DisplayReferenceSchema,
  reports: PathologyReportItemSchema.array().min(1),
  results: ObservationPathologySchema.optional()
});
var PathologyReportGetAllParamsSchema = z45.object({
  subject: UUIDSchema.optional(),
  _include: z45.string().optional()
});
var PathologyReportGetResponseSchema = PathologyReportSchema.partial().extend({
  id: UUIDSchema
});
var PathologyReportGetAllResponseSchema = PaginatedBundleSchema.extend({
  reports: PathologyReportGetResponseSchema.extend({
    id: UUIDSchema
  }).array()
});
var PathologyProvider = /* @__PURE__ */ ((PathologyProvider2) => {
  PathologyProvider2["lancet"] = "pathology.provider.lancet";
  return PathologyProvider2;
})(PathologyProvider || {});
var PathologyProviderSchema = z45.nativeEnum(PathologyProvider);
var PathologyCreateOrderRequestSchema = z45.object({
  doctors: z45.string().array().describe("IDs of doctors/practices you want to cc results to").optional(),
  comment: z45.string().optional().describe("Allow practitioner/user to add comment"),
  tests: z45.string().array().min(1).describe("The tests that will be submitted to the pathology provider"),
  collectionDateTime: DateTimeSchema,
  patient: z45.string().describe("Patient ID"),
  provider: PathologyProviderSchema,
  practitioner: z45.string().describe("Practitioner ID")
});
var PathologyGetTestsRequestSchema = z45.object({
  limit: z45.number().default(10),
  query: z45.string().optional(),
  provider: PathologyProviderSchema
});
var PathologyGetTestsResponseSchema = z45.object({
  description: z45.string(),
  code: z45.string()
}).array();
var PathologyReportWithObservationsResponseSchema = z45.object({
  pathology: PathologyReportGetResponseSchema.extend({
    id: UUIDSchema
  }),
  observations: ObservationResultSchema.array().optional()
});
var PathologyReportGetAllObservationsResponseSchema = PaginatedBundleSchema.extend({
  results: PathologyReportWithObservationsResponseSchema.array()
});
var PathologyCreateOrderWebRequestSchema = PathologyCreateOrderRequestSchema.omit({
  practitioner: true
});
var PathologyCreateOrderFormRequestSchema = PathologyCreateOrderRequestSchema.omit({
  practitioner: true,
  provider: true,
  patient: true
});

// src/pathology/order/pathologyOrder.ts
import {
  ExtensionUrl as ExtensionUrl11,
  IdentifierCode as IdentifierCode13,
  ServiceRequestCode,
  ServiceRequestIdentifier,
  ServiceRequestPerformerIdentifier as ServiceRequestPerformerIdentifier2,
  ampathSystem as ampathSystem2,
  createCodeableConcept as createCodeableConcept21,
  createIdentifier as createIdentifier10,
  getExtensionValue,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts13,
  getIdentifier as getIdentifier7,
  isValidReference as isValidReference8,
  system as system5
} from "@quromedical/fhir-common";
import { compact as compact19, first as first17 } from "@quromedical/utils";
var mapModelToFhirServiceRequest = (pathologyOrder) => {
  const {
    patient,
    requestor,
    sampleDateTime,
    testCodes,
    comment,
    copyDoctors,
    status: status2,
    orderNumber
  } = pathologyOrder;
  const orderDetail = testCodes.map((test) => {
    const { code, display } = test;
    return createCodeableConcept21(code, display, display, ampathSystem2);
  });
  return {
    resourceType: "ServiceRequest",
    subject: patient,
    requester: requestor,
    occurrenceDateTime: sampleDateTime,
    orderDetail,
    note: comment ? [{ text: comment }] : void 0,
    extension: copyDoctors ? copyDoctors.map((copyDoctor) => ({
      url: ExtensionUrl11.serviceRequestCopyPractitioners,
      valueReference: isValidReference8(copyDoctor) ? copyDoctor : void 0
    })) : void 0,
    intent: "order",
    code: createCodeableConcept21(
      ServiceRequestCode.PathologyRequest,
      ServiceRequestCode.PathologyRequest,
      ServiceRequestCode.PathologyRequest,
      system5
    ),
    performer: [
      {
        identifier: createIdentifier10(
          IdentifierCode13.serviceRequestPerformer,
          ServiceRequestPerformerIdentifier2.Ampath,
          ampathSystem2
        )
      }
    ],
    priority: "stat",
    status: status2,
    identifier: orderNumber ? [createIdentifier10(ServiceRequestIdentifier.AmpathOrderNumber, orderNumber, ampathSystem2)] : void 0
  };
};
var mapOrderDetailToTestCodes = (orderDetail) => {
  if (!orderDetail) {
    return void 0;
  }
  const testCodes = orderDetail?.map((detail) => {
    const code = getFirstCodeFromCodableConcepts13([detail]);
    const display = detail.text;
    if (!code) {
      return void 0;
    }
    return {
      code,
      display
    };
  });
  return compact19(testCodes);
};
var getLancetIdentifiers = (identifier) => {
  const orderNumber = getIdentifier7(ServiceRequestIdentifier.LancetOrderNumber, identifier);
  const orderId = getIdentifier7(ServiceRequestIdentifier.LancetOrderId, identifier);
  return {
    orderNumber,
    orderId
  };
};
var getAmpathIdentifiers = (identifier) => {
  const orderNumber = getIdentifier7(ServiceRequestIdentifier.AmpathOrderNumber, identifier);
  return {
    orderNumber,
    orderId: "NA"
  };
};
var mapFhirServiceRequestToModel = (fhir) => {
  const {
    id,
    subject,
    requester,
    status: status2,
    occurrenceDateTime,
    orderDetail,
    note,
    extension,
    identifier
  } = fhir;
  const getCopyDoctors = getExtensionValue(ExtensionUrl11.serviceRequestCopyPractitioners);
  const copyDoctors = extension?.map(getCopyDoctors).filter(isValidReference8);
  const { orderNumber, orderId } = getIdentifier7(ServiceRequestIdentifier.AmpathOrderNumber, identifier) == void 0 ? getLancetIdentifiers(identifier) : getAmpathIdentifiers(identifier);
  return {
    id,
    status: status2,
    sampleDateTime: occurrenceDateTime ? occurrenceDateTime : void 0,
    comment: note ? first17(note)?.text : void 0,
    copyDoctors,
    orderNumber,
    orderId,
    requestor: isValidReference8(requester) ? requester : void 0,
    patient: isValidReference8(subject) ? subject : void 0,
    testCodes: mapOrderDetailToTestCodes(orderDetail)
  };
};

// src/pathology/report/pathologyReport.ts
import {
  DiagnosticReportIdentifier,
  IdentifierCode as IdentifierCode14,
  ServiceRequestCode as ServiceRequestCode2,
  ServiceRequestPerformerIdentifier as ServiceRequestPerformerIdentifier3,
  ampathSystem as ampathSystem3,
  createCodeableConcept as createCodeableConcept22,
  createFhirDateTime as createFhirDateTime5,
  getIdentifier as getIdentifier8,
  isValidReference as isValidReference9,
  system as system6,
  createIdentifier as createIdentifier11
} from "@quromedical/fhir-common";
import { compact as compact20, first as first18 } from "@quromedical/utils";
var mapModelToFhirDiagnosticReport = (pathologyReport) => {
  const { patient, status: status2, orderNumber, issued, order, reports } = pathologyReport;
  const presentedForm = reports.map((report) => ({
    contentType: report.contentType,
    hash: report.hash,
    title: report.title,
    url: report.url
  }));
  return {
    resourceType: "DiagnosticReport",
    subject: patient,
    basedOn: order ? [order] : void 0,
    effectiveDateTime: createFhirDateTime5(new Date(issued)),
    code: createCodeableConcept22(
      ServiceRequestCode2.PathologyRequest,
      ServiceRequestCode2.PathologyRequest,
      ServiceRequestCode2.PathologyRequest,
      system6
    ),
    performer: [
      {
        identifier: createIdentifier11(
          IdentifierCode14.serviceRequestPerformer,
          ServiceRequestPerformerIdentifier3.Ampath,
          ampathSystem3
        )
      }
    ],
    status: status2,
    identifier: orderNumber ? [createIdentifier11(DiagnosticReportIdentifier.AmpathOrderNumber, orderNumber, ampathSystem3)] : void 0,
    presentedForm
  };
};
var mapFhirDiagnosticReportToModel = (fhir) => {
  const { id, subject, status: status2, effectiveDateTime, identifier, presentedForm, performer, basedOn } = fhir;
  const reports = presentedForm?.map((form) => {
    if (!form.contentType || !form.hash || !form.title || !form.url) {
      return void 0;
    }
    const report = {
      contentType: "application/pdf",
      hash: form.hash,
      title: form.title,
      url: form.url
    };
    return report;
  });
  const ampathOrderNo = getIdentifier8(DiagnosticReportIdentifier.AmpathOrderNumber, identifier);
  const lancetOrderNo = getIdentifier8(DiagnosticReportIdentifier.LancetOrderNumber, identifier);
  const orderNumber = ampathOrderNo == void 0 ? lancetOrderNo : ampathOrderNo;
  const serviceRequest = first18(basedOn);
  return {
    id,
    status: status2,
    order: isValidReference9(serviceRequest) ? serviceRequest : void 0,
    issued: effectiveDateTime ? effectiveDateTime : void 0,
    orderNumber,
    performer: first18(performer)?.identifier ? ServiceRequestPerformerIdentifier3.Ampath : void 0,
    patient: isValidReference9(subject) ? subject : void 0,
    reports: compact20(reports)
  };
};

// src/pathology/mapping.ts
import {
  ampathPerformerReference,
  ampathSystem as ampathSystem4,
  createCodeableConcept as createCodeableConcept23,
  ServiceRequestCode as ServiceRequestCode3,
  ServiceRequestIdentifier as ServiceRequestIdentifier2,
  valsOrNone as valsOrNone15,
  createIdentifier as createIdentifier12
} from "@quromedical/fhir-common";
import { ExtensionUrl as ExtensionUrl12 } from "@quromedical/fhir-common";
import { createExtensionValue as createExtensionValue10 } from "@quromedical/fhir-common";
import { createFormatter, formatter } from "@quromedical/utils";
import { first as first19 } from "@quromedical/utils";
import { format } from "date-fns";
import { z as z46 } from "zod";
var RequiredAdmissionMedicalAidAuthorizationSchema = z46.object({
  code: z46.string()
});
var RequiredPatientSchema = PartialPersonSchema;
var RequiredAdmissionSchema = AdmissionGetResponseSchema.pick({
  icd10Codes: true
}).extend({
  medicalAidAuthorization: RequiredAdmissionMedicalAidAuthorizationSchema.optional()
});
var RequiredMedicalAidMainMemberSchema = MedicalAidPersonSchema.pick({
  contact: true,
  general: true
}).partial();
var RequiredMedicalAidMembershipSchema = MedicalAidMembershipSchema.pick({
  dependantCode: true,
  medicalAid: true
}).partial();
var toAmpathDate = (isoDateTime) => {
  if (!isoDateTime) {
    return void 0;
  }
  const timestamp = new Date(isoDateTime).getTime();
  return format(timestamp, "yyyy-MM-dd");
};
var toAmpathTime = (isoDateTime) => {
  if (!isoDateTime) {
    return void 0;
  }
  const timestamp = new Date(isoDateTime).getTime();
  return format(timestamp, "hh:mm:ss");
};
var genderMapping = {
  female: "F",
  male: "M",
  other: "U",
  unknown: "U"
};
var getAmpathGender = (gender = "unknown") => gender && genderMapping[gender] || "U";
var getAmpathName = (general = {}) => {
  const { familyName = "", givenName = "" } = general;
  return `${familyName}, ${givenName}`;
};
var getPatient2 = (patient) => {
  const gender = getAmpathGender(patient.general.gender);
  const name = getAmpathName(patient.general);
  const shortName = name.slice(0, 30);
  const phone = first19(patient.contact?.telecomPhoneNumbers);
  const idNo = patient.general.identifierValue;
  return {
    gender,
    name: shortName,
    homeTelNo: phone,
    idNo
  };
};
var getGuarantor = ({
  patient,
  admission,
  medicalAidMainMember,
  medicalAidMembership
}) => {
  const name = getAmpathName(medicalAidMainMember.general);
  const medicalAidName = medicalAidMembership.medicalAid?.schemeName;
  const medicalAidNo = medicalAidMembership?.medicalAid?.membershipNumber;
  const medicalAidDependantCode = parseInt(medicalAidMembership.dependantCode || "0", 10);
  if (!(medicalAidName && medicalAidNo)) {
    throw new Error("Invalid medical aid name or number");
  }
  const { general: mainMemberGeneral = {}, contact: mainMemberContact = {} } = medicalAidMainMember;
  const patientContact = patient.contact;
  const addressline1 = first19(mainMemberContact.addressLines) || first19(patientContact.addressLines);
  const addresscity = mainMemberContact.addressCity || patientContact.addressCity;
  if (!(addresscity && addressline1)) {
    throw new Error("Guarantor address and city are required");
  }
  const idNo = mainMemberGeneral.identifierValue || "NA";
  const phoneNumber = first19(mainMemberContact.telecomPhoneNumbers) || "NA";
  const homeTelNo = phoneNumber;
  const cellphoneNo = phoneNumber;
  const medicalAidAuthorizationNo = admission.medicalAidAuthorization?.code;
  const shortMedicalAidName = medicalAidName.slice(0, 10);
  const shortAddressCity = addresscity.slice(0, 40);
  const shortName = name.slice(0, 30);
  return {
    addresscity: shortAddressCity,
    addressline1,
    homeTelNo,
    cellphoneNo,
    name: shortName,
    idNo,
    medicalAidName: shortMedicalAidName,
    medicalAidNo,
    medicalAidDependantCode,
    medicalAidAuthorizationNo
  };
};
var CreateAmpathOrderModelPropsSchema = z46.object({
  doctorAlias: z46.string(),
  createRequest: PathologyOrderCreateRequestBaseSchema,
  patient: RequiredPatientSchema,
  admission: RequiredAdmissionSchema,
  medicalAidMembership: RequiredMedicalAidMembershipSchema,
  medicalAidMainMember: RequiredMedicalAidMainMemberSchema
});
var mapOrderToAmpathModel = ({
  createRequest,
  doctorAlias: doctor,
  patient,
  medicalAidMembership,
  medicalAidMainMember,
  admission
}) => {
  const { comment, testCodes: orderedTests, sampleDateTime, copyDoctors = [] } = createRequest;
  const ampathPatient = getPatient2(patient);
  const guarantor = getGuarantor({
    patient,
    medicalAidMembership,
    medicalAidMainMember,
    admission
  });
  const collectionDate = toAmpathDate(sampleDateTime);
  const collectionTime = toAmpathTime(sampleDateTime);
  const admissionIcd10Codes = admission.icd10Codes?.map((code) => code.code);
  const admissionIcd10Code = admissionIcd10Codes?.join(",");
  const hospital = {
    name: "Quro Medical Sent In"
  };
  return {
    doctor,
    priority: "S",
    collectionDate,
    collectionTime,
    orderedTests,
    comment,
    patient: ampathPatient,
    guarantor,
    admissionIcd10Code,
    copyDoctors,
    // these will always be empty for our case
    admissionQuerries: [],
    orderQuerries: [],
    hospital
  };
};
var createCopyDoctorExtension = createExtensionValue10(
  ExtensionUrl12.serviceRequestCopyPractitioners
);
var createCopyDoctorsExtension = (copyDoctor) => {
  if (!copyDoctor) {
    return void 0;
  }
  const extension = createCopyDoctorExtension(copyDoctor);
  return extension;
};
var createAmpathServiceRequestFromOrder = ({ form, orderNumber, priority = "stat", status: status2 = "draft" }) => {
  const {
    sampleDateTime: occurrenceDateTime,
    testCodes,
    comment,
    patient: subject,
    requestor: requester,
    copyDoctors = []
  } = form;
  const orderDetail = testCodes.map(
    (test) => createCodeableConcept23(test.code, test.display, ampathSystem4)
  );
  const code = createCodeableConcept23(ServiceRequestCode3.PathologyRequest);
  const identifier = [
    createIdentifier12(ServiceRequestIdentifier2.AmpathOrderNumber, orderNumber)
  ];
  const note = comment ? [
    {
      text: comment
    }
  ] : void 0;
  const extension = valsOrNone15(copyDoctors.map(createCopyDoctorsExtension));
  return {
    resourceType: "ServiceRequest",
    intent: "order",
    status: status2,
    priority,
    orderDetail,
    occurrenceDateTime,
    subject,
    code,
    note,
    requester,
    identifier,
    performer: [ampathPerformerReference],
    extension
  };
};
var formatAmpathPayload = createFormatter({
  guarantor: {
    idNo: formatter.length(15),
    homeTelNo: formatter.length(18),
    cellphoneNo: formatter.length(18),
    addresscity: formatter.length(40),
    addressline1: formatter.length(30),
    medicalAidNo: formatter.length(15),
    medicalAidName: formatter.length(15),
    email: formatter.optional(formatter.length(40)),
    medicalAidDependantCode: formatter.optional(formatter.round),
    medicalAidAuthorizationNo: formatter.optional(formatter.length(15))
  },
  patient: {
    name: formatter.length(30),
    idNo: formatter.optional(formatter.length(13)),
    title: formatter.optional(formatter.length(10)),
    homeTelNo: formatter.optional(formatter.length(18)),
    workTelNo: formatter.optional(formatter.length(18)),
    reference: formatter.optional(formatter.length(20)),
    dob: formatter.optional(toAmpathDate)
  },
  admissionIcd10Code: formatter.optional(formatter.length(7)),
  orderedTests: formatter.optional(formatter.array(formatter.length(13))),
  //ampath internal use
  admissionQuerries: formatter.constant([]),
  orderQuerries: formatter.constant([]),
  copyDoctors: formatter.array(formatter.length(10)),
  hospital: {
    name: formatter.optional(formatter.trim)
  }
});
var createAmpathOrderModel = (order) => {
  const request = mapOrderToAmpathModel(order);
  return formatAmpathPayload(request);
};

// src/care-team/mapping.ts
import {
  createCodeableConcept as createCodeableConcept24,
  createReference as createReference10,
  isValidReference as isValidReference10,
  mapFhirToModelPeriod as mapFhirToModelPeriod4
} from "@quromedical/fhir-common";
import { compact as compact21, first as first20 } from "@quromedical/utils";
var mapCreateModelToFhirCareTeam = (careTeam) => {
  const { name, status: status2, period: period2, managingOrganization, patient, admission, teamMembers } = careTeam;
  const organizations = compact21(
    managingOrganization?.map((organization) => createReference10("Organization", organization))
  );
  const subject = patient ? createReference10("Patient", patient) : void 0;
  const encounter = admission ? createReference10("Encounter", admission) : void 0;
  const participant = compact21(
    teamMembers.map((team) => {
      return {
        role: team.role.map((code) => createCodeableConcept24(code)),
        member: createReference10("Practitioner", team.member)
      };
    })
  );
  return {
    resourceType: "CareTeam",
    name,
    status: status2,
    period: period2,
    managingOrganization: organizations,
    subject,
    encounter,
    participant
  };
};
var mapUpdateModelToFhirCareTeam = (careTeam) => {
  const { id } = careTeam;
  const careTeams = mapCreateModelToFhirCareTeam(careTeam);
  return {
    id,
    ...careTeams
  };
};
var mapModelToFhirCareTeam = (careTeam) => {
  const { id, status: status2, name, period: period2, managingOrganization, patient, admission, teamMembers } = careTeam;
  return {
    id,
    resourceType: "CareTeam",
    name,
    status: status2,
    period: period2,
    managingOrganization,
    subject: patient,
    encounter: admission,
    participant: teamMembers
  };
};
var getCodeableConceptCode2 = (code) => {
  const coding = code ? first20(code.coding) : void 0;
  const codes = coding ? coding.code : void 0;
  const display = coding ? coding.display : void 0;
  const codeableConceptCode = codes ? createCodeableConcept24(codes, codes, display) : void 0;
  return codeableConceptCode;
};
var mapFhirCareTeamToModel = (fhir) => {
  const { id, name, status: status2, period: period2, managingOrganization, subject, encounter, participant } = fhir;
  const organizations = compact21(
    managingOrganization?.map(
      (organization) => isValidReference10(organization) ? organization : void 0
    )
  );
  const subjectReference = isValidReference10(subject) ? subject : void 0;
  const encounterReference = isValidReference10(encounter) ? encounter : void 0;
  const teamMembers = compact21(
    participant?.map((team) => {
      const member = isValidReference10(team.member) ? team.member : void 0;
      if (!member) {
        return void 0;
      }
      return {
        role: compact21(team.role?.map(getCodeableConceptCode2)),
        member
      };
    })
  );
  return {
    id,
    name,
    status: status2,
    period: mapFhirToModelPeriod4(period2),
    managingOrganization: organizations,
    patient: subjectReference,
    admission: encounterReference,
    teamMembers
  };
};

// src/care-team/schema.ts
import { z as z47 } from "zod";
var SupportedCareTeamStatus = [
  "proposed",
  "active",
  "suspended",
  "inactive",
  "entered-in-error"
];
var CareTeamStatusSchema = z47.enum(SupportedCareTeamStatus);
var TeamMembersSchema = z47.object({
  role: z47.string().array(),
  member: z47.string()
});
var CareTeamSearchSchema = PaginationSchema.extend({
  name: z47.string().optional(),
  status: CareTeamStatusSchema.optional(),
  patient: UUIDSchema.optional(),
  admission: UUIDSchema.optional()
});
var ParticipantSchema = z47.object({
  role: DisplayCodeableConceptSchema.array(),
  member: DisplayReferenceSchema
});
var CareTeamSchema = z47.object({
  id: UUIDSchema,
  name: z47.string(),
  period: PeriodSchema,
  status: CareTeamStatusSchema,
  managingOrganization: DisplayReferenceSchema.array(),
  patient: DisplayReferenceSchema.optional(),
  admission: DisplayReferenceSchema.optional(),
  teamMembers: ParticipantSchema.array()
});
var CreateCareTeamSchema = z47.object({
  name: z47.string(),
  period: PeriodSchema,
  status: CareTeamStatusSchema,
  managingOrganization: z47.string().array(),
  patient: UUIDSchema.optional(),
  admission: UUIDSchema.optional(),
  teamMembers: TeamMembersSchema.array()
});
var CareTeamValidationSchema = z47.object({
  name: z47.string(),
  status: CareTeamStatusSchema,
  managingOrganization: z47.string(),
  patient: UUIDSchema.optional(),
  admission: UUIDSchema.optional()
});
var TeamMembersValidationSchema = z47.object({
  role: z47.string(),
  member: z47.string()
});
var UpdateCareTeamSchema = z47.object({
  id: UUIDSchema,
  name: z47.string(),
  period: PeriodSchema,
  status: CareTeamStatusSchema,
  managingOrganization: z47.string().array(),
  teamMembers: TeamMembersSchema.array(),
  patient: UUIDSchema.optional(),
  admission: UUIDSchema.optional()
});
var CareTeamResponseSchema = CareTeamSchema.partial();
var CareTeamGetAllResponseSchema = PaginatedBundleSchema.extend({
  careTeams: CareTeamResponseSchema.array()
});

// src/mdm/schema.ts
import { z as z48 } from "zod";
var MDMGeolocationPositionsSchema = z48.object({
  address: z48.string(),
  date: z48.date(),
  latitude: z48.number(),
  longitude: z48.number(),
  timestamp: z48.number()
});
var MDMStatusItemSchema = z48.union([z48.any(), z48.array(MDMGeolocationPositionsSchema)]);
var MDMStatusSchema = z48.record(z48.string(), MDMStatusItemSchema.optional());
var MDMStatusResponseSchema = MDMStatusSchema;
var MDMRebootDeviceResponseSchema = z48.object({
  isSuccessful: z48.boolean()
});

// src/alert/validation.ts
import * as yup16 from "yup";
var alertCommentCreateSchema = yup16.object({
  alertTs: yup16.number().positive().required(),
  classification: yup16.string().required(),
  details: yup16.string().optional(),
  quality: yup16.string().required()
}).required();

// src/relay/schema.ts
import z49 from "zod";
var createRelayStatusFieldSchema = (valueSchema) => {
  return z49.object({
    timestamp: z49.number(),
    value: valueSchema
  });
};
var RelayStatusFieldSchema = createRelayStatusFieldSchema(z49.any());
var NotificationSchema = z49.object({
  id: z49.number(),
  timestamp: z49.number(),
  channel: z49.string(),
  title: z49.string().optional(),
  content: z49.string().optional()
});
var RelayStatusSchema = z49.object({
  timestamp: z49.number().optional(),
  // PhoneStatusStream
  batteryIsCharging: createRelayStatusFieldSchema(z49.boolean()).optional(),
  batteryPercent: createRelayStatusFieldSchema(z49.number()).optional(),
  bluetoothGattConnectedDevices: createRelayStatusFieldSchema(z49.array(z49.string())).optional(),
  bluetoothIsOn: createRelayStatusFieldSchema(z49.boolean()).optional(),
  ringerIsEnabled: createRelayStatusFieldSchema(z49.boolean()).optional(),
  phoneNumber: createRelayStatusFieldSchema(z49.string()).optional(),
  mbsAppVersion: createRelayStatusFieldSchema(z49.string()).optional(),
  relayId: createRelayStatusFieldSchema(z49.string()).optional(),
  relayAppVersion: createRelayStatusFieldSchema(z49.string()).optional(),
  availableStorageMB: createRelayStatusFieldSchema(z49.number()).optional(),
  androidSoftwareVersion: createRelayStatusFieldSchema(z49.string()).optional(),
  uptimeMillis: createRelayStatusFieldSchema(z49.number()).optional(),
  // MBSAppStatusStream
  mbsAppStatus: createRelayStatusFieldSchema(z49.string()).optional(),
  // NetworkStatusStream
  cellularNetworkType: createRelayStatusFieldSchema(z49.string()).optional(),
  cellularNetworkLevel: createRelayStatusFieldSchema(z49.number()).optional(),
  activeNotifications: createRelayStatusFieldSchema(z49.array(NotificationSchema)).optional()
});
var RelayStatusGetAllResponseSchema = z49.object({
  relayStatuses: RelayStatusSchema.array()
});
var RelayStatusRequestSchema = z49.object({
  deviceIds: z49.string()
});

// src/specialty/schema.ts
import { z as z50 } from "zod";
var SpecialtySchema = z50.object({
  code: z50.string(),
  name: z50.string()
});

// src/specialty/mapping.ts
var mapMedpraxSpecialtyToSpecialty = (specialty) => {
  return {
    code: specialty.Code,
    name: specialty.Description
  };
};

// src/templates/schema.ts
import { TemplateNames } from "@quromedical/communication";
import { TemplateTypeCode } from "@quromedical/fhir-common";
import { z as z51 } from "zod";
var TemplateTypeSchema = z51.nativeEnum(TemplateTypeCode);
var TemplateNameSchema = z51.nativeEnum(TemplateNames);
var MessageTemplateSchema = z51.object({
  id: UUIDSchema,
  name: TemplateNameSchema.or(z51.string()),
  content: z51.string(),
  templateId: z51.string(),
  type: TemplateTypeSchema,
  active: z51.boolean(),
  effectivePeriod: PartialPeriodSchema,
  system: systemSchema
});
var MessageTemplateGetResponseSchema = MessageTemplateSchema;
var MessageTemplateGetAllResponseSchema = PaginatedBundleSchema.extend({
  templates: MessageTemplateGetResponseSchema.array()
});
var MessageTemplateSearchSchema = PaginationSchema.extend({
  name: TemplateNameSchema.optional(),
  active: z51.boolean().optional(),
  type: TemplateTypeSchema.optional()
});

// src/templates/mapping.ts
import {
  IdentifierCode as IdentifierCode15,
  createCodeableConcept as createCodeableConcept25,
  createIdentifier as createIdentifier13,
  findIdentifier,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts14,
  getIdentifier as getIdentifier9
} from "@quromedical/fhir-common";
var mapTemplateModelToFhirLibrary = (template) => {
  const { name, content, active, effectivePeriod, templateId, type, id, system: system9 } = template;
  return {
    resourceType: "Library",
    id,
    identifier: [createIdentifier13(IdentifierCode15.libraryTemplateId, templateId, system9)],
    name,
    status: active ? "active" : "retired",
    type: createCodeableConcept25(type),
    description: content,
    effectivePeriod
  };
};
var mapFhirLibraryToTemplateModel = (fhir) => {
  const { name, status: status2, description, id, identifier, effectivePeriod } = fhir;
  const template = {
    id,
    name,
    active: status2 === "active",
    content: description,
    effectivePeriod,
    type: getFirstCodeFromCodableConcepts14([fhir.type]),
    templateId: getIdentifier9(IdentifierCode15.libraryTemplateId, identifier),
    system: findIdentifier(IdentifierCode15.libraryTemplateId, identifier)?.system
  };
  const parsed = MessageTemplateSchema.safeParse(template);
  if (!parsed.success) {
    throw parsed.error;
  }
  return parsed.data;
};

// src/authentication/schema.ts
import { z as z52 } from "zod";
var DeliveryMethod = /* @__PURE__ */ ((DeliveryMethod2) => {
  DeliveryMethod2["Email"] = "email";
  DeliveryMethod2["SMS"] = "sms";
  return DeliveryMethod2;
})(DeliveryMethod || {});
var AuthenticationStartRequestSchema = z52.object({
  mode: z52.nativeEnum(DeliveryMethod),
  username: z52.string()
});
var AuthenticationStartResponseSchema = z52.object({
  username: z52.string(),
  field: z52.string()
});
var AuthenticationCompleteRequestSchema = z52.object({
  mode: z52.nativeEnum(DeliveryMethod),
  username: z52.string(),
  code: z52.string().length(6)
});
var AuthenticationCompleteResponseSchema = z52.object({
  accessToken: z52.string(),
  idToken: z52.string(),
  refreshToken: z52.string()
});

// src/care-plan/mapping.ts
import {
  CarePlanActivityCode as CarePlanActivityCode3,
  CarePlanActivityCodeDisplay,
  LanguagePreference as LanguagePreference4,
  ServiceRequestCode as ServiceRequestCode4,
  createCodeableConcept as createCodeableConcept26,
  createFhirDate as createFhirDate5,
  createReference as createReference11,
  createReferenceFromResource as createReferenceFromResource4,
  getCodes as getCodes5,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts15,
  isValidReference as isValidReference11,
  system as system7
} from "@quromedical/fhir-common";
import { compact as compact22, first as first22, groupby, orderby, sum as sum3 } from "@quromedical/utils";
import { format as format2 } from "date-fns";

// src/questionnaire/schema.ts
import { z as z53 } from "zod";
var PriorityScoringSchema = z53.object({
  value: z53.number()
});
var QuestionTypeSchema = z53.enum([
  "string",
  "boolean",
  "reference",
  "display",
  "integer",
  "date",
  "text",
  "choice",
  "group",
  "question",
  "decimal",
  "dateTime",
  "time",
  "url",
  "open-choice",
  "attachment",
  "quantity"
]);
var OperatorSchema = z53.enum(["=", ">", "<", ">=", "<=", "!=", "exists"]);
var AnswerOptionSchema = z53.object({
  value: z53.union([z53.string(), z53.number(), z53.boolean(), z53.date()]),
  isDefaultAnswer: z53.boolean(),
  priorityScoring: PriorityScoringSchema.optional()
});
var ConditionSchema = z53.object({
  relatedLinkId: z53.string(),
  operator: OperatorSchema,
  value: z53.union([z53.string(), z53.boolean(), z53.number()])
});
var QuestionSchema = z53.lazy(
  () => z53.object({
    linkId: z53.string(),
    text: z53.string().optional(),
    type: QuestionTypeSchema,
    required: z53.boolean().optional(),
    answerOptions: AnswerOptionSchema.array().min(1),
    questions: z53.array(QuestionSchema).optional(),
    condition: ConditionSchema.optional()
  })
);
var QuestionnaireSectionSchema = z53.object({
  type: QuestionTypeSchema,
  icon: z53.string().optional(),
  linkId: z53.string(),
  required: z53.boolean().optional(),
  answerOptions: AnswerOptionSchema.array(),
  questions: z53.array(QuestionSchema).optional(),
  condition: ConditionSchema.optional(),
  text: z53.string().optional()
});
var QuestionnaireSchema = z53.object({
  id: UUIDSchema,
  url: z53.string().optional(),
  title: z53.string(),
  icon: z53.string().optional(),
  section: QuestionnaireSectionSchema.array().min(1)
});
var QuestionnairesSearchParamsSchema = z53.object({
  carePlanId: UUIDSchema
});
var QuestionnaireSearchParamsSchema = z53.object({
  questionnaire: UUIDSchema
});
var QuestionnaireGetAllResponseSchema = PaginatedBundleSchema.extend({
  questionnaires: QuestionnaireSchema.array()
});

// src/questionnaire/mapping.ts
import {
  ExtensionUrl as ExtensionUrl13,
  getExtensionValue as getExtensionValue2
} from "@quromedical/fhir-common";
import { first as first21 } from "@quromedical/utils";
var mapAnswerOptions = (questionnaireItemAnswerOption) => {
  return questionnaireItemAnswerOption.map((answerOption) => {
    const priorityScoringValue = getExtensionValue2(ExtensionUrl13.riskAssessmentRiskScore)(
      first21(answerOption.extension)
    );
    return {
      //TODO complex fhir types like valueCoding or valueReference are not supported
      value: answerOption.valueString || answerOption.valueInteger || answerOption.valueDate || answerOption.valueTime || // TODO: We need to use components for this so we have a better mapping to the UI.
      "",
      isDefaultAnswer: answerOption.initialSelected || false,
      priorityScoring: priorityScoringValue ? {
        value: priorityScoringValue
      } : void 0
    };
  });
};
var getCondition = (fhirCondition) => {
  let value;
  if ("answerBoolean" in fhirCondition) {
    value = fhirCondition.answerBoolean;
  } else if ("answerString" in fhirCondition) {
    value = fhirCondition.answerString;
  } else if ("answerInteger" in fhirCondition) {
    value = fhirCondition.answerInteger;
  }
  if (value === void 0) return void 0;
  return {
    relatedLinkId: fhirCondition.question,
    operator: fhirCondition.operator,
    value
  };
};
var mapSections = (section) => {
  const { type, text, item = [], code, answerOption, linkId, required, enableWhen } = section;
  const fhirCondition = first21(enableWhen);
  return {
    text,
    icon: first21(code)?.code,
    type,
    linkId,
    answerOptions: mapAnswerOptions(answerOption || []),
    required,
    condition: fhirCondition ? getCondition(fhirCondition) : void 0,
    questions: item.map((question) => {
      const { answerOption: answerOption2, linkId: linkId2, type: type2, text: text2, enableWhen: enableWhen2, required: required2, item: item2 } = question;
      const fhirCondition2 = first21(enableWhen2);
      return {
        linkId: linkId2,
        text: text2,
        type: type2,
        required: required2,
        answerOptions: mapAnswerOptions(answerOption2 || []),
        condition: fhirCondition2 ? getCondition(fhirCondition2) : void 0,
        questions: item2?.map(mapSections)
      };
    })
  };
};
var mapFhirQuestionnaireToModel = (fhirQuestionnaire) => {
  const { item, title, code, url, id } = fhirQuestionnaire;
  const sections = item ? item.map(mapSections) : [];
  const questionnaire = {
    id,
    url,
    title,
    icon: first21(code)?.code,
    section: sections
  };
  const validatedQuestionnaire = QuestionnaireSchema.safeParse(questionnaire);
  if (!validatedQuestionnaire.success) {
    throw new Error(JSON.stringify(validatedQuestionnaire.error, null, 2));
  }
  return validatedQuestionnaire.data;
};

// src/care-plan/schema.ts
import { CarePlanActivityCode as CarePlanActivityCode2, LanguagePreference as LanguagePreference3 } from "@quromedical/fhir-common";
import { z as z55 } from "zod";

// src/service-request/schema.ts
import { CarePlanActivityCode } from "@quromedical/fhir-common";
import { z as z54 } from "zod";
var PrioritySchema = z54.enum(["routine", "urgent", "asap", "stat"]);
var CarePlanActivityCodeSchema = z54.nativeEnum(CarePlanActivityCode);
var ServiceRequestStatusSchema = z54.enum([
  "draft",
  "active",
  "on-hold",
  "revoked",
  "completed",
  "entered-in-error",
  "unknown"
]);
var ServiceRequestIntentSchema = z54.enum(["proposal", "plan", "order", "option"]);
var BaseServiceRequestSchema = z54.object({
  id: UUIDSchema,
  status: ServiceRequestStatusSchema,
  intent: ServiceRequestIntentSchema,
  category: CarePlanActivityCodeSchema,
  priority: PrioritySchema,
  subject: DisplayReferenceSchema,
  encounter: DisplayReferenceSchema,
  requester: DisplayReferenceSchema,
  performer: DisplayReferenceSchema,
  note: z54.array(z54.string()),
  occurrenceDateTime: z54.string(),
  basedOn: z54.array(DisplayReferenceSchema)
});

// src/care-plan/schema.ts
var CarePlanActivityType = [
  "Appointment",
  "CommunicationRequest",
  "DeviceRequest",
  "MedicationRequest",
  "Task",
  "ServiceRequest",
  "NutritionOrder",
  "VisionPrescription"
];
var CarePlanActivityStatus = [
  "not-started",
  "scheduled",
  "in-progress",
  "on-hold",
  "completed",
  "cancelled",
  "stopped",
  "entered-in-error",
  "unknown"
];
var CarePlanActivityStatusSchema = z55.enum(CarePlanActivityStatus);
var CarePlanActivityTypeSchema = z55.enum(CarePlanActivityType);
var CarePlanActivityCodeSchema2 = z55.nativeEnum(CarePlanActivityCode2);
var versionSchema = z55.string().regex(/^[0-9]+(\.[0-9])?$/, {
  message: "Invalid version format. Must be a non-negative integer or a decimal with one digit after the decimal point."
});
var instantiatesCanonicalSchema = z55.string().refine(
  (value) => {
    const parts = value.split("|");
    const [reference, version] = parts;
    if (!ReferenceSchema.safeParse(reference).success) {
      return false;
    }
    if (version && !versionSchema.safeParse(version).success) {
      return false;
    }
    return true;
  },
  {
    message: "Invalid canonical URI. Must be in the format: {URI}|{version}"
  }
);
var CarePlanActivityDetailSchema = z55.object({
  code: CarePlanActivityCodeSchema2,
  description: z55.string(),
  kind: CarePlanActivityTypeSchema,
  status: CarePlanActivityStatusSchema,
  instantiatesCanonical: z55.array(instantiatesCanonicalSchema),
  scheduledPeriod: PeriodSchema
});
var CarePlanActivitySchema = z55.object({
  detail: CarePlanActivityDetailSchema,
  /** Reference to an Activity Definition */
  reference: DisplayReferenceSchema.optional()
});
var SupportedCarePlanStatus = [
  "draft",
  "active",
  "on-hold",
  "completed",
  "entered-in-error",
  "unknown",
  "revoked"
];
var CarePlanIntentSchema = z55.enum(["proposal", "plan", "order", "option"]);
var CarePlanStatusSchema = z55.enum(SupportedCarePlanStatus);
var BaseCarePlanSchema = z55.object({
  activity: z55.array(CarePlanActivitySchema),
  created: z55.string(),
  description: z55.string(),
  admission: DisplayReferenceSchema,
  status: CarePlanStatusSchema,
  title: z55.string(),
  period: PartialPeriodSchema,
  author: DisplayReferenceSchema,
  careTeam: z55.array(DisplayReferenceSchema),
  instantiatesCanonical: z55.array(instantiatesCanonicalSchema),
  intent: CarePlanIntentSchema
});
var ExtendCarePlanSchema = BaseCarePlanSchema.extend({
  call: PatientCallSchema.optional(),
  patient: z55.object({
    reference: DisplayReferenceSchema,
    languagePreference: z55.nativeEnum(LanguagePreference3)
  }),
  coverage: z55.object({
    membershipNumber: z55.string(),
    coverage: UUIDSchema
  }).optional(),
  priorityScore: z55.number()
});
var CarePlanSchema = ExtendCarePlanSchema.extend({
  id: UUIDSchema,
  dailyQuestionnaires: QuestionnaireSchema.array().optional()
});
var CarePlanSearchSchema = PaginationSchema.extend({
  status: CarePlanStatusSchema.optional(),
  date: z55.string().optional(),
  language: z55.nativeEnum(LanguagePreference3).optional(),
  patientId: UUIDSchema.optional(),
  withCalls: z55.boolean().optional(),
  callStatus: CallStatusSchema.optional()
});
var CreateCarePlanRequestSchema = z55.object({
  patientId: UUIDSchema,
  admissionId: UUIDSchema,
  planDefinition: instantiatesCanonicalSchema
});
var CreateCarePlanParamsSchema = z55.object({
  careTeam: z55.array(DisplayReferenceSchema),
  patient: DisplayReferenceSchema,
  admission: DisplayReferenceSchema,
  author: DisplayReferenceSchema
});
var CarePlanGetResponseSchema = CarePlanSchema;
var CarePlanGetAllResponseSchema = PaginatedBundleSchema.extend({
  carePlans: z55.array(CarePlanGetResponseSchema),
  callOverview: CallOverviewBarSchema
});
var CarePlanDaySchema = z55.object({
  dayNumber: z55.number(),
  date: z55.string(),
  escalations: z55.boolean(),
  flags: z55.boolean(),
  appointments: z55.boolean(),
  pathology: z55.boolean(),
  radiology: z55.boolean()
});
var CarePlanByPatientSchema = z55.object({
  patient: DisplayReferenceSchema,
  status: CarePlanStatusSchema,
  period: PartialPeriodSchema,
  rfa: RequiredCodingSchema,
  days: CarePlanDaySchema.array(),
  pauses: PartialPeriodSchema.array().optional()
});
var CarePlanByPatientGetResponseSchema = z55.array(CarePlanByPatientSchema);
var CarePlanDischargeRequestSchema = z55.object({
  id: UUIDSchema,
  periodEnd: z55.string().date(),
  statusReason: EncounterDischargeStatusReason
});
var CarePlanPauseRequestSchema = z55.object({
  id: UUIDSchema,
  statusReason: EncounterPausedStatusReason
});
var CreateCarePlanEscalationSchema = z55.object({
  note: z55.string(),
  patient: UUIDSchema,
  requestor: UUIDSchema.optional(),
  carePlan: UUIDSchema,
  admission: UUIDSchema,
  priority: PrioritySchema,
  careTeam: z55.array(UUIDSchema)
});
var CarePlanEscalationResponseSchema = z55.object({
  carePlan: BaseCarePlanSchema,
  serviceRequest: BaseServiceRequestSchema
});
var EscalationFormSchema = z55.object({
  note: z55.string().min(3),
  priority: z55.string().min(3),
  careTeam: z55.string().min(3)
});

// src/care-plan/mapping.ts
var mapRiskAssessmentToPriorityScore = (riskAssessment) => {
  return sum3(riskAssessment.prediction?.map((prediction) => prediction.probabilityDecimal));
};
var mapModelToFhirCarePlan = (carePlan) => {
  const {
    id,
    activity,
    created,
    patient,
    period: period2,
    status: status2,
    title,
    admission,
    author,
    careTeam,
    description,
    intent,
    instantiatesCanonical
  } = carePlan;
  const activityResult = activity.map((activity2) => {
    const { code, description: description2, instantiatesCanonical: instantiatesCanonical2, kind, scheduledPeriod, status: status3 } = activity2.detail;
    return {
      detail: {
        code: createCodeableConcept26(code),
        description: description2,
        kind,
        status: status3,
        instantiatesCanonical: instantiatesCanonical2,
        scheduledPeriod
      },
      reference: activity2.reference
    };
  });
  return {
    id,
    resourceType: "CarePlan",
    status: status2,
    period: period2,
    subject: patient.reference,
    encounter: admission,
    intent,
    title,
    created,
    activity: activityResult,
    author,
    careTeam,
    description,
    instantiatesCanonical
  };
};
var mapFhirCarePlanToModel = (input) => {
  const { patient, riskAssessment, coverage, questionnaires = [], call } = input;
  const {
    id,
    status: status2,
    period: period2,
    subject,
    encounter,
    intent,
    title,
    created,
    activity,
    author,
    careTeam,
    description,
    instantiatesCanonical
  } = input.carePlan;
  const subjectReference = isValidReference11(subject) ? subject : void 0;
  const encounterReference = isValidReference11(encounter) ? encounter : void 0;
  const authorReference = isValidReference11(author) ? author : void 0;
  const careTeamReference = careTeam?.map((t) => isValidReference11(t) ? t : void 0);
  const activityResult = activity?.map((activity2) => {
    return {
      detail: {
        ...activity2.detail,
        scheduledPeriod: activity2.detail?.scheduledPeriod,
        code: first22(getCodes5(activity2.detail?.code))
      },
      reference: activity2.reference
    };
  });
  const priorityScore = mapRiskAssessmentToPriorityScore(riskAssessment);
  const languageCodableConcept = first22(patient.communication)?.language;
  const languagePreference = languageCodableConcept ? getFirstCodeFromCodableConcepts15([languageCodableConcept]) : LanguagePreference4.English;
  const carePlanModel = {
    id,
    dailyQuestionnaires: questionnaires.map(mapFhirQuestionnaireToModel),
    title,
    status: status2,
    period: period2,
    patient: {
      reference: subjectReference,
      languagePreference
    },
    admission: encounterReference,
    description,
    intent,
    author: authorReference,
    careTeam: compact22(careTeamReference),
    created,
    activity: activityResult,
    instantiatesCanonical,
    priorityScore,
    call,
    coverage: coverage ? {
      coverage: coverage?.id,
      membershipNumber: coverage.subscriberId
    } : void 0
  };
  const parsed = CarePlanSchema.safeParse(carePlanModel);
  if (!parsed.success) {
    throw parsed.error;
  }
  return parsed.data;
};
var normalizeDate = (date5) => {
  if (!date5) {
    return "";
  }
  return format2(new Date(date5), "yyyy-MM-dd");
};
var mapCarePlanActivitiesToDay = (carePlanActivities, flags) => {
  const groupedActivities = groupby(carePlanActivities, (activity) => {
    const normalizedDate = normalizeDate(activity.detail?.scheduledPeriod?.start);
    return normalizedDate;
  });
  const filteredGroupedActivities = Object.entries(groupedActivities).filter(([dateKey]) => new Date(dateKey) <= /* @__PURE__ */ new Date()).sort(([dateKeyA], [dateKeyB]) => new Date(dateKeyA).getTime() - new Date(dateKeyB).getTime()).map(([dateKey, activity], index) => {
    const hasAppointment = activity.some((act) => act.detail?.kind === "Appointment");
    const hasPathology = activity.some(
      (act) => act.detail?.kind === "ServiceRequest" && act.detail?.code?.coding?.some(
        (coding) => coding.code === ServiceRequestCode4.PathologyRequest
      )
    );
    const hasEscalations = activity.some(
      (act) => act.detail?.kind === "ServiceRequest" && act.detail?.code?.coding?.some((coding) => coding.code === CarePlanActivityCode3.Escalation)
    );
    const hasFlag = flags.some((flag) => flag.period?.start === dateKey);
    return {
      dayNumber: index + 1,
      date: dateKey,
      escalations: hasEscalations,
      flags: hasFlag,
      appointments: hasAppointment,
      pathology: hasPathology,
      radiology: false
    };
  });
  return orderby(filteredGroupedActivities, (act) => act.dayNumber, "desc");
};
var mapFhirToCarePlanByPatientModel = (carePlan, patient, encounter, flags) => {
  const admission = mapEncounterToAdmission(encounter);
  const pauses = admission.statusHistory?.filter((status2) => status2.status === "onleave");
  const days = mapCarePlanActivitiesToDay(carePlan.activity || [], flags || []);
  const result = {
    patient: createReferenceFromResource4(patient),
    status: carePlan.status,
    period: carePlan.period,
    rfa: first22(admission.reasonOfAdmission),
    days,
    pauses: pauses?.map((pause) => {
      return {
        start: pause.period?.start,
        end: pause.period?.end
      };
    })
  };
  const parsed = CarePlanByPatientSchema.safeParse(result);
  if (!parsed.success) {
    throw parsed.error;
  }
  return parsed.data;
};
var mapModelToCreateServiceRequestEscalation = (escalation) => {
  const { patient, admission, carePlan, careTeam, note, priority = "stat", requestor } = escalation;
  return {
    resourceType: "ServiceRequest",
    subject: createReference11("Patient", patient),
    requester: requestor ? createReference11("Practitioner", requestor) : void 0,
    encounter: createReference11("Encounter", admission),
    occurrenceDateTime: createFhirDate5(/* @__PURE__ */ new Date()),
    note: note ? [{ text: note }] : void 0,
    intent: "order",
    code: createCodeableConcept26(
      CarePlanActivityCode3.Escalation,
      CarePlanActivityCodeDisplay[CarePlanActivityCode3.Escalation],
      CarePlanActivityCodeDisplay[CarePlanActivityCode3.Escalation],
      system7
    ),
    performer: careTeam.map((member) => createReference11("CareTeam", member)),
    priority,
    status: "active",
    basedOn: [createReference11("CarePlan", carePlan)]
  };
};
var mapFhirToServiceRequest = (escalation) => {
  const {
    id,
    subject,
    requester,
    encounter,
    occurrenceDateTime,
    note,
    intent,
    performer,
    priority,
    status: status2,
    basedOn,
    code
  } = escalation;
  const subjectReference = isValidReference11(subject) ? subject : void 0;
  const encounterReference = isValidReference11(encounter) ? encounter : void 0;
  const requesterReference = isValidReference11(requester) ? requester : void 0;
  const careTeamReference = performer?.map((t) => isValidReference11(t) ? t : void 0);
  return {
    id,
    status: status2,
    intent,
    basedOn,
    note: note?.map((n) => n.text),
    occurrenceDateTime,
    category: first22(getCodes5(code)) ?? CarePlanActivityCode3.Escalation,
    subject: subjectReference,
    requester: requesterReference,
    performer: first22(careTeamReference),
    priority,
    encounter: encounterReference
  };
};
var mapFhirCarePlanToModels = (input) => {
  const {
    id,
    status: status2,
    period: period2,
    subject,
    encounter,
    intent,
    title,
    created,
    activity,
    author,
    careTeam,
    description,
    instantiatesCanonical
  } = input;
  const subjectReference = isValidReference11(subject) ? subject : void 0;
  const encounterReference = isValidReference11(encounter) ? encounter : void 0;
  const authorReference = isValidReference11(author) ? author : void 0;
  const careTeamReference = careTeam?.map((t) => isValidReference11(t) ? t : void 0);
  const activityResult = activity?.map((activity2) => {
    return {
      detail: {
        ...activity2.detail,
        scheduledPeriod: activity2.detail?.scheduledPeriod,
        code: first22(getCodes5(activity2.detail?.code))
      },
      reference: activity2.reference
    };
  });
  const carePlanModel = {
    id,
    title,
    status: status2,
    period: period2,
    patient: subjectReference,
    admission: encounterReference,
    description,
    intent,
    author: authorReference,
    careTeam: compact22(careTeamReference),
    created,
    activity: activityResult,
    instantiatesCanonical
  };
  const { success, data, error } = BaseCarePlanSchema.safeParse(carePlanModel);
  if (!success) {
    throw error;
  }
  return data;
};

// src/questionnaire-response/schema.ts
import { z as z56 } from "zod";
var QuestionnaireResponseStatusSchema = z56.enum([
  "in-progress",
  "completed",
  "amended",
  "entered-in-error",
  "stopped"
]);
var AnswerSchema = z56.object({
  valueBoolean: z56.boolean().optional(),
  valueDate: z56.string().optional(),
  valueString: z56.string().optional(),
  valueInteger: z56.number().optional(),
  item: z56.lazy(() => z56.array(baseQuestionResponseAnswerSchema).optional())
});
var baseQuestionResponseAnswerSchema = z56.object({
  linkId: z56.string(),
  text: z56.string().optional(),
  answer: z56.array(AnswerSchema).optional()
});
var QuestionResponseAnswerSchema = baseQuestionResponseAnswerSchema.extend({
  section: z56.lazy(() => z56.array(QuestionResponseAnswerSchema).optional())
});
var UpdateSchema = z56.union([z56.string(), z56.number(), z56.boolean(), z56.date()]);
var UpdateAnswerSchema = z56.object({
  linkId: z56.string(),
  answer: z56.union([UpdateSchema, z56.array(UpdateSchema)])
});
var QuestionResponseUpdateAnswerSchema = z56.object({
  id: UUIDSchema,
  answers: z56.array(UpdateAnswerSchema).min(1)
});
var QuestionnaireResponseSchema = z56.object({
  id: UUIDSchema,
  questionnaire: z56.string().url(),
  careplan: DisplayReferenceSchema,
  admission: DisplayReferenceSchema,
  patient: DisplayReferenceSchema,
  status: QuestionnaireResponseStatusSchema,
  section: z56.array(QuestionResponseAnswerSchema).optional()
});
var QuestionnaireResponseSearchParamsSchema = z56.object({
  questionnaire: z56.string().url().optional(),
  identifier: z56.string().optional(),
  subject: UUIDSchema.optional()
});
var QuestionnaireResponseGetAllResponseSchema = PaginatedBundleSchema.extend({
  questionnaireResponses: QuestionnaireResponseSchema.array()
});

// src/questionnaire-response/mapping.ts
import { compact as compact23, first as first23 } from "@quromedical/utils";
import { isValid as isValid2, parseISO } from "date-fns";
var mapFhirItemToModelSections = (questionnaireResponseItem) => {
  const result = compact23(
    questionnaireResponseItem?.map((section) => {
      const { text, item, linkId, answer } = section;
      const mappedAnswer = answer?.map((a) => {
        return {
          item: a.item && a.item.length ? mapFhirItemToModelSections(a.item) : void 0,
          valueString: a.valueString,
          valueInteger: a.valueInteger,
          valueBoolean: a.valueBoolean,
          valueDate: a.valueDate ? a.valueDate.split("T")[0] : void 0
        };
      });
      return {
        text,
        linkId,
        required: true,
        answer: mappedAnswer,
        section: item && item.length ? mapFhirItemToModelSections(item) : void 0
      };
    })
  );
  return result;
};
var mapFhirQuestionnaireResponseToModel = (fhirQuestionnaireResponse) => {
  const { item, status: status2, id, questionnaire, encounter, subject, basedOn } = fhirQuestionnaireResponse;
  const questionnaireResponse = {
    id,
    questionnaire,
    admission: encounter,
    patient: subject,
    careplan: first23(basedOn),
    status: status2,
    section: mapFhirItemToModelSections(item)
  };
  const validatedQuestionnaireResponse = QuestionnaireResponseSchema.safeParse(questionnaireResponse);
  if (!validatedQuestionnaireResponse.success) {
    throw new Error(JSON.stringify(validatedQuestionnaireResponse.error, null, 2));
  }
  return validatedQuestionnaireResponse.data;
};
var mapModelSectionsToFhirItem = (section) => {
  const result = compact23(
    section.map((sec) => {
      const { text, linkId, answer, section: section2 } = sec;
      return {
        text,
        linkId,
        answer,
        item: section2 ? mapModelSectionsToFhirItem(section2) : void 0
      };
    })
  );
  return result;
};
var mapQuestionnaireResponseToModelToFhir = (questionnaireResponse) => {
  const { section, patient, admission, careplan, questionnaire, status: status2, id } = questionnaireResponse;
  const fhirQuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    questionnaire,
    id,
    status: status2,
    source: patient,
    subject: patient,
    encounter: admission,
    basedOn: careplan ? [careplan] : void 0,
    item: section ? mapModelSectionsToFhirItem(section) : void 0
  };
  return fhirQuestionnaireResponse;
};
var isISODateString = (value) => {
  const parsed = parseISO(value);
  return isValid2(parsed);
};
var getValueField = (value) => {
  const isValidISODate = typeof value === "string" && isISODateString(value);
  const isBooleanString = typeof value === "string" && (value === "Yes" || value === "No");
  if (isValidISODate) {
    return { valueDate: value?.split("T")[0] };
  }
  if (isBooleanString) {
    return { valueBoolean: value === "Yes" ? true : false };
  }
  const typeToFieldMap = {
    string: { valueString: value },
    boolean: { valueBoolean: value },
    number: { valueInteger: value },
    object: { valueDate: value instanceof Date ? value.toISOString().split("T")[0] : void 0 }
  };
  const fieldMapper = typeToFieldMap[typeof value];
  return fieldMapper;
};
var buildResponseItems = (updates, questions) => {
  const responseItems = questions.map((question) => {
    const matchingUpdate = updates.find((update) => update.linkId === question.linkId);
    const answer = matchingUpdate ? matchingUpdate.answer : void 0;
    const value = answer ? Array.isArray(answer) ? answer.map(getValueField) : [
      {
        ...getValueField(answer),
        item: question.item ? buildResponseItems(updates, question.item) : void 0
      }
    ] : void 0;
    const shouldAddResponse = value || question.item;
    if (!shouldAddResponse) {
      return;
    }
    return {
      linkId: question.linkId,
      text: question.text,
      answer: value,
      item: question.item && value === void 0 ? buildResponseItems(updates, question.item) : void 0
    };
  });
  return compact23(responseItems);
};
var mapQuestionnaireResponseUpdateModelToFhir = (questionnaireResponse, answers, questionnaireItems) => {
  return {
    ...questionnaireResponse,
    status: "completed",
    item: buildResponseItems(answers, questionnaireItems)
  };
};

// src/flag/schema.ts
import { z as z57 } from "zod";
var FlagStatusSchema = z57.enum(["active", "inactive", "entered-in-error"]);
var FlagBaseSchema = z57.object({
  note: z57.string()
});
var FlagSchema = FlagBaseSchema.extend({
  subject: DisplayReferenceSchema,
  encounter: DisplayReferenceSchema,
  practitioner: DisplayReferenceSchema,
  status: FlagStatusSchema,
  period: PeriodSchema
});
var FlagCreateSchema = FlagBaseSchema.extend({
  carePlan: UUIDSchema,
  subject: UUIDSchema,
  encounter: UUIDSchema
});
var FlagSearchSchema = PaginationSchema.extend({
  status: FlagStatusSchema.optional(),
  patientId: z57.string().uuid()
});
var FlagGetAllResponseSchema = PaginatedBundleSchema.extend({
  flags: z57.array(FlagSchema)
});

// src/flag/mapping.ts
import {
  createCodeableConcept as createCodeableConcept27,
  createExtensionValue as createExtensionValue11,
  ExtensionUrl as ExtensionUrl14,
  findExtensionValue as findExtensionValue8,
  FlagCode
} from "@quromedical/fhir-common";
var mapFlagModelToFhir = (flag) => {
  const extension = createExtensionValue11(ExtensionUrl14.flagNote)(flag.note);
  return {
    resourceType: "Flag",
    code: createCodeableConcept27(FlagCode.clinical),
    status: flag.status,
    subject: flag.subject,
    encounter: flag.encounter,
    author: flag.practitioner,
    period: flag.period,
    extension: [extension]
  };
};
var mapFhirFlagToModel = (fhirFlag) => {
  const flagModel = {
    note: findExtensionValue8(ExtensionUrl14.flagNote)(fhirFlag.extension),
    status: fhirFlag.status,
    subject: fhirFlag.subject,
    encounter: fhirFlag.encounter,
    practitioner: fhirFlag.author,
    period: fhirFlag.period
  };
  const result = FlagSchema.safeParse(flagModel);
  if (!result.success) {
    throw new Error(JSON.stringify(result.error));
  }
  return result.data;
};

// src/appointment/schema.ts
import { z as z58 } from "zod";
var Status2 = [
  "proposed",
  "pending",
  "booked",
  "arrived",
  "fulfilled",
  "cancelled",
  "noshow",
  "entered-in-error",
  "checked-in",
  "waitlist"
];
var AppointmentSchema = z58.object({
  appointmentType: z58.string(),
  description: z58.string(),
  date: z58.string(),
  status: z58.enum(Status2),
  subject: DisplayReferenceSchema
});
var AppointmentGetAllRequestSchema = PaginationSchema.extend({
  patientId: UUIDSchema
});
var AppointmentGetAllResponseSchema = PaginatedBundleSchema.extend({
  appointments: z58.array(AppointmentSchema)
});

// src/appointment/mapping.ts
import { createCodeableConcept as createCodeableConcept28, getCodes as getCodes6 } from "@quromedical/fhir-common";
import { first as first24 } from "@quromedical/utils";
var mapModelToFhirAppointment = (appointment) => {
  const { status: status2, appointmentType, date: date5, description, subject } = appointment;
  const type = createCodeableConcept28(appointmentType, appointmentType, appointmentType);
  return {
    resourceType: "Appointment",
    participant: [
      {
        actor: subject,
        status: "accepted"
      }
    ],
    appointmentType: type,
    start: date5,
    end: date5,
    description,
    status: status2
  };
};
var mapFhirAppointmentToModel = (fhir) => {
  const { status: status2, start, description, participant, appointmentType } = fhir;
  const mapped = {
    date: start,
    description,
    status: status2,
    subject: first24(participant)?.actor,
    appointmentType: first24(getCodes6(appointmentType))
  };
  const parsed = AppointmentSchema.safeParse(mapped);
  if (parsed.success == false) {
    throw parsed.error;
  }
  return parsed.data;
};

// src/document/schema.ts
import { DocumentTypeCode, Extension } from "@quromedical/fhir-common";
import { z as z59 } from "zod";
var documentStatus = ["current", "entered-in-error"];
var documentExtensions = [
  Extension.csv,
  Extension.dcm,
  Extension.doc,
  Extension.docx,
  Extension.json,
  Extension.odt,
  Extension.pdf,
  Extension.xls,
  Extension.xlsx,
  Extension.xml
];
var DocumentTypeSchema = z59.enum([
  DocumentTypeCode.clinicalReferral,
  DocumentTypeCode.homeBasedCareReferral,
  DocumentTypeCode.motivationMedicalDevice,
  DocumentTypeCode.createScript
]);
var FileExtensionDocumentSchema = z59.enum(documentExtensions);
var DocumentSchema = z59.object({
  id: UUIDSchema,
  patient: DisplayReferenceSchema,
  author: DisplayReferenceSchema,
  created: z59.string(),
  status: z59.enum(documentStatus),
  type: DocumentTypeSchema,
  document: MediaDataSchema
});
var DocumentMotivationMedicalDeviceSchema = z59.object({
  deviceName: z59.string()
});
var MedicationSchema2 = z59.object({
  name: z59.string(),
  nappi: z59.string(),
  qty: z59.string()
});
var DocumentCreateMedicationUsageSchema = z59.object({
  medications: z59.array(MedicationSchema2).or(MedicationSchema2),
  notes: z59.string()
});
var DocumentCreateMedicationScriptSchema = z59.object({
  medications: z59.object({
    display: z59.string(),
    value: z59.string()
  }),
  quantity: z59.string()
});
var ReferralSchema = z59.object({
  reasonForReferral: z59.string(),
  medicalHistory: z59.string(),
  currentTreatment: z59.string(),
  recentInvestigations: z59.string().optional(),
  externalPractitionerDiscipline: z59.string(),
  externalPractitionerName: z59.string(),
  externalPractitionerEmail: z59.string().email().optional(),
  externalPractitionerPhoneNumber: z59.string().optional()
});
var DocumentClinicalReferralSchema = ReferralSchema;
var DocumentHomeBasedCareReferralSchema = ReferralSchema;
var DocumentCreateSchema = z59.object({
  patient: UUIDSchema,
  type: DocumentTypeSchema,
  data: z59.unknown()
  // Use `unknown` initially, refine later based on `type`
}).superRefine((document, ctx) => {
  switch (document.type) {
    case DocumentTypeCode.motivationMedicalDevice: {
      const motivationMedicalDeviceResult = DocumentMotivationMedicalDeviceSchema.safeParse(
        document.data
      );
      if (!motivationMedicalDeviceResult.success) {
        motivationMedicalDeviceResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ["data", ...issue.path]
          });
        });
      }
      break;
    }
    case DocumentTypeCode.homeBasedCareReferral: {
      const homeBasedCareReferralResult = DocumentHomeBasedCareReferralSchema.safeParse(
        document.data
      );
      if (!homeBasedCareReferralResult.success) {
        homeBasedCareReferralResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ["data", ...issue.path]
          });
        });
      }
      break;
    }
    case DocumentTypeCode.clinicalReferral: {
      const clinicalReferralResult = DocumentClinicalReferralSchema.safeParse(document.data);
      if (!clinicalReferralResult.success) {
        clinicalReferralResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ["data", ...issue.path]
          });
        });
      }
      break;
    }
    case DocumentTypeCode.createScript: {
      const clinicalReferralResult = DocumentCreateMedicationUsageSchema.safeParse(document.data);
      if (!clinicalReferralResult.success) {
        clinicalReferralResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ["data", ...issue.path]
          });
        });
      }
      break;
    }
    default:
      ctx.addIssue({
        code: z59.ZodIssueCode.custom,
        message: "Invalid document type",
        path: ["type"]
      });
  }
});
var DocumentSearchSchema = PaginationSchema.extend({
  patient: UUIDSchema.optional()
});
var DocumentGetAllResponseSchema = PaginatedBundleSchema.extend({
  documents: DocumentSchema.array()
});

// src/document/mapping.ts
import {
  createCodeableConcept as createCodeableConcept29,
  createReference as createReference12,
  createReferenceFromResource as createReferenceFromResource5,
  DocumentTypeCodeDescription,
  getFirstCodeFromCodableConcepts as getFirstCodeFromCodableConcepts16,
  system as system8
} from "@quromedical/fhir-common";
import { first as first25 } from "@quromedical/utils";
var mapDocumentCreateModelToFhir = (document, attachment, admission, author) => {
  const { type, patient } = document;
  const DocumentTypeDisplay = DocumentTypeCodeDescription[type];
  const documentReference = {
    resourceType: "DocumentReference",
    status: "current",
    type: createCodeableConcept29(type, DocumentTypeDisplay, DocumentTypeDisplay, system8),
    date: (/* @__PURE__ */ new Date()).toISOString(),
    context: {
      encounter: [admission]
    },
    subject: createReference12("Patient", patient),
    content: [
      {
        attachment
      }
    ],
    author: [createReferenceFromResource5(author)]
  };
  return documentReference;
};
var mapDocumentReferenceToModel = (documentReference) => {
  const { content, status: status2, author, date: date5, subject, context, type, id } = documentReference;
  const documentTypeCode = type ? getFirstCodeFromCodableConcepts16([type]) : void 0;
  const firstContent = first25(content);
  const document = {
    id,
    patient: subject,
    author: first25(author),
    created: date5,
    status: status2,
    document: firstContent ? {
      contentType: firstContent.attachment?.contentType,
      key: firstContent.attachment?.url,
      title: firstContent.attachment?.title
    } : void 0,
    admission: first25(context?.encounter),
    type: documentTypeCode
  };
  const parsedResult = DocumentSchema.safeParse(document);
  if (parsedResult.error) {
    throw new Error(JSON.stringify(parsedResult.error.errors));
  }
  return parsedResult.data;
};

// src/plan-definition/schema.ts
import { z as z60 } from "zod";
var PlanDefinitionStatusSchema = z60.enum(["draft", "active", "retired", "unknown"]);
var PlanDefinitionSchema = z60.object({
  id: UUIDSchema,
  questionnaires: z60.array(ReferenceSchema),
  date: z60.string(),
  description: z60.string(),
  effectivePeriod: PartialPeriodSchema,
  status: PlanDefinitionStatusSchema,
  title: z60.string(),
  version: z60.string()
});
var PlanDefinitionGetResponseSchema = PlanDefinitionSchema;
var PlanDefinitionGetAllResponseSchema = PaginatedBundleSchema.extend({
  planDefinitions: z60.array(PlanDefinitionGetResponseSchema)
});
var PlanDefinitionSearchSchema = PaginationSchema.extend({
  title: z60.string().optional(),
  status: PlanDefinitionStatusSchema.optional()
});

// src/plan-definition/mapping.ts
var mapPlanDefinitionModelToFhir = (template) => {
  const { id, date: date5, description, effectivePeriod, questionnaires, status: status2, title, version } = template;
  return {
    resourceType: "PlanDefinition",
    id,
    status: status2,
    title,
    version,
    description,
    date: date5,
    effectivePeriod,
    action: questionnaires.map((q) => {
      return {
        definitionCanonical: q
      };
    })
  };
};
var mapFhirToPlanDefinitionModel = (fhir) => {
  const { status: status2, description, id, title, date: date5, version, effectivePeriod } = fhir;
  const template = {
    id,
    status: status2,
    title,
    date: date5,
    version,
    questionnaires: fhir.action?.map((a) => a.definitionCanonical),
    description,
    effectivePeriod
  };
  const parsed = PlanDefinitionSchema.safeParse(template);
  if (!parsed.success) {
    throw parsed.error;
  }
  return parsed.data;
};
export {
  AdministratorSchema,
  AdmissionDiagnosisSchema,
  AdmissionDischargeRequestSchema,
  AdmissionGetAllResponseSchema,
  AdmissionGetOneByIdResponseSchema,
  AdmissionGetResponseSchema,
  AdmissionGetResponseWithMediaSchema,
  AdmissionInvalidateRequestSchema,
  AdmissionLocationUpdateSchema,
  AdmissionMediaSchema,
  AdmissionMedicalAidAuthorizationSchema,
  AdmissionPauseRequestSchema,
  AdmissionSchema,
  AdmissionSearchSchema,
  AdmissionStatusHistorySchema,
  AdmissionSubjectSchema,
  AdmissionUpdateSchema,
  AdmissionVitalSummarySchema,
  AdmissionWithPartialPrimaryCodeSchema,
  AdmissionWithUsagesSchema,
  AllergyAdmissionCreateRequestSchema,
  AllergyCodeSchema,
  AllergyCreateRequestSchema,
  AllergyCreateSchema,
  AllergyGetAllResponseSchema,
  AllergySchema,
  AllergySearchSchema,
  AllergyStatusSchema,
  AllergyTypeArraySchema,
  AllergyTypeSchema,
  AllergyWithIdArraySchema,
  AllergyWithIdSchema,
  AlternatePhoneNumberSchema,
  ampath_exports as Ampath,
  AppointmentGetAllRequestSchema,
  AppointmentGetAllResponseSchema,
  AppointmentSchema,
  AuthenticationCompleteRequestSchema,
  AuthenticationCompleteResponseSchema,
  AuthenticationStartRequestSchema,
  AuthenticationStartResponseSchema,
  BaseAdmissionMedicalAidAuthorizationSchema,
  BaseAlertSchema,
  BaseCarePlanSchema,
  BaseLocationSchema,
  BaseServiceRequestSchema,
  BaseTaskSearchSchema,
  BaseTaskUpdateSchema,
  BaseTimeSeriesRecordSchema,
  CallOverviewBarSchema,
  CallStatusSchema,
  CarePlanActivitySchema,
  CarePlanByPatientGetResponseSchema,
  CarePlanDischargeRequestSchema,
  CarePlanEscalationResponseSchema,
  CarePlanGetAllResponseSchema,
  CarePlanGetResponseSchema,
  CarePlanPauseRequestSchema,
  CarePlanSchema,
  CarePlanSearchSchema,
  CarePlanStatusSchema,
  CareTeamGetAllResponseSchema,
  CareTeamResponseSchema,
  CareTeamSchema,
  CareTeamSearchSchema,
  CareTeamStatusSchema,
  CareTeamValidationSchema,
  ChargeItemDefinitionCodeSchema,
  ChargeItemDefinitionCreateSchema,
  ChargeItemDefinitionCurrencySchema,
  ChargeItemDefinitionGetAllParamsSchema,
  ChargeItemDefinitionGetAllResponseSchema,
  ChargeItemDefinitionGetResponseSchema,
  ChargeItemDefinitionMoneyFactor,
  ChargeItemDefinitionMoneyFactorSchema,
  ChargeItemDefinitionMoneySchema,
  ChargeItemDefinitionSchema,
  ChargeItemDefinitionSchemaPartial,
  ChargeItemDefinitionSchemaWithId,
  ChargeItemDefinitionStatusSchema,
  ChargeItemDefinitionTypeSchema,
  ChargeLineApplicabilitySchema,
  ChargeLineQuantitySchema,
  ChargeLineSchema,
  ClaimGetAllParams,
  ClaimGetAllResponseSchema,
  ClaimGetResponseSchema,
  ClaimGetResponseWithIdSchema,
  ClaimPriorityCodeSchema,
  ClaimResponseGetAllParamsSchema,
  ClaimResponseInputSchema,
  ClaimResponseItemPartialSchema,
  ClaimResponseItemSchema,
  ClaimResponseItemTotalSchema,
  ClaimResponseModelGetAllResponseSchema,
  ClaimResponseModelGetResponseSchema,
  ClaimResponseModelSchema,
  ClaimResponseOutcome,
  ClaimResponseStatus,
  ClaimResponseTotalSchema,
  ClaimSchema,
  ClaimStatusSchema,
  ClaimTemplateBaseSchema,
  ClaimTemplateCreateRequestSchema,
  ClaimTemplateGetAllResponseSchema,
  ClaimTemplateGetResponseSchema,
  ClaimTemplateGetTypeRequestSchema,
  ClaimTemplateSchema,
  ClaimTemplateUpdateRequestSchema,
  ClaimToHealthbridgeRequestParamSchema,
  ClaimTypeCodeSchema,
  ClaimTypeSchema,
  ClaimUpdateRequestSchema,
  ClaimUpdateResponseSchema,
  ClinicalImpressionAssessmentValueCodeSchema,
  ClinicalImpressionPlanValueCodeSchema,
  Command,
  CommunicationCategoryCode,
  CommunicationChannelCode,
  CommunicationChannelCodeDescription,
  CommunicationCreateRequestSchema,
  CommunicationGetAllResponseSchema,
  CommunicationGetResponseSchema,
  CommunicationSearchSchema,
  CommunicationTopicCode,
  ConsumableCreateInputRequestSchema,
  ConsumableCreateRequestSchema,
  ConsumableGetAllRequestSchema,
  ConsumableGetAllResponseSchema,
  ConsumableGetResponseSchema,
  ConsumableSchema,
  ConsumableSearchParamsSchema,
  ConsumableSearchResponseSchema,
  ConsumableUpdateRequestSchema,
  ConsumableUsageSchema,
  ContactSchema,
  ContactUpdateSchema,
  ContentTypeSchema,
  ConversationCreateRequestSchema,
  ConversationGetRequestSchema,
  ConversationGetResponseSchema,
  CoordinatesSchema,
  CoverageIdentifiersSchema2 as CoverageIdentifiersSchema,
  CoverageSchema,
  CreateAdmissionDiagnosisSchema,
  CreateAdmissionSchema,
  CreateAdmissionSchemaWithPatientSchema,
  CreateAmpathOrderModelPropsSchema,
  CreateBulkDevicesResponseSchema,
  CreateBulkDevicesSchema,
  CreateCarePlanEscalationSchema,
  CreateCarePlanRequestSchema,
  CreateCareTeamSchema,
  CreateClaimReversalSchema,
  CreateDeviceSchema,
  CreateDiagnosisSchema,
  CreateInitialGroupSchema,
  CreateLocationSchema,
  CreateMedicationUsageSchema,
  CreateNoteSchema,
  CreateObservationSchema,
  CreateQualificationFieldsSchema,
  CreateRelatedPersonSchema,
  CreateSOAPNoteSchema,
  CreateUploadURLSchema,
  CreateUploadUrlResponseSchema,
  DateTimeSchema,
  DeviceAssociationCreateRequestSchema,
  DeviceAssociationGetAllParamsSchema,
  DeviceAssociationSchema,
  DeviceAssociationSearchSchema,
  DeviceAssociationWithIdSchema,
  DeviceCodeSchema,
  DeviceGetAllResponseSchema,
  DeviceGetByIdSchema,
  DeviceMetricGetAllParamsSchema,
  DeviceMetricGetAllResponseSchema,
  DeviceSchema,
  DeviceSchemaMap,
  DeviceSearchSchema,
  DeviceStatusSchema,
  DiagnosisGetAllResponseSchema,
  DiagnosisSchema,
  DiagnosisSearchSchema,
  DisplayReferenceSchema,
  DocumentClinicalReferralSchema,
  DocumentCreateMedicationScriptSchema,
  DocumentCreateMedicationUsageSchema,
  DocumentCreateSchema,
  DocumentGetAllResponseSchema,
  DocumentHomeBasedCareReferralSchema,
  DocumentMotivationMedicalDeviceSchema,
  DocumentSchema,
  DocumentSearchSchema,
  DocumentTypeSchema,
  ECGAlertCommentSchema,
  ECGPredictSinusRhythmRequestSchema,
  ECGSinusRhythmAlertSchema,
  EncounterDischargeStatusReason,
  EncounterInvalidateStatusReason,
  EncounterPausedStatusReason,
  EncounterStatusReasonSchema,
  EndPatientCallRequestSchema,
  EscalationFormSchema,
  ExternalSchema,
  FileExtensionDocumentSchema,
  FlagCreateSchema,
  FlagGetAllResponseSchema,
  FlagSchema,
  FlagSearchSchema,
  GenderSchema,
  GeneralPractitionerSchema,
  GeneralPractitionerUpdateRequestSchema,
  GeneralSchema,
  GeneralUpdateSchema,
  GetAdministratorsResponseSchema,
  GetDiagnosisResponseSchema,
  GetLocationResponseSchema,
  GetPractitionerRolesInputSchema,
  GetPractitionerRolesResponseSchema,
  GetSchemesResponseSchema,
  GooglePlacesAddressComponentsSchema,
  GooglePlacesCoordinateSchema,
  GooglePlacesGeometrySchema,
  GooglePlacesPlacePredictionSchema,
  GooglePlacesPlaceResultSchema,
  GooglePlacesPlaceSchema,
  GooglePlacesPredictionSchema,
  GooglePlacesSearchAddressQueryParamsSchema,
  GroupAddMembersResponseSchema,
  GroupCreateRequestSchema,
  GroupGetAllResponseSchema,
  GroupGetResponseSchema,
  GroupSchema,
  GroupSearchSchema,
  GroupUpdateMemberRequestSchema,
  GroupUpdateRequestSchema,
  ICD10CodeSchema,
  IdentifierCode2 as IdentifierCode,
  LatestAggregateDataErrorSchema,
  LatestAggregateDataSchema,
  LatestAggregateDataSuccessSchema,
  LatestResultMeasuresSchema,
  LatestResultNotesMeasuresSchema,
  LatestResultSchema,
  LiveDataMeasuresSchema,
  LocationGetAllResponseSchema,
  LocationSchema,
  LocationSearchSchema,
  LocationStatusSchema,
  LocationTypeSchema,
  MDMRebootDeviceResponseSchema,
  MDMStatusResponseSchema,
  MONEY_FACTOR,
  MeasurementName,
  MeasurementNameSchema,
  MeasurementSchema,
  MeasurementTTLMap,
  MediaCreateSchema,
  MediaDataSchema,
  MediaGetAllResponseSchema,
  MediaGetResponseSchema,
  MediaItemCreateSchema,
  MediaSchema,
  MedicalAidAdmissionSchema,
  MedicalAidMembershipSchema,
  MedicalAidPersonSchema,
  MedicalAidSchema,
  MedicalAidValidationIdNumberRequestSchema,
  MedicalAidValidationMembershipNumberRequestSchema,
  MedicalAidValidationRequestApiSchema,
  MedicalAidValidationResponseSchema,
  MedicationCreateRequestSchema,
  MedicationCreateResponseSchema,
  MedicationDosageSchema,
  MedicationGetAllParamsSchema,
  MedicationGetAllResponseSchema,
  MedicationGetResponseSchema,
  MedicationMeasureSchema,
  MedicationQueryParamsSchema,
  MedicationRepeatSchema,
  MedicationRouteCode,
  MedicationRouteCodeDescription,
  MedicationRouteCodeSchema,
  MedicationScheduleSchema,
  MedicationSchema,
  MedicationTimeUnitSchema,
  MedicationUsageStatusSchema,
  MedpraxAllergySchema,
  MedpraxTariffMedicalTypeCode,
  MessageTemplateGetAllResponseSchema,
  MessageTemplateGetResponseSchema,
  MessageTemplateSchema,
  MessageTemplateSearchSchema,
  MovePatientsToWardInputSchema,
  MovePatientsToWardResponseSchema,
  NoteAdmissionSchema,
  NoteBundleSchema,
  NoteSchema,
  NoteSearchSchema,
  NoteUpdateSchema,
  NotificationSchema,
  ObservationGetResponseSchema,
  ObservationPathologySchema,
  ObservationPregnancySchema,
  ObservationReportComponentSchema,
  ObservationResultSchema,
  ObservationResultSearchSchema,
  ObservationResultsResponseSchema,
  ObservationSchema,
  ObservationStatus,
  ObservationStatusSchema,
  ObservationVitalMeasurementSchema,
  OrganizationGeneralBaseSchema,
  OrganizationGeneralSchema,
  OrganizationGeneralUpdateSchema,
  OrganizationGetAllInsuranceParamsSchema,
  OrganizationGetAllInsurancesResponseSchema,
  OrganizationGetAllResponseSchema,
  OrganizationGetResponseSchema,
  OrganizationSchema,
  OrganizationSearchSchema,
  PartialBaseMedicationReferenceSchema,
  PartialClaimSchema,
  PartialClaimWithIdSchema,
  PartialMedicationRequestWithIdSchema,
  PartialMedicationUsageSchema,
  PartialOrganizationSchema,
  PartialPersonSchema,
  PartialPractitionerSchema,
  PartialPractitionerWithIdSchema,
  PartialRelatedPersonSchema,
  PartialTaskSchema,
  PartialTaskWithIdSchema,
  PatchCalibrateResponseSchema,
  PatchNameRegex,
  PatchPostureCalibrateValue,
  PathologyCreateOrderFormRequestSchema,
  PathologyCreateOrderRequestSchema,
  PathologyCreateOrderWebRequestSchema,
  PathologyGetTestsRequestSchema,
  PathologyGetTestsResponseSchema,
  PathologyOrderCreateRequestSchema,
  PathologyOrderGetAllParamsSchema,
  PathologyOrderGetAllResponseSchema,
  PathologyOrderGetResponseSchema,
  PathologyOrderGetResponseWithIdSchema,
  PathologyOrderSchema,
  PathologyPatientOrderCreateSchema,
  PathologyPerformerCodeSchema,
  PathologyProvider,
  PathologyProviderSchema,
  PathologyReportGetAllObservationsResponseSchema,
  PathologyReportGetAllParamsSchema,
  PathologyReportGetAllResponseSchema,
  PathologyReportGetResponseSchema,
  PathologyReportWithObservationsResponseSchema,
  PatientCallSchema,
  PatientCoverageResponseSchema,
  PatientCreateCoverageResponseSchema,
  PatientGeneralPractitionerUpdateSchema,
  PatientGetAllResponseSchema,
  PatientGetResponseSchema,
  PatientMediaCreateSchema,
  PatientMediaSearchSchema,
  PatientMedicalAidMembershipSchema,
  PatientMedicalAidOnboardRequestSchema,
  PatientMedicalAidOnboardSchema,
  PatientMedicalAidOnboardUpdateSchema,
  PatientMedicalAidUpdateRequestSchema,
  PatientOnboardResponseSchema,
  PatientPrivateOnboardRequestSchema,
  PatientPrivateOnboardSchema,
  PatientQuerySearchResponseSchema,
  PatientQuerySearchSchema,
  PatientRequestSchema,
  PatientSearchSchema,
  PatientSummarySchema,
  PatientTaskSearchSchema,
  PermissionSchema,
  PersonContactGetResponseSchema,
  PersonContactWithAddressEmergencyAccessSchema,
  PersonGeneralGetResponseSchema,
  PersonSchema,
  PlanDefinitionGetAllResponseSchema,
  PlanDefinitionGetResponseSchema,
  PlanDefinitionSchema,
  PlanDefinitionSearchSchema,
  PostureCalibration,
  PostureReading,
  PractitionerContactUpdateRequestSchema,
  PractitionerCreateSchema,
  PractitionerExternalSchema,
  PractitionerGeneralSchema,
  PractitionerGeneralUpdateRequestSchema,
  PractitionerGetAllResponseSchema,
  PractitionerGetOneResponseSchema,
  PractitionerGetResponseSchema,
  PractitionerQualificationCodeSchema,
  PractitionerQualificationUpdateRequestSchema,
  PractitionerRoleSchema,
  PractitionerSearchSchema,
  PractitionerSettingsSchema,
  PractitionerTaskSearchSchema,
  PractitionerUpdateStatusSchema,
  PractitionerWithScopesSchema,
  PreferenceItemSchema,
  PreferenceSchema,
  PregnancySchema,
  PrimaryPhoneNumberSchema,
  PrivateToMedicalAidConversionResponseSchema,
  ProfileSchema,
  QualificationFieldsSchema,
  QuestionResponseAnswerSchema,
  QuestionResponseUpdateAnswerSchema,
  QuestionnaireGetAllResponseSchema,
  QuestionnaireResponseGetAllResponseSchema,
  QuestionnaireResponseSchema,
  QuestionnaireResponseSearchParamsSchema,
  QuestionnaireSchema,
  QuestionnaireSearchParamsSchema,
  QuestionnairesSearchParamsSchema,
  RelatedPersonGetAllResponseSchema,
  RelatedPersonGetResponseSchema,
  RelatedPersonSchema,
  RelatedPersonSearchParamsSchema,
  RelatedPersonUpdateSchema,
  RelatedPersonWithIdSchema,
  RelayStatusFieldSchema,
  RelayStatusGetAllResponseSchema,
  RelayStatusRequestSchema,
  RelayStatusSchema,
  ResourceNoteSchema,
  SOAPNoteSchema,
  SchemeSchema,
  SelectOptionSchema,
  SensorTimeSeriesRecordSchema,
  SettingsSchema,
  SpecialtySchema,
  StartPatientCallRequestSchema,
  StructuredContentSchema,
  StructuredContentTypeSchema,
  StructuredNoteBaseSchema,
  StructuredNoteGroupSchema,
  StructuredNoteItemSchema,
  StructuredNoteNodeSchema,
  SubmitClaimRequestSchema,
  SubscriptionActionSchema,
  SubscriptionParamsSchema,
  SupportedCareTeamStatus,
  SupportedClaimStatus,
  SupportedLocationStatus,
  TaskAssignmentSchema,
  TaskCreateSchema,
  TaskGetAllResponseSchema,
  TaskGetResponseSchema,
  TaskIntentSchema,
  TaskNoteScheme,
  TaskReferenceSchema,
  TaskSchema,
  TaskSearchSchema,
  TaskSearchStatusSchema,
  TaskStatusSchema,
  TaskUpdateOverdueSchema,
  TaskUpdateSchema,
  TaskUpdateStatusSchema,
  TeamMembersSchema,
  TeamMembersValidationSchema,
  ThemeSchema,
  Themes,
  TimeSeriesPatientQuerySchema,
  TimeToLiveOffsetSeconds,
  UUIDInputSchema,
  UUIDSearchSchema,
  UpdateAllergySchema,
  UpdateCareTeamSchema,
  UpdateDeviceSchema,
  UpdateGeneralPatientSchema,
  UpdateHospitalSettingsSchema,
  UpdateLocationSchema,
  UpdatePractitionerExternalSchema,
  UpdatePractitionerSettingsSchema,
  UserPreferenceItemSchema,
  UserPreferenceWardSchema,
  UserSchema,
  UserWithIdSchema,
  ValueSetSearchSchema,
  VideoConsultCreateRoomRequestSchema,
  VideoConsultCreateRoomResponseSchema,
  VideoConsultSendInvitationRequestSchema,
  VideoConsultSendInvitationResponseSchema,
  VitalsAggregateDataErrorSchema,
  VitalsAggregateDataSchema,
  VitalsAggregateDataSuccessSchema,
  VitalsAggregateMeasureResultSchema,
  VitalsAggregateResultSchema,
  VitalsMeasurementRequestSchema,
  VitalsQueryParamsSchema,
  VitalsResultMeasuresSchema,
  VitalsResultSchema,
  addResourceNote,
  addSubjectsToAdmission,
  admissionCreateRequestSchema,
  admissionLocationUpdateRequestSchema,
  admissionRequestSchemaFields,
  admissionUpdateRequestSchema,
  alertCommentCreateSchema,
  allergySchema,
  assertClaimToHealthbridgeRequestParamsValid,
  assignTaskSchema,
  attachIdentifiers,
  attachMeasurementToDeviceMetric,
  citySchema,
  claimReversalCreateSchema,
  claimTemplateRequestSchema,
  commandToDeviceMetricTypeMap,
  communicationContentCreateSchema,
  communicationContentSchema,
  completeReusableDeviceUseStatements as completeRelayDeviceUseStatements,
  conversationContentSchema,
  countrySchema,
  createAddressExtensionList,
  createAddressLatitude,
  createAddressList,
  createAddressLongitude,
  createAddressPlaceId,
  createAmpathOrderModel,
  createAmpathServiceRequestFromOrder,
  createChargeItemDefinitionSchema,
  createContactPoint,
  createCoverageClasses,
  createEmailContactPoint,
  createEncounterAuditResource,
  createMediaDataSchema,
  createMediaRequestSchema,
  createMediaResources,
  createNoteSchema,
  createOptionalIdentifier,
  createPathologyOrderSchema,
  createPractitionerQualification,
  createPrimaryPhoneContactPoint,
  createSecondaryPhoneContactPoint,
  createTaskAssignmentSchema,
  createUploadUrlRequestSchema,
  customStatusToFhirMap,
  dateSchema,
  deviceCodeToDeviceUseStatementCode,
  deviceMetricTypeToCommandMap,
  documentExtensions,
  documentStatus,
  doesResourceMatchReference,
  emailSchema,
  endDeviceUseStatements,
  filterResourcesByType,
  findReasonOfAdmission,
  findReferencedResource,
  findReferencedResources,
  finishEncounter,
  generalPractitionerUpdateRequestSchema,
  generateClaimInvoiceNumber,
  getAddressLatitude,
  getAddressLongitude,
  getAddressPlaceId,
  getAdmissionICD10CodesFromExtension,
  getApplicableChargeLine,
  getBirthDateFromSouthAfricanIdNumber,
  getBundlePaginationTokens,
  getBundlePaginationTokensEncoded,
  getClaimInvoiceNumber,
  getCoordinates,
  getDescription,
  getDistanceParamsSchema,
  getEffectiveDateTimeFromMeasures,
  getExtensionCodingValue,
  getFirstAdmissionFromEncounterBundle,
  getGenderFromSouthAfricanIdNumber,
  getICD10CodesFromAdmissionDiagnosis,
  getICD10CodesFromCodeableConcepts,
  getItemTotal,
  getMedicalAidIdentifiers,
  getObservationVitals,
  getObservationVitalsByMeasurementCode,
  getOrganizationSchemeCode,
  getPatientCareTeam,
  getPatientStatus,
  getPersonIdentifier,
  getReasonCode,
  getSubscriberType,
  getTaskStatusQuery,
  getValidReference,
  getValidVersionReference,
  getValueTypeModel,
  getValuesFromExtensions,
  hasAnyActiveDeviceOfType,
  hasClaimLinks,
  identifierCodeSchema,
  identifierPartialRequiredFieldSchemas,
  invalidateEncounter,
  isCentsFactor,
  isConditionType,
  isCurrency,
  isGeneralInsurance,
  isPrimaryIcd10Code,
  isRequiredCoding,
  isSameDiagnosis,
  isValidReference,
  isValidSouthAfricanIdNumber,
  linesSchema,
  longTermMetrics,
  makePatchDevicesInactive,
  mapActiveConditions,
  mapAdmissionDiagnosisToFhir,
  mapAdmissionToEncounter,
  mapAdmissionToEncounterWithId,
  mapAllergyToFhir,
  mapChargeItemDefinitionToChargeLine,
  mapChargeItemDefinitionToFhir,
  mapChargeItemDefinitionToFhirWithID,
  mapChargeItemDefinitionToModel,
  mapChargeItemToModel,
  mapChargeItemToUsageModel,
  mapClaimReversalToHBFormat,
  mapClaimToFhir,
  mapClaimToFhirModel,
  mapClaimToHealthbridgeRequest,
  mapClaimToModel,
  mapCodeToMeasurementName,
  mapCodeableConceptToRequiredCode,
  mapCodeableConceptToRequiredCoding,
  mapConditionToDiagnoses,
  mapCoverageToIdentifiers,
  mapCoverageToPatientMembership,
  mapCoverageToPatientMembershipWithId,
  mapCreateAllergyToFhir,
  mapCreateDiagnosisToFhir,
  mapCreateModelToChargeItem,
  mapCreateModelToDeviceUseStatement,
  mapCreateModelToFhirCareTeam,
  mapCreateModelToFhirCommunication,
  mapCreateModelToFhirGroup,
  mapCreateModelToFhirLocation,
  mapCreateToFhirMedia,
  mapCreationEntriesToNote,
  mapDeviceCreateModelToFhir,
  mapDeviceMetricFhirToModel,
  mapDeviceMetricModelToFhir,
  mapDeviceToFhir,
  mapDeviceToNameAndId,
  mapDeviceUseStatementToModel,
  mapDeviceUseStatementsToTimeSeriesQueries,
  mapDiagnosesToMedprax,
  mapDocumentCreateModelToFhir,
  mapDocumentReferenceToModel,
  mapEncounterToAdmission,
  mapEncounterToAdmissionWithId,
  mapFhirAppointmentToModel,
  mapFhirCarePlanToModel,
  mapFhirCarePlanToModels,
  mapFhirCareTeamToModel,
  mapFhirClaimResponseToModel,
  mapFhirCommunicationToModel,
  mapFhirDiagnosticReportToModel,
  mapFhirFlagToModel,
  mapFhirLibraryToTemplateModel,
  mapFhirLocationToModel,
  mapFhirObservationToModelPregnancy,
  mapFhirPersonToContactModel,
  mapFhirPersonToGeneralModel,
  mapFhirQuestionnaireResponseToModel,
  mapFhirQuestionnaireToModel,
  mapFhirServiceRequestToModel,
  mapFhirTaskUpdate,
  mapFhirToAdmissionDiagnosis,
  mapFhirToAllergy,
  mapFhirToCarePlanByPatientModel,
  mapFhirToDevice,
  mapFhirToDiagnosisModel,
  mapFhirToExternalModel,
  mapFhirToModelGroup,
  mapFhirToModelGroupWithId,
  mapFhirToModelMedia,
  mapFhirToModelMediaWithId,
  mapFhirToModelObservation,
  mapFhirToModelObservationWithId,
  mapFhirToModelOnePractitionerWithId,
  mapFhirToModelOrganization,
  mapFhirToModelOrganizationWithId,
  mapFhirToModelPractitioner,
  mapFhirToModelPractitionerWithId,
  mapFhirToMoney,
  mapFhirToPlanDefinitionModel,
  mapFhirToPractitionerRole,
  mapFhirToQualificationModel,
  mapFhirToRelatedPersonModel,
  mapFhirToRelatedPersonModelWithId,
  mapFhirToResourceNote,
  mapFhirToServiceRequest,
  mapFhirToSettingsModel,
  mapFhirToSummaryModelPatient,
  mapFhirToTask,
  mapFhirToTaskWithId,
  mapFlagModelToFhir,
  mapGroupToFhir,
  mapHealthbridgeResponseToFhir,
  mapICD10CodeToCodeableConcept,
  mapICD10CodeToExtension,
  mapICD10ToCoding,
  mapInactiveConditions,
  mapLatestVitalsToObservation,
  mapMeasurementNameToCode,
  mapMedpraxConsumableModel,
  mapMedpraxSpecialtyToPractitionerQualification,
  mapMedpraxSpecialtyToSpecialty,
  mapMedpraxToMedicationModel,
  mapModelToChargeItem,
  mapModelToCreateServiceRequestEscalation,
  mapModelToDeviceUseStatement,
  mapModelToFhirAppointment,
  mapModelToFhirCarePlan,
  mapModelToFhirCareTeam,
  mapModelToFhirCommunication,
  mapModelToFhirDiagnosis,
  mapModelToFhirDiagnosticReport,
  mapModelToFhirMedia,
  mapModelToFhirMediaUpdate,
  mapModelToFhirObservation,
  mapModelToFhirObservationPathologyReport,
  mapModelToFhirOrganization,
  mapModelToFhirPractitioner,
  mapModelToFhirRelatedPerson,
  mapModelToFhirServiceRequest,
  mapModelWithIdToFhirRelatedPerson,
  mapMoneyToFhir,
  mapNoteToClinicalImpression,
  mapObservationsToLatestVitals,
  mapOnboardModelToFhirPatient,
  mapPageResultToMedpraxAllergy,
  mapPatientContactToFhirPartial,
  mapPatientMembershipToCoverage,
  mapPersonContactToFhirPartial,
  mapPersonGeneralToFhirPerson,
  mapPlanDefinitionModelToFhir,
  mapPractitionerGeneral,
  mapPractitionerRoleToFhir,
  mapPregnancyToFhirObservation,
  mapPregnancyToObservationComponent,
  mapQuestionnaireResponseToModelToFhir,
  mapQuestionnaireResponseUpdateModelToFhir,
  mapRelatedPersonContactToFhirPartial,
  mapResourceNoteToFhir,
  mapRiskAssessmentToPriorityScore,
  mapTaskToFhir,
  mapTaskToFhirWithId,
  mapTemplateModelToFhirLibrary,
  mapToValueSet,
  mapToValueSetComposeIncludeConcept,
  mapUpdateAllergyToFhir,
  mapUpdateContactModelToFhirOrganization,
  mapUpdateContactModelToFhirPractitioner,
  mapUpdateGeneralModelToFhirOrganization,
  mapUpdateGeneralModelToFhirPractitioner,
  mapUpdateModelToFhirCareTeam,
  mapUpdateModelToFhirGroup,
  mapUpdateModelToFhirLocation,
  mapUpdateModelToFhirOrganization,
  mapUpdatePractitionerSettings,
  mapUpdateQualificationModelToFhirPractitioner,
  markClaimAsActive,
  medicalAidAuthorizationCreateSchemaFields,
  mergeContactPoints,
  mergePersonContactWithFhir,
  mergePersonGeneralWithFhir,
  mergeResourcesToNote,
  namibianIdNumber,
  nextOfKinContactSchema,
  nextOfKinGeneralSchema,
  nextOfKinSchema,
  noteUpdateSchema,
  patchCalibrateRequestSchema,
  pathologyReportStatus,
  pauseEncounter,
  personContactFieldSchemas,
  personContactSchema,
  personGeneralFieldSchemas,
  personGeneralSchema,
  personMinimalGeneralFieldSchema,
  phoneRegex,
  phoneSchema,
  pmaName,
  pmaVersion,
  postalCodeSchema,
  postureCalibrationToMeasurement,
  preferenceUpdateSchema,
  providers,
  quroConsumableCodes,
  resolveConditionDifference,
  resumeEncounter,
  saIdNumber,
  setClaimResponse,
  setPatientStatus,
  statusSchema,
  completeDisposableDeviceUseStatements as stopPatchDeviceUseStatements,
  telecomPhoneNumbersSchema2 as telecomPhoneNumbersSchema,
  temperatureCalibrationToMeasurement,
  typeCodes,
  updateChargeItemDefinitionSchema,
  updateChargeLines,
  updateEncounterFromAdmission,
  updateEncounterFromAdmissionWithId,
  updateFhirTaskNote,
  updateFhirTaskRequestedPeriod,
  updateFhirTaskStatus,
  updateGroupMembers,
  updatePatientCareTeam,
  updatePatientStatus,
  updatePractitionerExternal,
  updateTaskStatusSchema,
  usernameSchema
};
//# sourceMappingURL=index.js.map